import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { adminApiService } from "../../../service/admin/adminApiService";
import { cronLogService } from "../../../service/admin/cronLogService";
import { productDraftApiService } from "../../../service/admin/productDraftApiService";
import { fetchToken } from "../../../lib/globalApiClient";
import noImage from "../../../assets/images/common/importerr-no-image-logo.png";
import { TbZoomScan } from "react-icons/tb";
import { FaSpinner, FaExternalLinkAlt, FaStar, FaStore, FaBox, FaRuler, FaWeight, FaSync, FaArrowLeft, FaInfoCircle, FaEdit } from "react-icons/fa";
import SearchBySkuUrl from "./SearchBySkuUrl";
import PriceCalc from "./PriceCalc";
import UpdatePriceModal from "./UpdatePriceModal";
import "./amazonProduct.css";
import { ProductsApiService } from "../../../service/website/productsApiService";

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

export default function AmazonProductDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const asinFromQuery = searchParams.get("asin") || "";

  // Check if user came from failed orders page
  const fromFailedOrders = searchParams.get("fromFailedOrders") === "true";

  // Check if this is a remap operation for an existing order
  const remapOrderId = searchParams.get("remapOrderId") || "";
  const remapUpsaleoOrderId = searchParams.get("oid") || ""; // Short param name for URL
  const isRemapMode = !!remapOrderId;

  // Get order value from URL params (for remap mode margin calculation)
  const remapOrderValue = parseFloat(searchParams.get("orderValue") || 0);
  const remapOrderQty = parseInt(searchParams.get("orderQty") || 1);

  // Get old/current mapped price info (for remap mode comparison)
  const oldMappedPrice = parseFloat(searchParams.get("oldPrice") || 0);
  const oldMappedSkuId = searchParams.get("oldSkuId") || "";
  const oldMappedOfferId = searchParams.get("oldOfferId") || "";
  // Apply 30% reduction to order value for old margin calculation
  const adjustedRemapOrderValue = remapOrderValue > 0 ? remapOrderValue * 0.7 : 0;
  const oldMargin = adjustedRemapOrderValue > 0 && oldMappedPrice > 0 ? adjustedRemapOrderValue - oldMappedPrice : 0;

  // Check if a product was selected from similar products
  const isSelectedProductMode = searchParams.get("selectedProduct") === "true";
  const selectedOfferId = searchParams.get("offerId") || "";
  const selectedTitle = decodeURIComponent(searchParams.get("title") || "");
  const selectedImage = decodeURIComponent(searchParams.get("image") || "");
  const selectedRmb = parseFloat(searchParams.get("rmb") || 0);
  const selectedInr = parseFloat(searchParams.get("inr") || 0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("description");

  // Pricing tab state (BTPL or Reseller/Others)
  const [pricingTab, setPricingTab] = useState("BTPL"); // "BTPL" or "Reseller"

  // Similar products state
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showSimilar, setShowSimilar] = useState(!isSelectedProductMode); // Hide sidebar if product selected
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [pricingCache, setPricingCache] = useState({});
  const pricingCacheRef = useRef({}); // Ref to avoid re-renders when checking cache
  const [failedOrderAmount, setFailedOrderAmount] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Multiple filter states - array of selected filters
  const [selectedFilters, setSelectedFilters] = useState([]); // Array of filter strings like ['price-low', 'margin-high', 'moq-low']
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);

  // Keep sortOption for backward compatibility (will be deprecated)
  const [sortOption, setSortOption] = useState('default');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFilterDropdown]);

  // Selected product details state
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [selectedProductVariants, setSelectedProductVariants] = useState([]);
  const [loadingSelectedProduct, setLoadingSelectedProduct] = useState(false);
  const [selectedVariantImageIdx, setSelectedVariantImageIdx] = useState(0);
  const [selectedColorAttr, setSelectedColorAttr] = useState(null); // Track selected color/first attribute
  const hasFetchedSelectedProduct = useRef(false); // Prevent duplicate fetches
  const [showGetFinalPriceButton, setShowGetFinalPriceButton] = useState(false); // Show button after API success
  const [finalPriceFromApi, setFinalPriceFromApi] = useState(null); // Final price from get-final-price API
  const [finalPriceBreakdown, setFinalPriceBreakdown] = useState(null); // Full breakdown data from get-final-price API
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false); // Toggle price breakdown popover
  const [loadingFinalPrice, setLoadingFinalPrice] = useState(false); // Loading state for get-final-price API
  const [calculatedPriceRanges, setCalculatedPriceRanges] = useState([]); // Calculated prices for priceRangeList

  // Confirmation modal state (before mapping)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmVariant, setConfirmVariant] = useState(null);
  const [mappingRemark, setMappingRemark] = useState("");
  const [isMapping, setIsMapping] = useState(false);

  // Price calculator modal state
  const [showPriceCalculatorModal, setShowPriceCalculatorModal] = useState(false);

  // Edit weight/dimensions modal state
  const [showEditWeightDimensionsModal, setShowEditWeightDimensionsModal] = useState(false);
  const [editingVariantForWeightDimensions, setEditingVariantForWeightDimensions] = useState(null);

  // Dimensions modal form state
  const [dimFormValues, setDimFormValues] = useState({ length: '', width: '', height: '', weight: '' });
  const [dimFormErrors, setDimFormErrors] = useState({});
  const [dimApplyingCurrent, setDimApplyingCurrent] = useState(false);
  const [dimApplyingAll, setDimApplyingAll] = useState(false);
  const [dimToast, setDimToast] = useState(null); // { type: 'success'|'error', message: string }

  // Store edited weight and dimensions per variant (keyed by variant id or skuId)
  const [editedVariantData, setEditedVariantData] = useState({}); // { variantId: { weight, length, width, height, finalPrice } }

  // Apply to All Results Modal state
  const [showApplyToAllResultsModal, setShowApplyToAllResultsModal] = useState(false);
  const [applyToAllResults, setApplyToAllResults] = useState([]);
  const [applyToAllDimensions, setApplyToAllDimensions] = useState(null); // Store dimensions
  const [isProcessingApplyToAll, setIsProcessingApplyToAll] = useState(false);
  const [applyToAllProgress, setApplyToAllProgress] = useState(0);

  // Image zoom modal state
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [zoomedImageUrl, setZoomedImageUrl] = useState("");

  // Custom finalPrice input state (deprecated - using per-seller prices now)
  const [customFinalPrice, setCustomFinalPrice] = useState("");

  // Custom price modal state
  const [showCustomPriceModal, setShowCustomPriceModal] = useState(false);
  const [loadingFailedOrders, setLoadingFailedOrders] = useState(false);
  const [customPricesByOrderDetail, setCustomPricesByOrderDetail] = useState({}); // { orderDetailId: price } - orderDetailId is orderDetail _id (variant-wise)
  const [customQuantitiesByOrderDetail, setCustomQuantitiesByOrderDetail] = useState({}); // { orderDetailId: quantity } - orderDetailId is orderDetail _id
  const [failedOrdersWithSellers, setFailedOrdersWithSellers] = useState([]); // Array of { orderId, sellerId, sellerEmail, orderDetails, ... }

  // Close image zoom modal on ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showImageZoom) {
        setShowImageZoom(false);
      }
    };

    if (showImageZoom) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [showImageZoom]);

  // Pricing algorithm selection state
  const [pricingAlgorithms, setPricingAlgorithms] = useState([]);
  const [selectedAlgoId, setSelectedAlgoId] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('boundless.co.in'); // Default to boundless.co.in
  const [loadingAlgos, setLoadingAlgos] = useState(false);

  // Marketplace deduction state (default 30%)
  const [marketplaceDeduction, setMarketplaceDeduction] = useState(30);

  // Importerr algorithm state (for showing Importerr price alongside)
  const [importerrAlgo, setImporterrAlgo] = useState(null);

  // HSN mapping state
  const [hsnCategories, setHsnCategories] = useState([]);
  const [selectedHsnCategory, setSelectedHsnCategory] = useState('');
  const [hsnMappings, setHsnMappings] = useState([]);
  const [selectedHsnMapping, setSelectedHsnMapping] = useState(null);
  const [loadingHsn, setLoadingHsn] = useState(false);

  // Remap mode: count of other orders with same ASIN that will be affected
  const [affectedOrdersCount, setAffectedOrdersCount] = useState(0);

  // Info icon hover state
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [breakdownTooltipPosition, setBreakdownTooltipPosition] = useState({ top: 0, left: 0, width: 0 });
  const breakdownTooltipRef = useRef(null);

  // Sort similar products based on multiple filters
  const sortedSimilarProducts = useMemo(() => {
    if (!similarProducts || similarProducts.length === 0) return [];

    const avgOrderValue = failedOrderAmount ? failedOrderAmount.totalAmount / failedOrderAmount.orderCount : 0;
    // Apply marketplace deduction to order value
    const adjustedOrderValue = avgOrderValue * (1 - marketplaceDeduction / 100);

    const productsWithMargin = similarProducts.map(product => {
      const marginPerOrder = adjustedOrderValue - (product.price?.inr || 0);
      return { ...product, marginPerOrder };
    });

    // If no filters selected, use old sortOption for backward compatibility
    if (selectedFilters.length === 0) {
      switch (sortOption) {
        case 'price-low':
          return [...productsWithMargin].sort((a, b) => (a.importerrPrice || 0) - (b.importerrPrice || 0));
        case 'price-high':
          return [...productsWithMargin].sort((a, b) => (b.importerrPrice || 0) - (a.importerrPrice || 0));
        case 'margin-low':
          return [...productsWithMargin].sort((a, b) => a.marginPerOrder - b.marginPerOrder);
        case 'margin-high':
          return [...productsWithMargin].sort((a, b) => b.marginPerOrder - a.marginPerOrder);
        case 'moq-low':
          return [...productsWithMargin].sort((a, b) => (a.moq || 1) - (b.moq || 1));
        case 'moq-high':
          return [...productsWithMargin].sort((a, b) => (b.moq || 1) - (a.moq || 1));
        default:
          return productsWithMargin;
      }
    }

    // Multi-level sorting: Apply all selected filters in priority order (Price > Rating > Margin > MOQ)
    return [...productsWithMargin].sort((a, b) => {
      // Priority 1: Price filter (using Importerr Price)
      if (selectedFilters.includes('price-low')) {
        const priceDiff = (a.importerrPrice || 0) - (b.importerrPrice || 0);
        if (priceDiff !== 0) return priceDiff;
      } else if (selectedFilters.includes('price-high')) {
        const priceDiff = (a.importerrPrice || 0) - (b.importerrPrice || 0);
        if (priceDiff !== 0) return -priceDiff;
      }

      // Priority 2: Margin filter
      if (selectedFilters.includes('margin-low')) {
        const marginDiff = a.marginPerOrder - b.marginPerOrder;
        if (marginDiff !== 0) return marginDiff;
      } else if (selectedFilters.includes('margin-high')) {
        const marginDiff = a.marginPerOrder - b.marginPerOrder;
        if (marginDiff !== 0) return -marginDiff;
      }

      // Priority 3: MOQ filter
      if (selectedFilters.includes('moq-low')) {
        const moqDiff = (a.moq || 1) - (b.moq || 1);
        if (moqDiff !== 0) return moqDiff;
      } else if (selectedFilters.includes('moq-high')) {
        const moqDiff = (a.moq || 1) - (b.moq || 1);
        if (moqDiff !== 0) return -moqDiff;
      }

      return 0; // Equal on all criteria
    });
  }, [similarProducts, selectedFilters, sortOption, failedOrderAmount, marketplaceDeduction]);

  // Fetch pricing algorithms on mount
  useEffect(() => {
    const fetchAlgorithms = async () => {
      setLoadingAlgos(true);
      try {
        // Check if user is a seller - use different endpoint
        const userType = (localStorage.getItem('userType') || localStorage.getItem('USER_TYPE') || '').toLowerCase();
        const isSeller = userType === 'seller';

        let algos = [];
        if (isSeller) {
          // Sellers use the order endpoint which doesn't require adminAuth
          const token = localStorage.getItem('LOGIN_HEADER');
          const response = await fetch(`${BACKEND_API}order/pricing-algorithms`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          const result = await response.json();
          algos = result?.data || [];
        } else {
          // Admins use the admin endpoint
          const res = await cronLogService.getPricingAlgorithms();
          algos = res?.data || [];
        }

        // console.log('[AmazonProductDetail] All algorithms fetched:', algos.length, algos.map(a => ({ name: a.name, active: a.active, deleted: a.deleted, domain: a.domain })));

        // Filter for inactive algorithms named "actual" (non-deleted)
        // This shows only the "actual" inactive algo for failed order ASIN mapping
        const actualAlgos = algos.filter(a => !a.active && !a.deleted && a.name?.toLowerCase() === 'actual');
        // console.log('[AmazonProductDetail] Filtered "actual" inactive algorithms:', actualAlgos.length, actualAlgos.map(a => ({ id: a._id, name: a.name, domain: a.domain })));

        setPricingAlgorithms(actualAlgos);

        // Find and store the active Importerr algorithm (domain: importerr.com, active: true)
        const importerrAlgorithm = algos.find(a => a.active && !a.deleted && a.domain === 'importerr.com');
        if (importerrAlgorithm) {
          setImporterrAlgo(importerrAlgorithm);
          // console.log('[AmazonProductDetail] Importerr algorithm found:', importerrAlgorithm._id, importerrAlgorithm.name);
        } else {
          console.warn('[AmazonProductDetail] No active Importerr algorithm found for domain importerr.com');
        }

        // Set default to the "actual" algorithm
        if (actualAlgos.length > 0) {
          setSelectedAlgoId(actualAlgos[0]._id);
          setSelectedDomain(actualAlgos[0].domain || 'importerr.com');
          // console.log('[AmazonProductDetail] Selected algorithm:', actualAlgos[0]._id, actualAlgos[0].name);
        } else {
          console.warn('[AmazonProductDetail] No "actual" inactive algorithm found! Pricing will not work correctly.');
          // console.log('[AmazonProductDetail] Available algorithms:', algos.map(a => `${a.name} (active: ${a.active}, deleted: ${a.deleted})`).join(', '));
        }
      } catch (err) {
        console.error('Failed to fetch pricing algorithms:', err);
      } finally {
        setLoadingAlgos(false);
      }
    };
    fetchAlgorithms();
  }, []);

  // Fetch HSN categories on mount (with fallback to fetch all mappings)
  useEffect(() => {
    const fetchHsnData = async () => {
      try {
        const token = localStorage.getItem('LOGIN_HEADER');

        // First try to get categories
        let categoriesResponse = await fetch(`${BACKEND_API}admin/hsn-mappings/categories`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (categoriesResponse.ok) {
          const result = await categoriesResponse.json();
          // console.log('[HSN] Categories fetched:', result);
          if (result.data && result.data.length > 0) {
            setHsnCategories(result.data);
            return;
          }
        }

        // Fallback: fetch all HSN mappings and extract unique categories
        // console.log('[HSN] Trying fallback - fetching all mappings');
        const allMappingsResponse = await fetch(`${BACKEND_API}admin/hsn-mappings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (allMappingsResponse.ok) {
          const result = await allMappingsResponse.json();
          // console.log('[HSN] All mappings fetched:', result);
          const mappings = result.data || [];
          // Extract unique categories
          const uniqueCategories = [...new Set(mappings.map(m => m.category))].filter(Boolean).sort();
          // console.log('[HSN] Extracted categories:', uniqueCategories);
          setHsnCategories(uniqueCategories);
        } else {
          console.error('[HSN] Failed to fetch all mappings:', allMappingsResponse.status);
        }
      } catch (err) {
        console.error('[HSN] Error fetching HSN data:', err);
      }
    };
    fetchHsnData();
  }, []);

  // Handle click outside to close breakdown tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBreakdown && breakdownTooltipRef.current && !breakdownTooltipRef.current.contains(event.target)) {
        // Check if click is not on the icon
        const iconElement = event.target.closest('[data-breakdown-icon]');
        if (!iconElement) {
          setShowBreakdown(false);
        }
      }
    };

    if (showBreakdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showBreakdown]);

  // Update breakdown tooltip position on scroll and resize
  useEffect(() => {
    const updateTooltipPosition = () => {
      if (showBreakdown) {
        const iconElement = document.querySelector('[data-breakdown-icon]');
        if (iconElement) {
          const rect = iconElement.getBoundingClientRect();
          setBreakdownTooltipPosition({
            top: rect.top,
            left: rect.left,
            width: rect.width
          });
        }
      }
    };

    if (showBreakdown) {
      // Update position on scroll with requestAnimationFrame for smooth updates
      const handleScroll = () => {
        requestAnimationFrame(updateTooltipPosition);
      };

      // Listen on both document and window for comprehensive scroll detection
      document.addEventListener('scroll', handleScroll, true);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', updateTooltipPosition);

      // Initial position update
      updateTooltipPosition();

      return () => {
        document.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', updateTooltipPosition);
      };
    }
  }, [showBreakdown]);

  // Fetch HSN mappings when category changes
  useEffect(() => {
    const fetchHsnMappings = async () => {
      if (!selectedHsnCategory) {
        setHsnMappings([]);
        setSelectedHsnMapping(null);
        return;
      }
      setLoadingHsn(true);
      try {
        const token = localStorage.getItem('LOGIN_HEADER');
        const response = await fetch(`${BACKEND_API}admin/hsn-mappings/category/${encodeURIComponent(selectedHsnCategory)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const result = await response.json();
          // console.log('[HSN] Mappings for category:', selectedHsnCategory, result);
          const mappings = result.data || [];
          setHsnMappings(mappings);
          // Auto-select first mapping when category is selected
          if (mappings.length > 0) {
            setSelectedHsnMapping(mappings[0]);
          } else {
            setSelectedHsnMapping(null);
          }
        } else {
          console.error('[HSN] Failed to fetch mappings:', response.status);
          // Fallback: filter from all mappings
          const allResponse = await fetch(`${BACKEND_API}admin/hsn-mappings?category=${encodeURIComponent(selectedHsnCategory)}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (allResponse.ok) {
            const result = await allResponse.json();
            const mappings = result.data || [];
            setHsnMappings(mappings);
            // Auto-select first mapping
            if (mappings.length > 0) {
              setSelectedHsnMapping(mappings[0]);
            }
          }
        }
      } catch (err) {
        console.error('[HSN] Error fetching mappings:', err);
      } finally {
        setLoadingHsn(false);
      }
    };
    fetchHsnMappings();
  }, [selectedHsnCategory]);

  // Reset finalPriceFromApi when the selected variant changes
  useEffect(() => {
    setFinalPriceFromApi(null);
    setFinalPriceBreakdown(null);
    setShowPriceBreakdown(false);
  }, [selectedVariantImageIdx]);

  // Handle algorithm change - recalculate prices
  const handleAlgoChange = useCallback((algoId) => {
    const algo = pricingAlgorithms.find(a => a._id === algoId);
    if (algo) {
      setSelectedAlgoId(algoId);
      setSelectedDomain(algo.domain || 'importerr.com');
      // Clear cache to force recalculation
      pricingCacheRef.current = {};
      setPricingCache({});
    }
  }, [pricingAlgorithms]);

  // Calculate INR price using selected pricing algorithm (display only, no freight API)
  // Uses algoId to directly specify which algorithm to use (supports inactive algorithms)
  // Uses same chargeable weight calculation as website PDP
  const calculateINRPrice = useCallback(async (rmbPrice, offerId = '', skuId = '', weight = 0, dimensions = null, algoIdOverride = null, categoryIds = {}) => {
    if (!rmbPrice || rmbPrice <= 0) return { inr: 0, breakdown: null };

    const useAlgoId = algoIdOverride || selectedAlgoId;

    // If no algorithm selected, return 0 with warning
    if (!useAlgoId) {
      console.warn('[calculateINRPrice] No algorithm ID available - prices will show as 0');
      return { inr: 0, breakdown: null };
    }

    // Same constants as website PDP (PRICE_LOGIC)
    const defaultWeight = 0.5; // PRICE_LOGIC.DEFAULT_DW
    const volumeFactor = 5000; // PRICE_LOGIC.VOLUME_FACTOR

    // Parse dimensions - convert from mm to cm if needed
    let length = 0, width = 0, height = 0;
    if (dimensions) {
      length = dimensions.length || 0;
      width = dimensions.width || 0;
      height = dimensions.height || 0;

      // If dimensions seem to be in mm (values > 100), convert to cm
      if (length > 100 || width > 100 || height > 100) {
        length = length / 10;
        width = width / 10;
        height = height / 10;
      }
    }

    const hasDimensions = length > 0 && width > 0 && height > 0;

    // Parse weight - convert from grams to kg if needed
    let rawWeight = weight || 0;
    if (rawWeight > 50) {
      rawWeight = rawWeight / 1000; // Convert grams to kg
    }
    const hasWeight = rawWeight > 0;

    // Calculate volumetric weight (same as website PDP)
    const volumetricWeight = hasDimensions ? (length * width * height) / volumeFactor : 0;

    // Calculate chargeable weight using same logic as website PDP (calculateVariantChargeableWeight)
    // 1. Both missing - use default weight
    // 2. Only weight available (dimensions missing) - max(actualWeight, defaultWeight)
    // 3. Only dimensions available (weight missing) - max(volumetricWeight, defaultWeight)
    // 4. Both available - max(actualWeight, volumetricWeight)
    let chargeableWeight;
    if (!hasDimensions && !hasWeight) {
      chargeableWeight = defaultWeight;
    } else if (hasWeight && !hasDimensions) {
      chargeableWeight = Math.max(rawWeight, defaultWeight);
    } else if (hasDimensions && !hasWeight) {
      chargeableWeight = Math.max(volumetricWeight, defaultWeight);
    } else {
      chargeableWeight = Math.max(rawWeight, volumetricWeight);
    }

    const cacheKey = `${rmbPrice.toFixed(2)}_${offerId}_${skuId}_${useAlgoId}`;

    // Check cache using ref (doesn't cause re-renders)
    if (pricingCacheRef.current[cacheKey]) {
      return pricingCacheRef.current[cacheKey];
    }

    try {
      // Use calculate endpoint with algoId to use specific algorithm (even if inactive)
      const response = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ap: parseFloat(rmbPrice),
          ls: 0,
          hsn: false,
          hsnTaxPercent: 18,
          dw: chargeableWeight, // Use calculated chargeable weight (same as website PDP)
          vw: volumetricWeight,
          sku: String(offerId || ''),
          algoId: useAlgoId, // Pass algorithm ID directly to use specific algorithm
          topCategoryId: categoryIds?.topCategoryId || '',
          secondCategoryId: categoryIds?.secondCategoryId || '',
          thirdCategoryId: categoryIds?.thirdCategoryId || ''
        })
      });

      if (!response.ok) {
        console.error('[calculateINRPrice] API error:', response.status, response.statusText);
        return { inr: 0, breakdown: null };
      }

      const result = await response.json();
      // console.log('[calculateINRPrice] API response for RMB', rmbPrice, ':', result);

      const inrPrice = parseFloat(result?.data?.breakdown?.FBP || result?.breakdown?.FBP || 0);
      const breakdown = result?.data?.breakdown || result?.breakdown || null;

      if (inrPrice === 0) {
        console.warn('[calculateINRPrice] FBP is 0 - check algorithm formula values');
      }

      const priceData = {
        inr: inrPrice,
        breakdown,
        chargeableWeight,
        volumetricWeight,
        rawWeight,
        dimensions: { length, width, height }
      };

      // Update both ref and state
      pricingCacheRef.current[cacheKey] = priceData;
      setPricingCache(prev => ({ ...prev, [cacheKey]: priceData }));
      return priceData;
    } catch (err) {
      console.error('[calculateINRPrice] Error:', err);
      return { inr: 0, breakdown: null };
    }
  }, [selectedAlgoId]); // Recalculate when algorithm changes

  // Compress image
  const compressImage = useCallback(async (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const maxDim = 400;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((b) => resolve(b || blob), 'image/jpeg', 0.7);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
      img.src = url;
    });
  }, []);

  // STUB: Dummy function to replace commented out fetchMinVariantPrice
  // Original function is commented out below - returns default values
  const fetchMinVariantPrice = useCallback(async (offerId, fallbackRmb, fallbackImage, categoryIds = {}) => {
    // Returns stub data - INR calculation is disabled
    return {
      minInr: 0, // COMMENTED OUT: INR price calculation disabled
      minRmb: fallbackRmb,
      variantCount: 1,
      breakdown: null
    };
  }, []);

  // COMMENTED OUT: Original Helper function to fetch variant details and calculate minimum FBP price
  // This function calculates product.price?.inr for similar products
  /*
  const fetchMinVariantPrice = useCallback(async (offerId, fallbackRmb, fallbackImage, categoryIds = {}) => {
    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl.replace(/\/$/, '') + '/product-detail-by-offerid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: offerId.toString(), country: 'en' })
      });

      if (!response.ok) {
        // Fallback to basic price calculation
        const priceData = await calculateINRPrice(fallbackRmb, offerId, '', 0.5, null, null, categoryIds);
        return { minInr: priceData.inr, minRmb: fallbackRmb, variantCount: 1, breakdown: priceData.breakdown };
      }

      const data = await response.json();
      const productData = data.result?.result || data.product || data.data || data;

      // Extract variants/SKUs and shipping info
      let skuInfos = productData.productSkuInfos || productData.skuInfos || productData.skuList ||
        productData.skus || productData.productInfo?.skuInfos || productData.data?.productSkuInfos ||
        productData.variants || [];

      const shippingDetails = productData.productShippingInfo?.skuShippingDetails || [];

      if (skuInfos.length === 0) {
        // No variants, use fallback
        const priceData = await calculateINRPrice(fallbackRmb, offerId, '', 0.5, null, null, categoryIds);
        return { minInr: priceData.inr, minRmb: fallbackRmb, variantCount: 1, breakdown: priceData.breakdown };
      }

      // First, find the variant with minimum RMB price (without calculating INR for all)
      // This is similar to how search products work - use minimum RMB price for calculation
      let minRmbVariant = null;
      let minRmb = Infinity;

      for (const sku of skuInfos) {
        const skuPrice = parseFloat(sku.price || sku.consignPrice || sku.salePrice || sku.retailPrice || sku.priceInfo?.price || fallbackRmb);

        if (skuPrice > 0 && skuPrice < minRmb) {
          minRmb = skuPrice;
          minRmbVariant = sku;
        }
      }

      // If no valid variant found, use fallback
      if (!minRmbVariant || minRmb === Infinity) {
        const priceData = await calculateINRPrice(fallbackRmb, offerId, '', 0.5, null, null, categoryIds);
        return { minInr: priceData.inr, minRmb: fallbackRmb, variantCount: skuInfos.length, breakdown: priceData.breakdown };
      }

      // Now calculate INR only once for the minimum RMB variant
      const skuId = minRmbVariant.skuId || minRmbVariant.id || minRmbVariant.specId;
      const skuShipping = shippingDetails.find(s => s.skuId == skuId) || shippingDetails[0] || {};
      const weight = parseFloat(skuShipping.weight || 0);
      const length = parseFloat(skuShipping.length || 0);
      const width = parseFloat(skuShipping.width || 0);
      const height = parseFloat(skuShipping.height || 0);
      const dimensions = (length > 0 && width > 0 && height > 0) ? { length, width, height } : null;

      // Calculate price only once for the minimum RMB variant
      const priceData = await calculateINRPrice(minRmb, offerId, skuId, weight, dimensions, null, categoryIds);

      return {
        minInr: priceData.inr || 0,
        minRmb: minRmb,
        variantCount: skuInfos.length,
        breakdown: priceData.breakdown
      };
    } catch (err) {
      console.error('[fetchMinVariantPrice] Error for offerId', offerId, ':', err);
      // Fallback to basic price calculation
      const priceData = await calculateINRPrice(fallbackRmb, offerId, '', 0.5, null, null, categoryIds);
      return { minInr: priceData.inr, minRmb: fallbackRmb, variantCount: 1, breakdown: priceData.breakdown };
    }
  }, [calculateINRPrice]);
  */

  // Find similar products with pagination - Optimized with parallel processing
  const handleFindSimilar = useCallback(async (page = 1) => {
    // Use the currently displayed image (selected thumbnail), not the default mainImage
    const allImages = data?.images?.length ? data.images : (data?.mainImage ? [data.mainImage] : []);
    const imageUrl = allImages[activeImageIdx] || allImages[0];
    if (!imageUrl) { alert('No image available'); return; }

    // Clear selected product to show the grid
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setLoadingSimilar(true);
    setShowSimilar(true);
    setCurrentPage(page);

    // Clear pricing cache to ensure fresh calculations
    pricingCacheRef.current = {};
    setPricingCache({});

    try {
      // Parallel image processing and API call preparation
      const [imgBlob, token] = await Promise.all([
        fetch(`${BACKEND_API.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(imageUrl)}`)
          .then(response => {
            if (!response.ok) throw new Error('Failed to fetch image');
            return response.blob();
          })
          .then(blob => compressImage(blob)),
        fetchToken()
      ]);

      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });

      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const response = await fetch(baseUrl.replace(/\/$/, '') + '/get-product-info-by-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
        body: JSON.stringify({ imageBase64: base64Image, beginPage: page, pageSize: 9, country: 'en' }),
      });

      if (!response.ok) throw new Error('API error');
      const result = await response.json();

      // Set total pages from response
      const totalPagesFromApi = result.pagination?.totalPage || result.totalPages || 5;
      setTotalPages(totalPagesFromApi);

      let productsData = result.products || result.result?.result?.data || result.data || [];
      if (Array.isArray(productsData) && productsData.length > 0) {
        const productsToProcess = productsData.slice(0, 9);
        const totalProducts = productsToProcess.length;
        setProcessingProgress({ current: 0, total: totalProducts });

        // Process products in parallel with controlled concurrency
        const BATCH_SIZE = 3; // Process 3 products at a time to avoid overwhelming the API
        const processed = [];

        for (let i = 0; i < productsToProcess.length; i += BATCH_SIZE) {
          const batch = productsToProcess.slice(i, i + BATCH_SIZE);

          // Process batch in parallel
          const batchPromises = batch.map(async (item, batchIdx) => {
            const idx = i + batchIdx;
            // console.log('Processing item:', idx, item);

            const rmbPrice = parseFloat(item.priceInfo?.price || item.price || 0);
            const offerId = item.offerId || item.id || '';
            const productImage = item.imageUrl || item.image || '';

            try {
              // COMMENTED OUT: Parallel processing of variant price and importerr price
              // This calculates product.price?.inr using fetchMinVariantPrice
              /*
              const [variantPriceData, importerrPrice] = await Promise.all([
                // Fetch variant details and get minimum FBP price
                fetchMinVariantPrice(offerId, rmbPrice, productImage, {
                  topCategoryId: item?.topCategoryId,
                  secondCategoryId: item?.secondCategoryId,
                  thirdCategoryId: item?.thirdCategoryId
                }),
              */
              // Calculate Importerr price: batch all SKU prices in one API call, take the minimum FBP
              const importerrPrice = await (async () => {
                const skuInfos = item?.detailed_info?.productSkuInfos;
                const skuShippingDetails = item?.detailed_info?.productShippingInfo?.skuShippingDetails || [];

                // Helper: compute chargeable weight for a given skuId using shipping details
                const getWeights = (skuId, aiWeight = 0) => {
                  const defaultWeight = 0.5;
                  const volumeFactor = 5000;
                  const shipping = skuShippingDetails.find(s => s.skuId == skuId) || skuShippingDetails[0] || {};

                  // Weight priority: shipping.weight → aiWeight → 0 (no default fallback yet)
                  let rawWeight = parseFloat(shipping.weight || 0);
                  if (rawWeight > 50) rawWeight = rawWeight / 1000; // grams → kg

                  if (rawWeight <= 0) {
                    // Fall back to aiWeight
                    let aiW = parseFloat(aiWeight || 0);
                    if (aiW > 50) aiW = aiW / 1000; // grams → kg
                    rawWeight = aiW; // stays 0 if aiWeight is also absent
                  }

                  let length = parseFloat(shipping.length || 0);
                  let width = parseFloat(shipping.width || 0);
                  let height = parseFloat(shipping.height || 0);
                  if (length > 100 || width > 100 || height > 100) {
                    length = length / 10; width = width / 10; height = height / 10;
                  }

                  const hasDimensions = length > 0 && width > 0 && height > 0;
                  const hasWeight = rawWeight > 0;
                  const vw = hasDimensions ? (length * width * height) / volumeFactor : 0;

                  let dw;
                  if (!hasDimensions && !hasWeight) dw = defaultWeight;
                  else if (hasWeight && !hasDimensions) dw = rawWeight;
                  else if (hasDimensions && !hasWeight) dw = vw;
                  else dw = Math.max(rawWeight, vw);

                  return { dw, vw };
                };

                const skuPrices = Array.isArray(skuInfos) && skuInfos.length > 0
                  ? skuInfos
                    .map(sku => ({
                      price: parseFloat(sku?.consignPrice || sku?.price || 0),
                      skuId: sku?.skuId || sku?.id || '',
                      aiWeight: sku?.aiWeight || sku?.aiweight || 0
                    }))
                    .filter(s => s.price > 0)
                  : (rmbPrice > 0 ? [{ price: rmbPrice, skuId: '' }] : []);

                const fallbackPrice = parseFloat(item?.priceInfo?.price || 0);
                const pricesToCalculate = skuPrices.length > 0
                  ? skuPrices
                  : (fallbackPrice > 0 ? [{ price: fallbackPrice, skuId: '' }] : []);

                if (pricesToCalculate.length === 0) return null;

                try {
                  const batchItems = pricesToCalculate.map(({ price: skuPrice, skuId, aiWeight }) => {
                    const { dw, vw } = getWeights(skuId, aiWeight);
                    return {
                      ap: skuPrice,
                      ls: 0,
                      hsn: false,
                      hsnTaxPercent: 18,
                      dw,
                      vw,
                      sku: String(offerId || ''),
                      topCategoryId: item?.topCategoryId || '',
                      secondCategoryId: item?.secondCategoryId || '',
                      thirdCategoryId: item?.thirdCategoryId || ''
                    };
                  });

                  const batchResponse = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate-batch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: batchItems })
                  });

                  if (batchResponse.ok) {
                    const batchResult = await batchResponse.json();
                    const results = batchResult?.data?.results || batchResult?.results || [];
                    const validPrices = results
                      .filter(r => r.success && r.breakdown?.FBP)
                      .map(r => parseFloat(r.breakdown.FBP))
                      .filter(p => p > 0);
                    return validPrices.length > 0 ? Math.min(...validPrices) : null;
                  }
                } catch (err) {
                  console.error(`[handleFindSimilar] Error calculating Importerr price (batch) for ${offerId}:`, err);
                }
                return null;
              })();
              // COMMENTED OUT: End of parallel processing
              /*
              ]);
              */

              // Update progress after completing this product
              setProcessingProgress({ current: idx + 1, total: totalProducts, stage: `Completed product ${idx + 1}` });

              return {
                id: offerId || `p-${idx}`,
                offerId,
                title: item.subjectTrans || item.subject || item.title || 'Product',
                image: productImage,
                // COMMENTED OUT: price calculation using variantPriceData.minInr
                /*
                price: {
                  rmb: variantPriceData.minRmb,
                  inr: variantPriceData.minInr,
                  breakdown: variantPriceData.breakdown
                },
                */
                // Using default price structure without INR calculation
                price: {
                  rmb: rmbPrice,
                  inr: 0, // COMMENTED OUT: was variantPriceData.minInr
                  breakdown: null // COMMENTED OUT: was variantPriceData.breakdown
                },
                importerrPrice, // Importerr calculated price
                supplier: item.companyName || item.supplier || 'Supplier',
                monthSold: item.monthSold || 0,
                // COMMENTED OUT: variantCount from variantPriceData
                // variantCount: variantPriceData.variantCount,
                variantCount: 1, // COMMENTED OUT: was variantPriceData.variantCount
                moq: item.moq || item.minOrderQuantity || 1,
                rating: parseFloat(item.sellerRating || item.rating || 0),
                detailed_info: item.detailed_info || {}, // Include detailed_info for variant count
                _processIndex: idx // Keep original index for sorting
              };
            } catch (err) {
              console.error(`[handleFindSimilar] Error processing product ${idx}:`, err);
              // Update progress even on error
              setProcessingProgress({ current: idx + 1, total: totalProducts, stage: `Error processing product ${idx + 1}` });
              // Return a fallback product object
              return {
                id: offerId || `p-${idx}`,
                offerId,
                title: item.subjectTrans || item.subject || item.title || 'Product',
                image: productImage,
                price: {
                  rmb: rmbPrice,
                  inr: 0,
                  breakdown: null
                },
                importerrPrice: null,
                supplier: item.companyName || item.supplier || 'Supplier',
                monthSold: item.monthSold || 0,
                variantCount: 1,
                moq: item.moq || item.minOrderQuantity || 1,
                rating: parseFloat(item.sellerRating || item.rating || 0),
                detailed_info: item.detailed_info || {}, // Include detailed_info for variant count
                _processIndex: idx
              };
            }
          });

          // Wait for batch to complete
          const batchResults = await Promise.all(batchPromises);
          processed.push(...batchResults);

          // Small delay between batches to prevent API rate limiting
          if (i + BATCH_SIZE < productsToProcess.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        // Sort by original index to maintain order
        processed.sort((a, b) => (a._processIndex || 0) - (b._processIndex || 0));

        // Remove the temporary index property
        processed.forEach(product => delete product._processIndex);

        // Update progress to show completion
        setProcessingProgress({ current: totalProducts, total: totalProducts });
        setSimilarProducts(processed);

        // Reset progress after a brief delay to show completion
        setTimeout(() => setProcessingProgress({ current: 0, total: 0 }), 500);
      }
    } catch (err) {
      console.error('Find similar error:', err);
      // Show user-friendly error message
      alert('Failed to find similar products. Please try again.');
    } finally {
      setLoadingSimilar(false);
    }
  }, [data, activeImageIdx, compressImage, fetchMinVariantPrice]);

  // Handle search start - called when search begins
  const handleSearchStart = useCallback(() => {
    setLoadingSimilar(true);
    setShowSimilar(true);
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);

    // Clear pricing cache
    pricingCacheRef.current = {};
    setPricingCache({});
  }, []);

  // Handle search complete - called when search finishes
  const handleSearchComplete = useCallback((products) => {
    setSimilarProducts(products);
    setCurrentPage(1);
    setTotalPages(1);
    setLoadingSimilar(false);
  }, []);

  // Open product variant page - inline in sidebar
  const openProductVariants = useCallback(async (product) => {
    // Set selected product mode inline (no new tab)
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setLoadingSelectedProduct(true);
    setShowSimilar(false); // Hide similar products grid, show product details
    setFinalPriceFromApi(false);
    setFinalPriceBreakdown(null);
    setShowPriceBreakdown(false);
    setCalculatedPriceRanges([]); // Reset calculated price ranges for new product

    // Store selected product basic info
    const selectedInfo = {
      offerId: product.offerId,
      title: product.title,
      image: product.image,
      rmb: product.price.rmb,
      inr: product.price.inr,
    };

    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl.replace(/\/$/, '') + '/product-detail-by-offerid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: product.offerId.toString(), country: 'en' })
      });

      if (!response.ok) throw new Error('Failed to fetch product details');

      const data = await response.json();
      // console.log('[1688 API] Full response for offerId', product.offerId, ':', data);

      const productData = data.result?.result || data.product || data.data || data;
      // console.log('[1688 API] Extracted productData:', {
      //   keys: Object.keys(productData),
      //   productSaleInfo: productData.productSaleInfo,
      //   productShippingInfo: productData.productShippingInfo,
      //   sellerDataInfo: productData.sellerDataInfo,
      //   productAttribute: productData.productAttribute,
      //   tagInfoList: productData.tagInfoList,
      //   productFeature: productData.productFeature,
      //   companyName: productData.companyName,
      //   monthSold: productData.monthSold
      // });

      // Merge with basic info
      productData._selectedInfo = selectedInfo;
      setSelectedProductDetails(productData);

      // Calculate prices for priceRangeList tiers using calculate-batch API
      const priceRangeList = productData.productSaleInfo?.priceRangeList || [];
      if (priceRangeList.length > 0) {
        // Use product-level shipping info for weight/dimensions (no SKU-level info at this point)
        const shippingInfo = productData.productShippingInfo?.skuShippingDetails || {};
        const defaultWeight = 0.5;
        const volumeFactor = 5000;

        let rawWeight = parseFloat(shippingInfo.weight || 0);
        if (rawWeight > 50) rawWeight = rawWeight / 1000;

        let length = parseFloat(shippingInfo.length || 0);
        let width = parseFloat(shippingInfo.width || 0);
        let height = parseFloat(shippingInfo.height || 0);
        if (length > 100 || width > 100 || height > 100) {
          length = length / 10; width = width / 10; height = height / 10;
        }

        const hasDimensions = length > 0 && width > 0 && height > 0;
        const hasWeight = rawWeight > 0;
        const volumetricWeight = hasDimensions ? (length * width * height) / volumeFactor : 0;

        let chargeableWeight;
        if (!hasDimensions && !hasWeight) chargeableWeight = defaultWeight;
        else if (hasWeight && !hasDimensions) chargeableWeight = rawWeight;
        else if (hasDimensions && !hasWeight) chargeableWeight = volumetricWeight;
        else chargeableWeight = Math.max(rawWeight, volumetricWeight);

        try {
          const rangeBatchItems = priceRangeList.map(tier => ({
            ap: parseFloat(tier.price || tier.consignPrice || 0),
            ls: 0,
            hsn: false,
            hsnTaxPercent: 18,
            dw: chargeableWeight,
            vw: volumetricWeight,
            sku: String(selectedInfo.offerId || ''),
            topCategoryId: productData?.topCategoryId || '',
            secondCategoryId: productData?.secondCategoryId || '',
            thirdCategoryId: productData?.thirdCategoryId || ''
          })).filter(item => item.ap > 0);

          if (rangeBatchItems.length > 0) {
            const rangeBatchResponse = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate-batch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: rangeBatchItems })
            });

            if (rangeBatchResponse.ok) {
              const rangeBatchResult = await rangeBatchResponse.json();
              const rangeResults = rangeBatchResult?.data?.results || rangeBatchResult?.results || [];
              const calculated = priceRangeList.map((tier, idx) => ({
                ...tier,
                calculatedInr: rangeResults[idx]?.success ? parseFloat(rangeResults[idx]?.breakdown?.FBP || 0) : null
              }));
              setCalculatedPriceRanges(calculated);
            }
          }
        } catch (err) {
          console.error('[openProductVariants] Error calculating priceRangeList prices:', err);
        }
      }

      // Extract variants/SKUs and shipping info
      let skuInfos = productData.productSkuInfos || productData.skuInfos || productData.skuList ||
        productData.skus || productData.productInfo?.skuInfos || productData.data?.productSkuInfos ||
        productData.variants || [];

      // Get shipping details for variants (for weight/dimensions display only)
      const shippingDetails = productData.productShippingInfo?.skuShippingDetails || [];

      // If no variants exist, synthesize one from the product-level sale/shipping info
      if (skuInfos.length === 0) {
        const productSaleInfo = productData.productSaleInfo || {};
        const productShippingInfo = productData?.productShippingInfo?.skuShippingDetails || {};
        const offerId = selectedInfo.offerId || productData.offerId || productData.productId || '';
        const productCargoNumber = productData.cargoNumber || productData.productCargoNumber || '';
        const vendorPrice = productData.vendorPrice || null;

        const defaultVariant = {
          skuId: String(offerId || ''),
          specId: '',
          amountOnSale: parseFloat(productSaleInfo?.amountOnSale || 0),
          skuAttributes: [],
          cargoNumber: productCargoNumber || '',
          cp: parseFloat(vendorPrice || productSaleInfo?.consignPrice || 0),
          consignPrice: parseFloat(vendorPrice || productSaleInfo?.consignPrice || 0),
          fenxiaoPriceInfo: {},
          weight: Number(productShippingInfo?.weight) || 0,
          length: Number(productShippingInfo?.length) || 0,
          height: Number(productShippingInfo?.height) || 0,
          width: Number(productShippingInfo?.width) || 0,
          // Use AP as the primary price field
          price: parseFloat(
            productSaleInfo?.fenxiaoPriceInfo?.onePiecePrice ||
            productSaleInfo?.fenxiaoPriceInfo?.offerPrice ||
            productSaleInfo?.consignPrice ||
            productSaleInfo?.price ||
            selectedInfo.rmb ||
            0
          ),
        };
        skuInfos = [defaultVariant];
      }

      if (skuInfos.length > 0) {
        // Step 1: Prepare variant data (synchronous operations)
        const variantDataList = skuInfos.map((sku, idx) => {
          const skuId = sku.skuId || sku.id || sku.specId || `sku-${idx}`;
          // AP (Alibaba Price) - primary price
          const skuPrice = parseFloat(sku.price || sku.salePrice || sku.retailPrice || sku.priceInfo?.price || selectedInfo.rmb);
          // CP (Consign Price) - separate from AP
          const skuConsignPrice = parseFloat(sku.consignPrice || sku.cp || skuPrice || 0);

          // Find shipping info for this SKU
          const skuShipping = shippingDetails.find(s => s.skuId == skuId) || shippingDetails[0] || {};

          // Parse weight - convert from grams to kg if needed (same as website PDP)
          let rawWeight = parseFloat(skuShipping.weight || 0);
          if (rawWeight > 50) {
            rawWeight = rawWeight / 1000; // Convert grams to kg
          }
          console.log('rawWeight', rawWeight);

          // Parse dimensions - convert from mm to cm if needed (same as website PDP)
          let length = parseFloat(skuShipping.length || 0);
          let width = parseFloat(skuShipping.width || 0);
          let height = parseFloat(skuShipping.height || 0);

          console.log('length: ', length);
          console.log('width: ', width);
          console.log('height: ', height)

          // If dimensions seem to be in mm (values > 100), convert to cm
          if (length > 100 || width > 100 || height > 100) {
            length = length / 10;
            width = width / 10;
            height = height / 10;
          }

          const hasDimensions = length > 0 && width > 0 && height > 0;
          const hasWeight = rawWeight > 0;
          const dimensions = hasDimensions ? { length, width, height } : null;

          // Same constants as website PDP (PRICE_LOGIC)
          const defaultWeight = 0.5; // PRICE_LOGIC.DEFAULT_DW
          const volumeFactor = 5000; // PRICE_LOGIC.VOLUME_FACTOR

          // Calculate volumetric weight (same as website PDP)
          const volumetricWeight = hasDimensions ? (length * width * height) / volumeFactor : 0;

          // Calculate chargeable weight using same logic as website PDP
          let chargeableWeight;
          if (!hasDimensions && !hasWeight) chargeableWeight = defaultWeight;
          else if (hasWeight && !hasDimensions) chargeableWeight = rawWeight;
          else if (hasDimensions && !hasWeight) chargeableWeight = volumetricWeight;
          else chargeableWeight = Math.max(rawWeight, volumetricWeight);

          // Extract attributes
          let attributes = [];
          let attrImageUrl = null;
          if (sku.skuAttributes?.length > 0) {
            attributes = sku.skuAttributes.map(attr => {
              // Check for image URL in attribute
              if (!attrImageUrl && attr.skuImageUrl) {
                attrImageUrl = attr.skuImageUrl;
              }
              return {
                name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute',
                value: attr.valueTrans || attr.value || attr.attributeValue || '',
                imageUrl: attr.skuImageUrl || null
              };
            });
          } else if (sku.attributes?.length > 0) {
            attributes = sku.attributes.map(attr => {
              if (!attrImageUrl && (attr.skuImageUrl || attr.imageUrl)) {
                attrImageUrl = attr.skuImageUrl || attr.imageUrl;
              }
              return {
                name: attr.name || attr.attributeName || 'Attribute',
                value: attr.value || attr.attributeValue || '',
                imageUrl: attr.skuImageUrl || attr.imageUrl || null
              };
            });
          }

          // Get variant image - check multiple sources including attribute images
          const variantImage = attrImageUrl || sku.skuImage || sku.image || sku.imageUrl || sku.pic || sku.picUrl || selectedInfo.image;

          return {
            idx,
            skuId,
            skuPrice,
            skuConsignPrice,
            rawWeight,
            dimensions,
            chargeableWeight,
            volumetricWeight,
            attributes,
            variantImage,
            stock: sku.amountOnSale || sku.stock || sku.canBookCount || 0,
            attributeText: attributes.map(a => a.value).filter(v => v).join(' / ') || sku.specAttrsText || `Variant ${idx + 1}`
          };
        });

        // Step 2: Batch calculate importerr prices for all variants in one API call
        const validVariants = variantDataList.filter(v => v.skuPrice > 0);
        const batchItems = validVariants.map(variantData => ({
          ap: parseFloat(variantData.skuPrice),
          ls: 0,
          hsn: false,
          hsnTaxPercent: 18,
          dw: variantData.chargeableWeight,
          vw: variantData.volumetricWeight,
          sku: String(product.offerId || ''),
          topCategoryId: productData?.topCategoryId || '',
          secondCategoryId: productData?.secondCategoryId || '',
          thirdCategoryId: productData?.thirdCategoryId || ''
        }));

        // Map from skuId -> { price, breakdown }
        const importerrPriceMap = {};
        if (batchItems.length > 0) {
          try {
            const batchResponse = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate-batch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: batchItems })
            });
            if (batchResponse.ok) {
              const batchResult = await batchResponse.json();
              const results = batchResult?.data?.results || batchResult?.results || [];
              results.forEach((r, idx) => {
                const variantData = validVariants[idx];
                if (variantData && r.success) {
                  importerrPriceMap[variantData.skuId] = {
                    price: parseFloat(r.breakdown?.FBP || 0) || null,
                    breakdown: r.breakdown || null
                  };
                }
              });
            }
          } catch (err) {
            console.error('[openProductVariants] Error in batch Importerr price calculation:', err);
          }
        }

        // Step 3: Build price results from batch map
        const priceResults = variantDataList.map(variantData => {
          const importerrData = importerrPriceMap[variantData.skuId] || { price: null, breakdown: null };
          return {
            variantData,
            priceData: { inr: 0, breakdown: null }, // No actual price calculation
            importerrPrice: importerrData.price,
            importerrBreakdown: importerrData.breakdown
          };
        });

        // Step 4: Build final variant objects
        const processedVariants = priceResults.map(result => {
          const { variantData, priceData, importerrPrice, importerrBreakdown } = result;

          return {
            id: variantData.skuId,
            skuId: variantData.skuId,
            image: variantData.variantImage,
            price: {
              rmb: variantData.skuPrice,
              inr: priceData.inr || 0,
              breakdown: priceData.breakdown,
              ap: variantData.skuPrice, // AP (Alibaba Price)
              cp: variantData.skuConsignPrice   // CP (Consign Price)
            },
            ap: variantData.skuPrice, // AP (Alibaba Price) - for easy access
            cp: variantData.skuConsignPrice,  // CP (Consign Price) - for easy access
            consignPrice: variantData.skuConsignPrice, // CP (Consign Price) - alternative name
            importerrPrice, // Importerr calculated price
            importerrBreakdown, // Importerr price breakdown
            stock: variantData.stock,
            attributes: variantData.attributes,
            attributeText: variantData.attributeText,
            // Weight and dimensions info
            weight: variantData.rawWeight,
            dimensions: variantData.dimensions,
            chargeableWeight: variantData.chargeableWeight,
            volumetricWeight: variantData.volumetricWeight,
          };
        });

        setSelectedProductVariants(processedVariants);
      }
    } catch (err) {
      console.error('Failed to fetch selected product details:', err);
      // Still show basic info even if detailed fetch fails
      setSelectedProductDetails({ _selectedInfo: selectedInfo });
    } finally {
      setLoadingSelectedProduct(false);
    }

    // Call product offer API in background (no need to wait for completion)
    (async () => {
      try {
        const token = localStorage.getItem('LOGIN_HEADER');
        const response = await fetch(`${BACKEND_API}product/offer/${product.offerId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response?.status == 200) {
          setShowGetFinalPriceButton(true);
        } else {
          console.error('[openProductVariants] Product offer API returned error status:', response.status);
        }
      } catch (err) {
        console.error('[openProductVariants] Background product offer API call failed:', err);
      }
    })();
  }, [calculateINRPrice]);

  // Go back to similar products grid
  const backToSimilarProducts = () => {
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setShowSimilar(true);
  };

  // Handle Get Final Price button - fetches final selling price from API
  const handleGetFinalPrice = useCallback(async () => {
    const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];

    if (!currentVariant) {
      alert('No variant selected');
      return;
    }

    const offerId = selectedProductDetails?._selectedInfo?.offerId || selectedProductDetails?.offerId || '';
    if (!offerId) {
      alert('Unable to calculate final price - offer ID is not available');
      return;
    }

    // Build payload for get-final-price API
    const payload = {
      offerId,
      isFromAdmin: true,
      variants: [
        {
          skuId: currentVariant?.skuId || currentVariant?.id || 0,
          price: currentVariant?.importerrPrice || 0,
          selectedQuantity: 1,
          cp: Number(currentVariant?.cp || currentVariant?.consignPrice || 0),
          weight: currentVariant?.weight || selectedProductDetails?.productShippingInfo?.weight || 0
        }
      ]
    };

    setLoadingFinalPrice(true);
    setFinalPriceFromApi(null);
    try {
      const response = await ProductsApiService.getFinalPrice(payload);
      if (response?.status?.code === 200 && response?.data) {
        const apiPrice = response.data.finalPrice;
        setFinalPriceFromApi(apiPrice);
        setFinalPriceBreakdown(response.data); // Store full breakdown including showWarning

        // Also store in editedVariantData for use in margin analysis
        const variantKey = currentVariant?.id || currentVariant?.skuId || `variant-${selectedVariantImageIdx}`;
        setEditedVariantData(prev => ({
          ...prev,
          [variantKey]: {
            ...prev[variantKey],
            finalPrice: apiPrice,
            wholesaleFinalPrice: response.data.finalFBP || apiPrice
          }
        }));
      } else {
        console.error('[handleGetFinalPrice] Unexpected response:', response);
        alert('Failed to fetch final price. Please try again.');
      }
    } catch (err) {
      console.error('[handleGetFinalPrice] Error:', err);
      alert('Error fetching final price. Please try again.');
    } finally {
      setLoadingFinalPrice(false);
    }
  }, [selectedProductVariants, selectedVariantImageIdx, selectedProductDetails]);

  // Fetch product data function
  const fetchProductData = useCallback(() => {
    if (!asinFromQuery) { setError("Missing ASIN"); setLoading(false); return; }
    setLoading(true);
    setError("");

    adminApiService.getAmazonProductByAsin(asinFromQuery)
      .then((res) => {
        if (res?.success && res?.data) {
          setData(res.data);
          if (res.data.error) setError(res.data.error);
        } else {
          setError(res?.message || "Failed to fetch product details");
        }
      })
      .catch((err) => setError(err?.message || "Failed to fetch product details"))
      .finally(() => setLoading(false));
  }, [asinFromQuery]);

  // Fetch failed order amount for this ASIN (or use URL params for remap mode)
  const fetchFailedOrderAmount = useCallback(async () => {
    if (!asinFromQuery) return;

    // If in remap mode, fetch all orders with same ASIN from mapped orders
    if (isRemapMode) {
      // console.log('[Remap] Fetching all orders with ASIN:', asinFromQuery);

      try {
        // Use the failed-amounts API which already handles ASIN lookup
        const result = await adminApiService.post('/orders/failed-amounts', {
          asins: [asinFromQuery],
          fromMappedOrders: true  // Flag to search in mapped orders instead of failed orders
        });

        // console.log('[Remap] Failed amounts response:', result);

        if (result.success && result.data && result.data[asinFromQuery]) {
          const data = result.data[asinFromQuery];
          setFailedOrderAmount({
            totalAmount: data.totalAmount || remapOrderValue,
            orderCount: data.orderCount || 1,
            totalQuantity: data.totalQuantity || remapOrderQty,
            orders: data.orders || [],
            isRemapOrder: true
          });
        } else {
          // Fallback to URL params
          setFailedOrderAmount({
            totalAmount: remapOrderValue * remapOrderQty,
            orderCount: 1,
            isRemapOrder: true
          });
        }
      } catch (err) {
        console.error('[Remap] Failed to fetch orders:', err);
        // Fallback to URL params
        setFailedOrderAmount({
          totalAmount: remapOrderValue * remapOrderQty,
          orderCount: 1,
          isRemapOrder: true
        });
      }
      return;
    }

    // For non-remap mode, fetch from failed orders API
    try {
      const result = await adminApiService.post('/orders/failed-amounts', { asins: [asinFromQuery] });
      // console.log('Failed order amounts response:', result);
      if (result.success && result.data && result.data[asinFromQuery]) {
        setFailedOrderAmount(result.data[asinFromQuery]);
      }
    } catch (err) {
      console.error('Failed to fetch order amount:', err);
    }
  }, [asinFromQuery, isRemapMode, remapOrderValue, remapOrderQty, oldMappedPrice]);

  // Fetch selected product details and variants
  const fetchSelectedProductDetails = useCallback(async () => {
    if (!isSelectedProductMode || !selectedOfferId) return;

    // Prevent duplicate fetches
    if (hasFetchedSelectedProduct.current) return;
    hasFetchedSelectedProduct.current = true;

    setLoadingSelectedProduct(true);
    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl.replace(/\/$/, '') + '/product-detail-by-offerid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: selectedOfferId.toString(), country: 'en' })
      });

      if (!response.ok) throw new Error('Failed to fetch product details');

      const data = await response.json();
      // console.log('Product detail API response:', data);

      const productData = data.result?.result || data.product || data.data || data;
      setSelectedProductDetails(productData);

      // Extract variants/SKUs
      let skuInfos = productData.productSkuInfos || productData.skuInfos || productData.skuList ||
        productData.skus || productData.productInfo?.skuInfos || productData.data?.productSkuInfos ||
        productData.variants || [];

      // Get shipping details for variants
      const shippingDetails = productData.productShippingInfo?.skuShippingDetails || [];

      // If no variants exist, synthesize one from the product-level sale/shipping info
      if (skuInfos.length === 0) {
        const productSaleInfo = productData.productSaleInfo || {};
        const productShippingInfo = productData.productShippingInfo || {};
        const offerId = selectedOfferId || productData.offerId || productData.productId || '';
        const productCargoNumber = productData.cargoNumber || productData.productCargoNumber || '';
        const vendorPrice = productData.vendorPrice || null;

        const defaultVariant = {
          skuId: String(offerId || ''),
          specId: '',
          amountOnSale: parseFloat(productSaleInfo?.amountOnSale || 0),
          skuAttributes: [],
          cargoNumber: productCargoNumber || '',
          cp: parseFloat(vendorPrice || productSaleInfo?.consignPrice || 0),
          consignPrice: parseFloat(vendorPrice || productSaleInfo?.consignPrice || 0),
          fenxiaoPriceInfo: {},
          weight: Number(productShippingInfo?.weight) || 0,
          length: Number(productShippingInfo?.length) || 0,
          height: Number(productShippingInfo?.height) || 0,
          width: Number(productShippingInfo?.width) || 0,
          // Use AP as the primary price field
          price: parseFloat(
            productSaleInfo?.fenxiaoPriceInfo?.onePiecePrice ||
            productSaleInfo?.fenxiaoPriceInfo?.offerPrice ||
            productSaleInfo?.consignPrice ||
            productSaleInfo?.price ||
            selectedRmb ||
            0
          ),
        };
        skuInfos = [defaultVariant];
      }

      if (skuInfos.length > 0) {
        // Step 1: Parse all SKU data (weight, dimensions, prices) without API calls
        const skuDataList = skuInfos.map((sku, idx) => {
          const skuId = sku.skuId || sku.id || sku.specId || `sku-${idx}`;
          const skuPrice = parseFloat(sku.price || sku.consignPrice || sku.salePrice || sku.retailPrice || sku.priceInfo?.price || selectedRmb);

          const skuShipping = shippingDetails.find(s => s.skuId == skuId) || shippingDetails[0] || {};

          let rawWeight = parseFloat(skuShipping.weight || 0);
          if (rawWeight > 50) rawWeight = rawWeight / 1000;

          let length = parseFloat(skuShipping.length || 0);
          let width = parseFloat(skuShipping.width || 0);
          let height = parseFloat(skuShipping.height || 0);
          if (length > 100 || width > 100 || height > 100) {
            length = length / 10; width = width / 10; height = height / 10;
          }

          const hasDimensions = length > 0 && width > 0 && height > 0;
          const hasWeight = rawWeight > 0;
          const dimensions = hasDimensions ? { length, width, height } : null;
          const defaultWeight = 0.5;
          const volumeFactor = 5000;
          const volumetricWeight = hasDimensions ? (length * width * height) / volumeFactor : 0;

          let chargeableWeight;
          if (!hasDimensions && !hasWeight) chargeableWeight = defaultWeight;
          else if (hasWeight && !hasDimensions) chargeableWeight = Math.max(rawWeight, defaultWeight);
          else if (hasDimensions && !hasWeight) chargeableWeight = Math.max(volumetricWeight, defaultWeight);
          else chargeableWeight = Math.max(rawWeight, volumetricWeight);

          return { sku, idx, skuId, skuPrice, rawWeight, dimensions, chargeableWeight, volumetricWeight };
        });

        // Step 2: Batch calculate importerr prices for all SKUs in one API call
        const validSkuData = skuDataList.filter(d => d.skuPrice > 0);
        const importerrPriceMap = {};
        if (validSkuData.length > 0) {
          try {
            const batchItems = validSkuData.map(d => ({
              ap: parseFloat(d.skuPrice),
              ls: 0,
              hsn: false,
              hsnTaxPercent: 18,
              dw: d.chargeableWeight,
              vw: d.volumetricWeight,
              sku: String(selectedOfferId || ''),
              topCategoryId: productData?.topCategoryId || '',
              secondCategoryId: productData?.secondCategoryId || '',
              thirdCategoryId: productData?.thirdCategoryId || ''
            }));

            const batchResponse = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate-batch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: batchItems })
            });

            if (batchResponse.ok) {
              const batchResult = await batchResponse.json();
              const results = batchResult?.data?.results || batchResult?.results || [];
              results.forEach((r, i) => {
                const skuData = validSkuData[i];
                if (skuData && r.success) {
                  importerrPriceMap[skuData.skuId] = {
                    price: parseFloat(r.breakdown?.FBP || 0) || null,
                    breakdown: r.breakdown || null
                  };
                }
              });
            }
          } catch (err) {
            console.error('[fetchSelectedProductDetails] Error in batch Importerr price calculation:', err);
          }
        }

        // Step 3: Build processed variants using batch results + parallel priceData calls
        const processedVariants = await Promise.all(
          skuDataList.map(async ({ sku, idx, skuId, skuPrice, rawWeight, dimensions, chargeableWeight, volumetricWeight }) => {
            // Calculate price using "actual" algorithm (with algoId)
            const priceData = await calculateINRPrice(skuPrice, selectedOfferId, skuId, rawWeight, dimensions, null, {
              topCategoryId: productData?.topCategoryId,
              secondCategoryId: productData?.secondCategoryId,
              thirdCategoryId: productData?.thirdCategoryId
            });

            const importerrData = importerrPriceMap[skuId] || { price: null, breakdown: null };
            const importerrPrice = importerrData.price;
            const importerrBreakdown = importerrData.breakdown;

            let attributes = [];
            let attrImageUrl = null;
            if (sku.skuAttributes?.length > 0) {
              attributes = sku.skuAttributes.map(attr => {
                if (!attrImageUrl && attr.skuImageUrl) attrImageUrl = attr.skuImageUrl;
                return {
                  name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute',
                  value: attr.valueTrans || attr.value || attr.attributeValue || '',
                  imageUrl: attr.skuImageUrl || null
                };
              });
            } else if (sku.attributes?.length > 0) {
              attributes = sku.attributes.map(attr => {
                if (!attrImageUrl && (attr.skuImageUrl || attr.imageUrl)) attrImageUrl = attr.skuImageUrl || attr.imageUrl;
                return {
                  name: attr.name || attr.attributeName || 'Attribute',
                  value: attr.value || attr.attributeValue || '',
                  imageUrl: attr.skuImageUrl || attr.imageUrl || null
                };
              });
            }

            const variantImage = attrImageUrl || sku.skuImage || sku.image || sku.imageUrl || sku.pic || sku.picUrl || selectedImage;

            return {
              id: skuId,
              skuId: skuId,
              image: variantImage,
              price: {
                rmb: skuPrice,
                inr: priceData.inr || 0,
                breakdown: priceData.breakdown
              },
              importerrPrice,
              importerrBreakdown,
              stock: sku.amountOnSale || sku.stock || sku.canBookCount || 0,
              attributes: attributes,
              attributeText: attributes.map(a => a.value).filter(v => v).join(' / ') || sku.specAttrsText || `Variant ${idx + 1}`,
              weight: rawWeight,
              dimensions: dimensions,
              chargeableWeight: chargeableWeight,
              volumetricWeight: volumetricWeight,
            };
          })
        );
        setSelectedProductVariants(processedVariants);
      }
    } catch (err) {
      console.error('Failed to fetch selected product details:', err);
    } finally {
      setLoadingSelectedProduct(false);
    }
  }, [isSelectedProductMode, selectedOfferId, selectedRmb, selectedImage, calculateINRPrice]);

  // Open confirmation modal before mapping
  const openConfirmModal = (variant) => {
    setConfirmVariant(variant);
    setMappingRemark("");
    setSelectedHsnCategory('');
    setSelectedHsnMapping(null);
    setShowConfirmModal(true);
  };

  // Map variant to ASIN (called after confirmation) - Background process
  const handleMapVariant = async () => {
    const variant = confirmVariant;
    const offerId = selectedProductDetails?._selectedInfo?.offerId;
    const remark = mappingRemark;
    const asin = asinFromQuery;
    const productTitle = selectedProductDetails?.subjectTrans || selectedProductDetails?._selectedInfo?.title;
    const productImage = variant.image || selectedProductDetails?._selectedInfo?.image;

    if (!asin || !offerId) {
      // Store error result in sessionStorage for global notification provider
      sessionStorage.setItem('mappingResult', JSON.stringify({ success: false, message: 'Missing ASIN or Product ID' }));
      setShowConfirmModal(false);
      return;
    }

    const token = localStorage.getItem('LOGIN_HEADER');
    if (!token) {
      // Store error result in sessionStorage for global notification provider
      sessionStorage.setItem('mappingResult', JSON.stringify({ success: false, message: 'Please login to map products' }));
      setShowConfirmModal(false);
      return;
    }

    // Close confirmation modal immediately and show processing indicator
    setShowConfirmModal(false);
    setConfirmVariant(null);
    setIsMapping(true);

    // Store mapping info for background process tracking
    const mappingInfo = {
      asin,
      offerId,
      skuId: variant.skuId,
      variantText: variant.attributeText,
      startTime: Date.now(),
      isRemap: isRemapMode,
      remapOrderId: remapOrderId
    };

    // Save to sessionStorage so we can show result even after navigation
    sessionStorage.setItem('pendingMapping', JSON.stringify(mappingInfo));

    // Prepare HSN info if selected (matching hsn_mappings collection structure)
    const hsnInfo = selectedHsnMapping ? {
      hsnMappingId: selectedHsnMapping._id,
      category: selectedHsnMapping.category || selectedHsnMapping.Item,
      Item: selectedHsnMapping.Item || selectedHsnMapping.category,
      hsnCode: selectedHsnMapping.hsnCode || String(selectedHsnMapping.HSN),
      HSN: selectedHsnMapping.HSN || selectedHsnMapping.hsnCode,
      BCD: selectedHsnMapping.BCD || selectedHsnMapping.bcd,
      Surcharge: selectedHsnMapping.Surcharge || selectedHsnMapping.surcharge,
      FinalCustomDuty: selectedHsnMapping.FinalCustomDuty,
      IGST: selectedHsnMapping.IGST,
      gstRate: selectedHsnMapping.gstRate,
      igstRate: selectedHsnMapping.igstRate,
      customsDutyRate: selectedHsnMapping.customsDutyRate,
    } : null;

    // Run mapping in background
    try {
      // Use different endpoint for remap vs new mapping
      const endpoint = isRemapMode ? `${BACKEND_API}admin/remap-order` : `${BACKEND_API}admin/map-asin`;

      // Prepare custom prices by orderDetail (variant-wise) - orderDetailId is orderDetail _id
      const customPricesByOrderDetailForApi = {};
      Object.keys(customPricesByOrderDetail).forEach(detailId => {
        const price = customPricesByOrderDetail[detailId];
        if (price && !isNaN(parseFloat(price)) && parseFloat(price) > 0) {
          customPricesByOrderDetailForApi[detailId] = parseFloat(price);
        }
      });

      // Prepare custom quantities by orderDetail (orderDetailId is orderDetail _id)
      const customQuantitiesByOrderDetailForApi = {};
      Object.keys(customQuantitiesByOrderDetail).forEach(detailId => {
        const qty = customQuantitiesByOrderDetail[detailId];
        if (qty && !isNaN(parseInt(qty)) && parseInt(qty) > 0) {
          customQuantitiesByOrderDetailForApi[detailId] = parseInt(qty);
        }
      });

      // Get edited weight and dimensions for ALL variants (not just the one being mapped)
      // This allows "Apply to All Variants" functionality
      const editedWeightDimensions = {};

      // Collect edited data for all variants
      selectedProductVariants.forEach(v => {
        const vKey = v?.id || v?.skuId;
        if (!vKey) return;

        const editedData = editedVariantData[vKey] || {};
        if (editedData.weight !== undefined || editedData.length !== undefined || editedData.width !== undefined || editedData.height !== undefined || editedData.finalPrice !== undefined || editedData.wholesaleFinalPrice !== undefined) {
          editedWeightDimensions[v.skuId || v.id || vKey] = {
            weight: editedData.weight !== undefined ? editedData.weight : null,
            length: editedData.length !== undefined ? editedData.length : null,
            width: editedData.width !== undefined ? editedData.width : null,
            height: editedData.height !== undefined ? editedData.height : null,
            finalPrice: editedData.finalPrice !== undefined ? editedData.finalPrice : null,
            wholesaleFinalPrice: editedData.wholesaleFinalPrice !== undefined ? editedData.wholesaleFinalPrice : null
          };
        }
      });

      const requestBody = isRemapMode ? {
        orderId: remapOrderId,
        asin: asin,
        offerId: offerId.toString(),
        skuId: variant.skuId || null,
        productTitle: productTitle,
        productImage: productImage,
        productPrice: variant.price.rmb,
        productPriceInr: variant.price.inr,
        variantAttributes: variant.attributeText,
        remark: remark || null,
        hsnInfo: hsnInfo,
        variantBreakdown: variant.breakdown || null,
        customFinalPrice: customFinalPrice ? parseFloat(customFinalPrice) : null, // Keep for backward compatibility
        customPricesByOrderDetail: Object.keys(customPricesByOrderDetailForApi).length > 0 ? customPricesByOrderDetailForApi : null,
        customQuantitiesByOrderDetail: Object.keys(customQuantitiesByOrderDetailForApi).length > 0 ? customQuantitiesByOrderDetailForApi : null,
        editedWeightDimensions: Object.keys(editedWeightDimensions).length > 0 ? editedWeightDimensions : null, // Variant-wise edited weight and dimensions
      } : {
        asin: asin,
        offerId: offerId.toString(),
        skuId: variant.skuId || null,
        productTitle: productTitle,
        productImage: productImage,
        productPrice: variant.price.rmb,
        variantAttributes: variant.attributeText,
        remark: remark || null,
        hsnInfo: hsnInfo,
        customFinalPrice: customFinalPrice ? parseFloat(customFinalPrice) : null, // Keep for backward compatibility
        customPricesByOrderDetail: Object.keys(customPricesByOrderDetailForApi).length > 0 ? customPricesByOrderDetailForApi : null,
        customQuantitiesByOrderDetail: Object.keys(customQuantitiesByOrderDetailForApi).length > 0 ? customQuantitiesByOrderDetailForApi : null,
        editedWeightDimensions: Object.keys(editedWeightDimensions).length > 0 ? editedWeightDimensions : null, // Variant-wise edited weight and dimensions
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      // Clear pending mapping
      sessionStorage.removeItem('pendingMapping');

      // For remap mode, include count of other orders updated
      const otherOrdersUpdated = data.data?.otherOrdersUpdated || 0;
      const remapMessage = otherOrdersUpdated > 0
        ? `Order re-mapped successfully! Also updated ${otherOrdersUpdated} other orders with same ASIN.`
        : 'Order re-mapped successfully!';

      const result = response.ok && data.success ? {
        success: true,
        message: isRemapMode ? remapMessage : data.message,
        asin: asin,
        skuId: variant.skuId,
        offerId: data.data?.offerId,
        productSku: data.data?.productSku,
        convertedOrders: isRemapMode ? (1 + otherOrdersUpdated) : (data.data?.convertedOrders || 0),
        convertedOrderIds: isRemapMode ? [remapOrderId] : (data.data?.convertedOrderIds || []),
        convertedOrderDetails: data.data?.convertedOrderDetails || [],
        isRemap: isRemapMode,
        otherOrdersUpdated: otherOrdersUpdated,
      } : { success: false, message: data.message || 'Failed to map variant' };

      // Store result in sessionStorage for global notification provider
      sessionStorage.setItem('mappingResult', JSON.stringify(result));

    } catch (error) {
      console.error('Map variant failed:', error);
      sessionStorage.removeItem('pendingMapping');

      const result = { success: false, message: 'Failed to map variant: ' + error.message };
      sessionStorage.setItem('mappingResult', JSON.stringify(result));
    } finally {
      setIsMapping(false);
    }
  };

  useEffect(() => {
    fetchProductData();
    fetchFailedOrderAmount();
  }, [fetchProductData, fetchFailedOrderAmount]);

  // Fetch count of other orders with same ASIN (for remap mode)
  useEffect(() => {
    if (!isRemapMode || !asinFromQuery) return;

    const fetchAffectedOrdersCount = async () => {
      try {
        // console.log('[Remap] Fetching affected orders count for ASIN:', asinFromQuery, 'excludeOrderId:', remapOrderId);
        const response = await fetch(
          `${BACKEND_API}admin/orders/count-by-asin?asin=${encodeURIComponent(asinFromQuery)}&excludeOrderId=${remapOrderId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        // console.log('[Remap] Response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          // console.log('[Remap] Affected orders count response:', data);
          setAffectedOrdersCount(data.count || 0);
        } else {
          console.error('[Remap] Failed to fetch count, status:', response.status);
        }
      } catch (err) {
        console.error('Failed to fetch affected orders count:', err);
      }
    };

    fetchAffectedOrdersCount();
  }, [isRemapMode, asinFromQuery, remapOrderId]);

  useEffect(() => {
    if (isSelectedProductMode) {
      fetchSelectedProductDetails();
    }
  }, [isSelectedProductMode, fetchSelectedProductDetails]);

  // COMMENTED OUT: Recalculate prices when algorithm changes - use variant-based calculation for minimum FBP
  // This uses fetchMinVariantPrice to calculate product.price?.inr
  /*
  useEffect(() => {
    if (!selectedAlgoId || similarProducts.length === 0) return;

    const recalculateSimilarPrices = async () => {
      // Clear cache to force recalculation with new algorithm
      pricingCacheRef.current = {};

      const updated = await Promise.all(
        similarProducts.map(async (product) => {
          // Fetch variant details and get minimum FBP price (same as initial load)
          const variantPriceData = await fetchMinVariantPrice(product.offerId, product.price.rmb, product.image);
          return {
            ...product,
            price: {
              rmb: variantPriceData.minRmb,
              inr: variantPriceData.minInr,
              breakdown: variantPriceData.breakdown
            },
            variantCount: variantPriceData.variantCount
          };
        })
      );
      setSimilarProducts(updated);
    };

    recalculateSimilarPrices();
  }, [selectedAlgoId]); // Only recalculate when algorithm changes
  */

  // Recalculate variant prices when algorithm changes
  useEffect(() => {
    if (!selectedAlgoId || selectedProductVariants.length === 0 || !selectedProductDetails) return;

    const recalculateVariantPrices = async () => {
      // Clear cache to force recalculation with new algorithm
      pricingCacheRef.current = {};

      const offerId = selectedProductDetails._selectedInfo?.offerId;
      const updated = await Promise.all(
        selectedProductVariants.map(async (variant) => {
          const priceData = await calculateINRPrice(variant.price.rmb, offerId, variant.skuId, variant.weight, variant.dimensions, null, {
            topCategoryId: selectedProductDetails?.topCategoryId,
            secondCategoryId: selectedProductDetails?.secondCategoryId,
            thirdCategoryId: selectedProductDetails?.thirdCategoryId
          });
          return {
            ...variant,
            price: {
              ...variant.price,
              inr: priceData.inr,
              breakdown: priceData.breakdown
            }
          };
        })
      );
      setSelectedProductVariants(updated);
    };

    recalculateVariantPrices();
  }, [selectedAlgoId]); // Only recalculate when algorithm changes

  // Reload/Retry function
  const handleReload = () => {
    setData(null);
    setError("");
    fetchProductData();
  };

  // Open dimensions modal with pre-filled values from variant
  const openDimensionsModal = (variant) => {
    const variantKey = variant?.id || variant?.skuId || `variant-${selectedVariantImageIdx}`;
    const editedData = editedVariantData[variantKey] || {};
    setEditingVariantForWeightDimensions(variant);
    setDimFormValues({
      length: String(editedData.length !== undefined ? editedData.length : (variant?.dimensions?.length || variant?.length || '')),
      width: String(editedData.width !== undefined ? editedData.width : (variant?.dimensions?.width || variant?.width || '')),
      height: String(editedData.height !== undefined ? editedData.height : (variant?.dimensions?.height || variant?.height || '')),
      weight: String(editedData.weight !== undefined ? editedData.weight : (variant?.weight || '')),
    });
    setDimFormErrors({});
    setDimToast(null);
    setShowEditWeightDimensionsModal(true);
  };

  // Validate dimensions form — all fields required, numeric only
  const validateDimForm = (values) => {
    const errors = {};
    ['length', 'width', 'height', 'weight'].forEach((field) => {
      const val = values[field];
      if (val === '' || val === null || val === undefined) {
        errors[field] = 'Required';
      } else if (isNaN(Number(val)) || Number(val) <= 0) {
        errors[field] = 'Must be a positive number';
      }
    });
    return errors;
  };

  // Resolve product draft _id from offerId via the admin API
  const resolveDraftId = useCallback(async () => {
    const offerId = selectedProductDetails?._selectedInfo?.offerId || selectedProductDetails?.offerId;
    if (!offerId) return null;
    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      const res = await fetch(`${BACKEND_API}admin/product-by-offerid/${encodeURIComponent(offerId)}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) return null;
      const json = await res.json();
      return json?.data?.product?._id || null;
    } catch {
      return null;
    }
  }, [selectedProductDetails]);

  // Apply dimensions to current variant
  const handleApplyDimensionsToCurrent = async () => {
    const errors = validateDimForm(dimFormValues);
    if (Object.keys(errors).length > 0) {
      setDimFormErrors(errors);
      return;
    }
    setDimApplyingCurrent(true);
    setDimToast(null);
    try {
      const dimensions = {
        length: parseFloat(dimFormValues.length),
        width: parseFloat(dimFormValues.width),
        height: parseFloat(dimFormValues.height),
        weight: parseFloat(dimFormValues.weight),
      };
      const variant = editingVariantForWeightDimensions;
      const variantKey = variant?.id || variant?.skuId || `variant-${selectedVariantImageIdx}`;

      // Update local state first
      setEditedVariantData(prev => ({
        ...prev,
        [variantKey]: {
          ...prev[variantKey],
          weight: dimensions.weight,
          length: dimensions.length,
          width: dimensions.width,
          height: dimensions.height,
          _lastAppliedDimensions: { ...dimensions }
        }
      }));

      setSelectedProductVariants(prev => prev.map(v => {
        const vKey = v?.id || v?.skuId;
        if (vKey === variantKey) {
          return {
            ...v,
            weight: dimensions.weight,
            dimensions: { length: dimensions.length, width: dimensions.width, height: dimensions.height },
            length: dimensions.length,
            width: dimensions.width,
            height: dimensions.height,
          };
        }
        return v;
      }));

      // Persist to DB via API
      const draftId = await resolveDraftId();
      if (draftId) {
        const skuId = variant?.skuId || variant?.id;
        const specId = variant?.specId;
        const payload = {
          applyToAll: false,
          updates: [{ skuId, specId, update: dimensions }],
        };
        const apiRes = await productDraftApiService.updateDraftVariantDimensions(draftId, payload);
        if (apiRes?.status?.code != 200) {
          setDimToast({ type: 'error', message: apiRes?.message || 'Saved locally but failed to persist to database.' });
          setDimApplyingCurrent(false);
          return;
        }
      }

      // Recalculate listing price for the updated variant
      try {
        const offerId = selectedProductDetails?._selectedInfo?.offerId || selectedProductDetails?.offerId || '';
        const rmbPrice = variant?.price?.rmb || variant?.ap || 0;
        if (rmbPrice > 0) {
          // Invalidate cache entry so fresh price is fetched
          pricingCacheRef.current = Object.fromEntries(
            Object.entries(pricingCacheRef.current).filter(([k]) => !k.startsWith(`${rmbPrice.toFixed(2)}_${offerId}_${variant?.skuId || variant?.id}`))
          );

          // Compute chargeable / volumetric weights from new dimensions
          const volumeFactor = 5000;
          const newVolumetricWeight = (dimensions.length * dimensions.width * dimensions.height) / volumeFactor;
          const newChargeableWeight = Math.max(dimensions.weight, newVolumetricWeight);

          const batchResponse = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                ap: parseFloat(rmbPrice),
                ls: 0,
                hsn: false,
                hsnTaxPercent: 18,
                dw: newChargeableWeight,
                vw: newVolumetricWeight,
                sku: String(offerId),
                topCategoryId: selectedProductDetails?.topCategoryId || '',
                secondCategoryId: selectedProductDetails?.secondCategoryId || '',
                thirdCategoryId: selectedProductDetails?.thirdCategoryId || ''
              }]
            })
          });

          if (batchResponse.ok) {
            const batchResult = await batchResponse.json();
            const r = (batchResult?.data?.results || batchResult?.results || [])[0];
            if (r?.success) {
              const newImporterrPrice = parseFloat(r.breakdown?.FBP || 0) || null;
              const newImporterrBreakdown = r.breakdown || null;
              setSelectedProductVariants(prev => prev.map(v => {
                const vKey = v?.id || v?.skuId;
                if (vKey === variantKey) {
                  return {
                    ...v,
                    importerrPrice: newImporterrPrice,
                    importerrBreakdown: newImporterrBreakdown,
                    chargeableWeight: newChargeableWeight,
                    volumetricWeight: newVolumetricWeight,
                  };
                }
                return v;
              }));
            }
          }
        }
      } catch (priceErr) {
        console.warn('[handleApplyDimensionsToCurrent] Listing price recalculation failed:', priceErr);
      }

      setDimToast({ type: 'success', message: draftId ? 'Dimensions saved to current variant.' : 'Dimensions applied locally (product not yet in database).' });
      setTimeout(() => {
        setShowEditWeightDimensionsModal(false);
        setEditingVariantForWeightDimensions(null);
        setDimToast(null);
      }, 1400);
    } catch (err) {
      setDimToast({ type: 'error', message: 'Failed to apply dimensions. Please try again.' });
    } finally {
      setDimApplyingCurrent(false);
    }
  };

  // Apply dimensions to all variants
  const handleApplyDimensionsToAll = async () => {
    const errors = validateDimForm(dimFormValues);
    if (Object.keys(errors).length > 0) {
      setDimFormErrors(errors);
      return;
    }
    setDimApplyingAll(true);
    setDimToast(null);
    try {
      const dimensions = {
        length: parseFloat(dimFormValues.length),
        width: parseFloat(dimFormValues.width),
        height: parseFloat(dimFormValues.height),
        weight: parseFloat(dimFormValues.weight),
      };

      // Update local state first
      const updatedEditedData = { ...editedVariantData };
      selectedProductVariants.forEach(v => {
        const vKey = v?.id || v?.skuId;
        if (!vKey) return;
        updatedEditedData[vKey] = {
          ...updatedEditedData[vKey],
          weight: dimensions.weight,
          length: dimensions.length,
          width: dimensions.width,
          height: dimensions.height,
          _lastAppliedDimensions: { ...dimensions }
        };
      });
      setEditedVariantData(updatedEditedData);

      setSelectedProductVariants(prev => prev.map(v => ({
        ...v,
        weight: dimensions.weight,
        dimensions: { length: dimensions.length, width: dimensions.width, height: dimensions.height },
        length: dimensions.length,
        width: dimensions.width,
        height: dimensions.height,
      })));

      // Persist to DB via API
      const draftId = await resolveDraftId();
      if (draftId) {
        const payload = { applyToAll: true, update: dimensions };
        const apiRes = await productDraftApiService.updateDraftVariantDimensions(draftId, payload);
        if (apiRes?.status?.code != 200) {
          setDimToast({ type: 'error', message: apiRes?.message || 'Saved locally but failed to persist to database.' });
          setDimApplyingAll(false);
          return;
        }
      }

      const count = selectedProductVariants.length;

      // Recalculate listing prices for all variants with new dimensions
      try {
        const offerId = selectedProductDetails?._selectedInfo?.offerId || selectedProductDetails?.offerId || '';
        const volumeFactor = 5000;
        const newVolumetricWeight = (dimensions.length * dimensions.width * dimensions.height) / volumeFactor;
        const newChargeableWeight = Math.max(dimensions.weight, newVolumetricWeight);

        const variantsWithPrice = selectedProductVariants.filter(v => (v?.price?.rmb || v?.ap || 0) > 0);
        if (variantsWithPrice.length > 0) {
          // Invalidate cache for all affected variants
          pricingCacheRef.current = {};

          const batchItems = variantsWithPrice.map(v => ({
            ap: parseFloat(v?.price?.rmb || v?.ap || 0),
            ls: 0,
            hsn: false,
            hsnTaxPercent: 18,
            dw: newChargeableWeight,
            vw: newVolumetricWeight,
            sku: String(offerId),
            topCategoryId: selectedProductDetails?.topCategoryId || '',
            secondCategoryId: selectedProductDetails?.secondCategoryId || '',
            thirdCategoryId: selectedProductDetails?.thirdCategoryId || ''
          }));

          const batchResponse = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: batchItems })
          });

          if (batchResponse.ok) {
            const batchResult = await batchResponse.json();
            const results = batchResult?.data?.results || batchResult?.results || [];
            const priceMap = {};
            results.forEach((r, idx) => {
              const variant = variantsWithPrice[idx];
              if (variant && r?.success) {
                const vKey = variant?.id || variant?.skuId;
                priceMap[vKey] = {
                  importerrPrice: parseFloat(r.breakdown?.FBP || 0) || null,
                  importerrBreakdown: r.breakdown || null,
                };
              }
            });

            setSelectedProductVariants(prev => prev.map(v => {
              const vKey = v?.id || v?.skuId;
              const updated = priceMap[vKey];
              if (!updated) return v;
              return {
                ...v,
                importerrPrice: updated.importerrPrice,
                importerrBreakdown: updated.importerrBreakdown,
                chargeableWeight: newChargeableWeight,
                volumetricWeight: newVolumetricWeight,
              };
            }));
          }
        }
      } catch (priceErr) {
        console.warn('[handleApplyDimensionsToAll] Listing price recalculation failed:', priceErr);
      }

      setDimToast({ type: 'success', message: draftId ? `Dimensions saved to all ${count} variants.` : `Dimensions applied locally to all ${count} variants (product not yet in database).` });
      setTimeout(() => {
        setShowEditWeightDimensionsModal(false);
        setEditingVariantForWeightDimensions(null);
        setDimToast(null);
      }, 1400);
    } catch (err) {
      setDimToast({ type: 'error', message: 'Failed to apply dimensions to all variants. Please try again.' });
    } finally {
      setDimApplyingAll(false);
    }
  };

  const images = useMemo(() => {
    if (!data?.images?.length) return data?.mainImage ? [data.mainImage] : [];
    return data.images;
  }, [data]);

  const amazonUrl = `https://www.amazon.in/dp/${asinFromQuery}`;

  // Extract weight and dimensions from product details
  const getProductSpec = (key) => {
    const details = { ...data?.productDetails, ...data?.technicalDetails, ...data?.additionalInfo };
    for (const [k, v] of Object.entries(details)) {
      if (k.toLowerCase().includes(key.toLowerCase())) return v;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="apd-container apd-loading-container">
        <div className="apd-loading">
          <FaSpinner className="apd-spinner" />
          <p>Searching for product details...</p>
          <span className="apd-asin-badge">ASIN: {asinFromQuery}</span>
        </div>
      </div>
    );
  }

  if (!data && error) {
    return (
      <div className="apd-container apd-loading-container">
        <div className="apd-error">
          <h4>Unable to load product</h4>
          <p>{error}</p>
          <div className="apd-error-actions">
            <button className="apd-btn-reload" onClick={handleReload}>
              <FaSync size={12} /> Retry Scraping
            </button>
            <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="apd-btn-amazon">
              <FaExternalLinkAlt size={12} /> View on Amazon
            </a>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className={`apd-container ${showSimilar ? 'apd-with-sidebar' : ''}`}>
      {/* Background Mapping Indicator - Fixed Position */}
      {isMapping && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: isRemapMode ? '#b45309' : '#1e293b',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 9998,
          fontSize: '13px',
          fontWeight: 500
        }}>
          <FaSpinner className="apd-spinner" style={{ width: '16px', height: '16px' }} />
          <span>{isRemapMode ? 'Re-mapping order in background...' : 'Mapping ASIN in background...'}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="apd-main">
        {/* Header Row 1: Amazon + ASIN + Buttons */}
        <div className="apd-header">
          <div className="apd-header-left">
            {fromFailedOrders && (
              <button
                onClick={() => navigate('/admin/failed-orders')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '5px 8px',
                  border: '1px solid #d0d7de',
                  background: '#fff',
                  color: '#24292f',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '2px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f6f8fa';
                  e.currentTarget.style.borderColor = '#0969da';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#d0d7de';
                }}
                title="Back to Failed Orders"
              >
                <FaArrowLeft size={12} />
              </button>
            )}
            <span className="apd-amazon-badge"><i className="fab fa-amazon"></i> Amazon.in</span>
            <span className="apd-asin-badge">ASIN: {asinFromQuery}</span>
          </div>
          <div className="apd-header-right">
            <button className="apd-btn-reload" onClick={handleReload} title="Reload product data">
              <FaSync size={12} /> Reload
            </button>
            <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="apd-btn-amazon">
              <FaExternalLinkAlt size={12} /> View on Amazon
            </a>
          </div>
        </div>

        {/* Header Row 2: Re-map Info Banner (only in remap mode) */}
        {isRemapMode && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 14px',
            marginBottom: '12px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            flexWrap: 'wrap'
          }}>
            {/* Re-map Mode Badge - Static indicator, not clickable */}
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#fff',
              borderRadius: '5px',
              boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
              cursor: 'default',
              userSelect: 'none'
            }}>
              <i className="fas fa-sync-alt" style={{ fontSize: '11px' }}></i>
              Re-map Mode
            </span>

            {/* Order ID */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#92400e', fontWeight: 500 }}>Order:</span>
              <span style={{
                padding: '4px 10px',
                fontSize: '12px',
                fontWeight: 700,
                background: '#fff',
                color: '#b45309',
                borderRadius: '4px',
                border: '1px solid #f59e0b',
                fontFamily: 'monospace'
              }}>
                {remapUpsaleoOrderId || remapOrderId.slice(-8)}
              </span>
            </div>

            {/* Order Value */}
            {remapOrderValue > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#166534', fontWeight: 500 }}>Value:</span>
                <span style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 700,
                  background: '#dcfce7',
                  color: '#166534',
                  borderRadius: '4px',
                  border: '1px solid #86efac'
                }}>
                  ₹{remapOrderValue.toLocaleString('en-IN')}
                </span>
              </div>
            )}

            {/* Old FBP */}
            {oldMappedPrice > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#991b1b', fontWeight: 500 }}>Old FBP:</span>
                <span style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 700,
                  background: '#fee2e2',
                  color: '#991b1b',
                  borderRadius: '4px',
                  border: '1px solid #fca5a5'
                }}>
                  ₹{oldMappedPrice.toFixed(0)}
                </span>
              </div>
            )}

            {/* Affected Orders Count */}
            {failedOrderAmount && failedOrderAmount.orderCount > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginLeft: 'auto',
                padding: '4px 10px',
                background: '#dbeafe',
                borderRadius: '4px',
                border: '1px solid #93c5fd'
              }}>
                <i className="fas fa-layer-group" style={{ fontSize: '11px', color: '#1e40af' }}></i>
                <span style={{ fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>
                  +{failedOrderAmount.orderCount - 1} more order{failedOrderAmount.orderCount > 2 ? 's' : ''} will be updated
                </span>
              </div>
            )}
          </div>
        )}

        {/* Product Layout - Vertical Stack */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%'
        }}>
          {/* Images Section - Main Image + Thumbnails */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            width: '100%'
          }}>
            <div className="apd-main-image">
              {images.length > 0 ? (
                <>
                  <img src={images[activeImageIdx] || images[0]} alt={data?.title || "Product"} onError={(e) => { e.currentTarget.src = noImage; }} />
                  <button className="apd-find-similar-icon" onClick={() => handleFindSimilar(1)} disabled={loadingSimilar} title="Find Similar Products">
                    {loadingSimilar ? <FaSpinner className="apd-spinner" /> : <TbZoomScan size={20} />}
                  </button>
                </>
              ) : (
                <div className="apd-no-image">No Image</div>
              )}
            </div>
            {images.length > 1 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                width: '100%'
              }}>
                {images.slice(0, 8).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Thumb ${idx + 1}`}
                    style={{
                      width: '48px',
                      height: '48px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      border: idx === activeImageIdx ? '2px solid #3b82f6' : '2px solid transparent',
                      cursor: 'pointer',
                      boxShadow: idx === activeImageIdx ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none'
                    }}
                    onClick={() => setActiveImageIdx(idx)}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product Info - Below Thumbnails */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}>
            <h1 style={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#1e293b',
              lineHeight: 1.5,
              margin: 0
            }}>{data?.title || "Product title not available"}</h1>

            {data?.brand && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#0ea5e9',
                fontSize: '13px'
              }}>
                <FaStore size={12} /> <span style={{ fontWeight: 600 }}>{data.brand}</span> Store
              </div>
            )}

            {/* Rating */}
            {data?.rating && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px'
              }}>
                <FaStar style={{ color: '#f59e0b', fontSize: '14px' }} />
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{data.rating}</span>
                {data.ratingCount && <span style={{ color: '#64748b' }}>({data.ratingCount})</span>}
              </div>
            )}

            {/* Price Section */}
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              border: '1px solid #bbf7d0',
              borderRadius: '10px',
              padding: '12px'
            }}>
              {data?.discount && !data.discount.includes("{") && (
                <span style={{
                  background: '#dc2626',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  marginRight: '8px'
                }}>{data.discount}</span>
              )}
              <div style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#059669',
                display: 'inline'
              }}>{data?.price || "Price not available"}</div>
              {data?.mrp && data.mrp !== data.price && (
                <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>M.R.P.: <s>{data.mrp}</s></div>
              )}
              <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>Inclusive of all taxes</div>
            </div>

            {/* Specs Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px'
            }}>
              <div className="apd-spec-item">
                <FaBox className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">SKU/Model</span>
                  <span className="apd-spec-value">{getProductSpec('model') || getProductSpec('sku') || asinFromQuery}</span>
                </div>
              </div>
              <div className="apd-spec-item">
                <FaWeight className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">Weight</span>
                  <span className="apd-spec-value">{getProductSpec('weight') || 'N/A'}</span>
                </div>
              </div>
              <div className="apd-spec-item">
                <FaRuler className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">Dimensions</span>
                  <span className="apd-spec-value">{getProductSpec('dimension') || getProductSpec('size') || 'N/A'}</span>
                </div>
              </div>
              <div className="apd-spec-item">
                <FaStore className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">Seller</span>
                  <span className="apd-spec-value">{data?.seller || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Seller Info */}
            {data?.seller && (
              <div className="apd-seller-box">
                <span className="apd-seller-label">Sold by:</span>
                <span className="apd-seller-name">{data.seller}</span>
                {data?.sellerRating && <span className="apd-seller-rating">{data.sellerRating}</span>}
                {data?.fulfilledBy && <span className="apd-fulfilled">{data.fulfilledBy}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Tabs Section */}
        <div className="apd-tabs-section">
          <div className="apd-tabs">
            <button className={activeTab === "description" ? "active" : ""} onClick={() => setActiveTab("description")}>Description</button>
            <button className={activeTab === "details" ? "active" : ""} onClick={() => setActiveTab("details")}>Product Details</button>
            <button className={activeTab === "technical" ? "active" : ""} onClick={() => setActiveTab("technical")}>Technical Details</button>
            <button className={activeTab === "reviews" ? "active" : ""} onClick={() => setActiveTab("reviews")}>Reviews</button>
          </div>

          <div className="apd-tab-content">
            {activeTab === "description" && (
              <div className="apd-description">
                {data?.features?.length > 0 ? (
                  <ul className="apd-features-list">
                    {data.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                ) : data?.description ? (
                  <div className="apd-desc-content">
                    {data.description.split(/[.!?]+/).filter(s => s.trim()).map((sentence, i) => (
                      <p key={i}>{sentence.trim()}.</p>
                    ))}
                  </div>
                ) : <p className="apd-no-data">No description available</p>}
              </div>
            )}

            {activeTab === "details" && (
              <div className="apd-details-table">
                {data?.productDetails && Object.keys(data.productDetails).length > 0 ? (
                  <table><tbody>
                    {Object.entries(data.productDetails).map(([k, v], i) => (
                      <tr key={i}><th>{k}</th><td>{v}</td></tr>
                    ))}
                  </tbody></table>
                ) : <p className="apd-no-data">No product details available</p>}
              </div>
            )}

            {activeTab === "technical" && (
              <div className="apd-details-table">
                {data?.technicalDetails && Object.keys(data.technicalDetails).length > 0 ? (
                  <table><tbody>
                    {Object.entries(data.technicalDetails).map(([k, v], i) => (
                      <tr key={i}><th>{k}</th><td>{v}</td></tr>
                    ))}
                  </tbody></table>
                ) : <p className="apd-no-data">No technical details available</p>}
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="apd-reviews">
                {data?.topReviews?.length > 0 ? (
                  data.topReviews.map((review, idx) => (
                    <div key={idx} className="apd-review-item">
                      <div className="apd-review-header">
                        <span className="apd-review-author">{review.author}</span>
                        {review.verified && <span className="apd-verified">Verified</span>}
                        <span className="apd-review-rating">{review.rating}</span>
                      </div>
                      {review.title && <div className="apd-review-title">{review.title}</div>}
                      <p className="apd-review-text">{review.text}</p>
                    </div>
                  ))
                ) : <p className="apd-no-data">No reviews available</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Similar Products Sidebar OR Selected Product Details */}
      {(showSimilar || selectedProductDetails) && (
        <div className="apd-sidebar">
          {selectedProductDetails ? (
            <div className="alibaba-detail-container">
              {/* Header with Back Button - 1688 Style */}
              <div className="alibaba-detail-header">
                <button className="alibaba-back-btn" onClick={backToSimilarProducts}>
                  ← Back to Similar Products
                </button>
                <button className="apd-sidebar-close" onClick={backToSimilarProducts}>×</button>
              </div>

              {/* Algorithm Selector - Pill Style with Custom Price Input */}
              <div className="alibaba-algo-section" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1', minWidth: '200px' }}>
                  <div className="alibaba-algo-label" style={{ marginBottom: 0 }}>Pricing Algorithm</div>
                  <div className="alibaba-algo-pills">
                    {loadingAlgos ? (
                      <span style={{ fontSize: '12px', color: '#64748b' }}>Loading...</span>
                    ) : pricingAlgorithms.length === 0 ? (
                      <span style={{ fontSize: '11px', color: '#dc2626', backgroundColor: '#fef2f2', padding: '6px 12px', borderRadius: '20px' }}>
                        ⚠️ No "actual" algorithm found
                      </span>
                    ) : (
                      pricingAlgorithms.map(algo => (
                        <button
                          key={algo._id}
                          className={`alibaba-algo-pill ${selectedAlgoId === algo._id ? 'active' : ''}`}
                          onClick={() => handleAlgoChange(algo._id)}
                        >
                          {algo.name}
                        </button>
                      ))
                    )}
                  </div>
                </div> */}
                {/* Marketplace Deduction Input */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
                    Marketplace Deduction:
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={marketplaceDeduction}
                      onChange={(e) => {
                        const value = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                        setMarketplaceDeduction(value);
                      }}
                      style={{
                        width: '60px',
                        padding: '6px 8px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1e293b',
                        textAlign: 'center',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                    />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>%</span>
                  </div>
                </div>
                {/* Custom Price Button - In Top Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                  <button
                    onClick={async () => {
                      // Open modal immediately
                      setShowCustomPriceModal(true);
                      setLoadingFailedOrders(true);
                      setFailedOrdersWithSellers([]);

                      // Fetch failed orders with seller info
                      if (asinFromQuery && !isRemapMode) {
                        try {
                          const result = await adminApiService.post('/orders/failed-orders-by-asin', {
                            asin: asinFromQuery
                          });
                          if (result.success && result.data) {
                            setFailedOrdersWithSellers(result.data);
                          } else {
                            setFailedOrdersWithSellers([]);
                          }
                        } catch (err) {
                          console.error('Failed to fetch orders:', err);
                          setFailedOrdersWithSellers([]);
                        } finally {
                          setLoadingFailedOrders(false);
                        }
                      } else {
                        setFailedOrdersWithSellers([]);
                        setLoadingFailedOrders(false);
                      }
                    }}
                    style={{
                      padding: '6px 14px',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#3b82f6',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#eff6ff';
                      e.target.style.borderColor = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#fff';
                      e.target.style.borderColor = '#3b82f6';
                    }}
                  >
                    Update Orders Prices
                  </button>
                </div>
              </div>

              {loadingSelectedProduct ? (
                <div className="apd-sidebar-loading">
                  <FaSpinner className="apd-spinner" />
                  <p>Loading product details...</p>
                </div>
              ) : (
                <>
                  {/* Failed Order Demand - TOP with 4 Cards */}
                  {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                    <div style={{ padding: '8px 12px', background: '#fef3c7', borderBottom: '1px solid #fcd34d' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e', marginRight: '8px' }}>{isRemapMode ? 'Order Value' : `Failed Order Demand for ASIN: ${asinFromQuery}`}</span>
                        {/* 4 Cards - Compact Size */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', flex: 1, minWidth: '400px' }}>
                          <div style={{ background: 'rgba(255, 255, 255, 0.7)', padding: '6px 8px', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '8px', color: '#78350f', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px', fontWeight: 600 }}>Total Value</div>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#92400e' }}>₹{failedOrderAmount.totalAmount.toLocaleString('en-IN')}</div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.7)', padding: '6px 8px', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '8px', color: '#78350f', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px', fontWeight: 600 }}>Orders</div>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#92400e' }}>{failedOrderAmount.orderCount}</div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.7)', padding: '6px 8px', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '8px', color: '#78350f', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px', fontWeight: 600 }}>Avg Order Value</div>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#92400e' }}>₹{(failedOrderAmount.totalAmount / failedOrderAmount.orderCount).toFixed(0)}</div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.7)', padding: '6px 8px', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '8px', color: '#78350f', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px', fontWeight: 600 }}>Order Quantity</div>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#92400e' }}>{failedOrderAmount.totalQuantity || 0}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Title */}
                  {/* <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', lineHeight: 1.4 }}>
                      {selectedProductDetails.subjectTrans || selectedProductDetails.subject || selectedProductDetails._selectedInfo?.title || 'Product'}
                    </div>
                  </div> */}

                  {/* Main Layout: Image+Info Left | Price Margin Right */}
                  <div style={{ display: 'flex', gap: '12px', padding: '10px 12px', background: '#fff' }}>
                    {/* LEFT: Main Image + Thumbnails + Info Grid */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, width: '350px' }}>
                      {/* Main Image */}
                      <div style={{ width: '350px', height: '210px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        {(() => {
                          const productImages = selectedProductDetails.productImage?.images || selectedProductDetails.images || selectedProductDetails.imageList || selectedProductDetails.productInfo?.images || [];
                          const selectedVariant = selectedProductVariants[selectedVariantImageIdx];
                          const mainImg = selectedVariant?.image || productImages[0] || selectedProductDetails._selectedInfo?.image || noImage;
                          return (
                            <>
                              <img
                                src={mainImg}
                                alt="Product"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer' }}
                                onError={(e) => { e.currentTarget.src = noImage; }}
                                onClick={() => {
                                  setZoomedImageUrl(mainImg);
                                  setShowImageZoom(true);
                                }}
                              />
                              {/* Zoom Icon */}
                              <div
                                onClick={() => {
                                  setZoomedImageUrl(mainImg);
                                  setShowImageZoom(true);
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  background: 'rgba(0, 0, 0, 0.6)',
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  zIndex: 10,
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
                                title="Click to zoom"
                              >
                                <TbZoomScan style={{ color: '#fff', fontSize: '18px' }} />
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      {/* Thumbnails */}
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {(() => {
                          const variantImages = selectedProductVariants.map(v => v.image).filter((img, idx, arr) => img && arr.indexOf(img) === idx);
                          const productImages = selectedProductDetails.productImage?.images || selectedProductDetails.images || [];
                          const thumbnailImages = variantImages.length > 0 ? variantImages : productImages;
                          return thumbnailImages.slice(0, 8).map((img, idx) => {
                            const variantIdx = selectedProductVariants.findIndex(v => v.image === img);
                            const isActive = variantIdx >= 0 ? variantIdx === selectedVariantImageIdx : idx === 0;
                            return <img key={idx} src={img} alt="" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: isActive ? '2px solid #3b82f6' : '1px solid #e5e7eb', cursor: 'pointer' }} onClick={() => variantIdx >= 0 ? setSelectedVariantImageIdx(variantIdx) : setSelectedVariantImageIdx(0)} onError={(e) => { e.currentTarget.src = noImage; }} />;
                          });
                        })()}
                      </div>

                      {/* Length, Width, Height Cards - Below Thumbnails */}
                      {(() => {
                        const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];
                        if (!currentVariant) {
                          // Show cards with "Not Available" if no variant
                          return (
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, 1fr)',
                              gap: '6px',
                              marginTop: '8px'
                            }}>
                              <div style={{
                                background: '#fee2e2',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                textAlign: 'center'
                              }}>
                                <div style={{ color: '#991b1b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>Length</div>
                                <div style={{ fontWeight: 600, color: '#dc2626', fontSize: '11px' }}>Not Available</div>
                              </div>
                              <div style={{
                                background: '#fee2e2',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                textAlign: 'center'
                              }}>
                                <div style={{ color: '#991b1b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>Width</div>
                                <div style={{ fontWeight: 600, color: '#dc2626', fontSize: '11px' }}>Not Available</div>
                              </div>
                              <div style={{
                                background: '#fee2e2',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                textAlign: 'center'
                              }}>
                                <div style={{ color: '#991b1b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>Height</div>
                                <div style={{ fontWeight: 600, color: '#dc2626', fontSize: '11px' }}>Not Available</div>
                              </div>
                            </div>
                          );
                        }

                        // Get dimensions ONLY from selected variant (no fallback)
                        const dimensions = currentVariant?.dimensions || {};

                        // Check if dimensions exist in the selected variant
                        const hasLength = dimensions.length !== undefined && dimensions.length !== null && parseFloat(dimensions.length) > 0;
                        const hasWidth = dimensions.width !== undefined && dimensions.width !== null && parseFloat(dimensions.width) > 0;
                        const hasHeight = dimensions.height !== undefined && dimensions.height !== null && parseFloat(dimensions.height) > 0;

                        // Parse dimensions - convert from mm to cm if needed
                        let length = hasLength ? parseFloat(dimensions.length) : null;
                        let width = hasWidth ? parseFloat(dimensions.width) : null;
                        let height = hasHeight ? parseFloat(dimensions.height) : null;

                        // If dimensions are > 100, they're likely in mm, convert to cm
                        if (length !== null && length > 100) {
                          length = length / 10;
                        }
                        if (width !== null && width > 100) {
                          width = width / 10;
                        }
                        if (height !== null && height > 100) {
                          height = height / 10;
                        }

                        // Get edited values if they exist
                        const variantKey = currentVariant?.id || currentVariant?.skuId || `variant-${selectedVariantImageIdx}`;
                        const editedData = editedVariantData[variantKey] || {};
                        const displayLength = editedData.length !== undefined ? editedData.length : length;
                        const displayWidth = editedData.width !== undefined ? editedData.width : width;
                        const displayHeight = editedData.height !== undefined ? editedData.height : height;

                        return (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '6px',
                            marginTop: '8px'
                          }}>
                            <div style={{
                              background: '#fee2e2',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              textAlign: 'center',
                              position: 'relative'
                            }}>
                              <div style={{ color: '#991b1b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>Length</div>
                              <div style={{ fontWeight: 600, color: '#dc2626', fontSize: displayLength !== null ? '12px' : '11px' }}>
                                {displayLength !== null ? `${displayLength}cm` : 'Not Available'}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDimensionsModal(currentVariant);
                                }}
                                disabled={!showGetFinalPriceButton}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: !showGetFinalPriceButton ? 'not-allowed' : 'pointer',
                                  padding: '2px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: !showGetFinalPriceButton ? '#93c5fd' : '#3b82f6',
                                  fontSize: '10px',
                                  opacity: !showGetFinalPriceButton ? 0.4 : 1
                                }}
                                title={!showGetFinalPriceButton ? 'Available after loading product variants' : 'Edit dimensions and weight'}
                              >
                                <FaEdit />
                              </button>
                            </div>
                            <div style={{
                              background: '#fee2e2',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              textAlign: 'center',
                              position: 'relative'
                            }}>
                              <div style={{ color: '#991b1b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>Width</div>
                              <div style={{ fontWeight: 600, color: '#dc2626', fontSize: displayWidth !== null ? '12px' : '11px' }}>
                                {displayWidth !== null ? `${displayWidth}cm` : 'Not Available'}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDimensionsModal(currentVariant);
                                }}
                                disabled={!showGetFinalPriceButton}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: !showGetFinalPriceButton ? 'not-allowed' : 'pointer',
                                  padding: '2px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: !showGetFinalPriceButton ? '#93c5fd' : '#3b82f6',
                                  fontSize: '10px',
                                  opacity: !showGetFinalPriceButton ? 0.4 : 1
                                }}
                                title={!showGetFinalPriceButton ? 'Available after loading product variants' : 'Edit dimensions and weight'}
                              >
                                <FaEdit />
                              </button>
                            </div>
                            <div style={{
                              background: '#fee2e2',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              textAlign: 'center',
                              position: 'relative'
                            }}>
                              <div style={{ color: '#991b1b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>Height</div>
                              <div style={{ fontWeight: 600, color: '#dc2626', fontSize: displayHeight !== null ? '12px' : '11px' }}>
                                {displayHeight !== null ? `${displayHeight}cm` : 'Not Available'}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDimensionsModal(currentVariant);
                                }}
                                disabled={!showGetFinalPriceButton}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: !showGetFinalPriceButton ? 'not-allowed' : 'pointer',
                                  padding: '2px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: !showGetFinalPriceButton ? '#93c5fd' : '#3b82f6',
                                  fontSize: '10px',
                                  opacity: !showGetFinalPriceButton ? 0.4 : 1
                                }}
                                title={!showGetFinalPriceButton ? 'Available after loading product variants' : 'Edit dimensions and weight'}
                              >
                                <FaEdit />
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Final Price Cards - Red Marked Area */}
                      {/* {(() => {
                        const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];
                        if (!currentVariant) return null;

                        const variantKey = currentVariant?.id || currentVariant?.skuId || `variant-${selectedVariantImageIdx}`;
                        const editedData = editedVariantData[variantKey] || {};

                        // Show cards if finalPrice exists (from edited data or variant)
                        const finalPrice = editedData.finalPrice !== undefined ? editedData.finalPrice : (currentVariant?.finalPrice || null);
                        const wholesaleFinalPrice = editedData.wholesaleFinalPrice !== undefined ? editedData.wholesaleFinalPrice : (currentVariant?.wholesaleFinalPrice || null);

                        // Show cards if finalPrice exists or if dimensions/weight were edited
                        const hasEditedDimensions = editedData.weight !== undefined || editedData.length !== undefined || editedData.width !== undefined || editedData.height !== undefined;
                        const shouldShowCards = (finalPrice !== null && finalPrice > 0) || (wholesaleFinalPrice !== null && wholesaleFinalPrice > 0) || hasEditedDimensions;

                        if (!shouldShowCards) return null;

                        return (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '8px',
                            marginTop: '4px'
                          }}>
                            <div style={{
                              background: '#f0fdf4',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: '1px solid #22c55e',
                              textAlign: 'center',
                              minHeight: '36px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center'
                            }}>
                              <div style={{ color: '#166534', fontSize: '10px', marginBottom: '1px', fontWeight: 600 }}>Final Price</div>
                              <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '14px' }}>
                                {finalPrice !== null && finalPrice > 0 ? `₹${finalPrice.toFixed(0)}` : '---'}
                              </div>
                            </div>

                            <div style={{
                              background: '#eff6ff',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: '1px solid #3b82f6',
                              textAlign: 'center',
                              minHeight: '36px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center'
                            }}>
                              <div style={{ color: '#1e40af', fontSize: '10px', marginBottom: '1px', fontWeight: 600 }}>
                                WS Final Price
                              </div>
                              <div style={{ fontWeight: 700, color: '#2563eb', fontSize: '14px' }}>
                                {wholesaleFinalPrice !== null && wholesaleFinalPrice > 0 ? `₹${wholesaleFinalPrice.toFixed(0)}` : '---'}
                              </div>
                            </div>
                          </div>
                        );
                      })()} */}
                    </div>

                    {/* RIGHT: Price + Price Margin Analysis */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
                      <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
                        <div
                          style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', lineHeight: 1.4 }}
                          title={selectedProductDetails.subjectTrans || selectedProductDetails.subject || selectedProductDetails._selectedInfo?.title || 'Product'}
                        >
                          {(() => {
                            const fullTitle = selectedProductDetails.subjectTrans || selectedProductDetails.subject || selectedProductDetails._selectedInfo?.title || 'Product';
                            return fullTitle.length > 75 ? fullTitle.substring(0, 75) + '...' : fullTitle;
                          })()}
                        </div>
                      </div>
                      {/* Price */}
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                        {(() => {
                          // Get current selected variant's RMB price
                          const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];
                          const variantRmbPrice = currentVariant?.price?.rmb ||
                            currentVariant?.ap ||
                            currentVariant?.price?.ap ||
                            selectedProductDetails.productSaleInfo?.priceRangeOriginal ||
                            selectedProductDetails.productSaleInfo?.priceRange ||
                            selectedProductDetails.priceInfo?.price ||
                            selectedProductDetails._selectedInfo?.rmb ||
                            0;

                          // Compute priceToUse (same logic as Price Margin Analysis)
                          const isResellerTab = pricingTab === "Reseller";
                          const variantKey = currentVariant?.id || currentVariant?.skuId || `variant-${selectedVariantImageIdx}`;
                          const editedData = editedVariantData[variantKey] || {};
                          const editedFinalPrice = editedData.finalPrice !== undefined && editedData.finalPrice > 0 ? editedData.finalPrice : null;
                          const importerrPrice = currentVariant?.importerrPrice || 0;
                          const currentVariantPrice = currentVariant?.price?.inr || 0;
                          const priceToUse = isResellerTab
                            ? (editedFinalPrice || (importerrPrice > 0 ? importerrPrice : currentVariantPrice))
                            : (importerrPrice > 0 ? importerrPrice : currentVariantPrice);

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '28px', fontWeight: 700, color: '#ea580c' }}>
                                ₹{priceToUse?.toFixed(0) || 0}
                              </span>

                              {/* <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                ¥{variantRmbPrice?.toFixed(2) || 0}
                              </span> */}
                            </div>
                          );
                        })()}
                        <span style={{ fontSize: '13px', color: '#9a3412' }}>/{selectedProductDetails.productSaleInfo?.unit || 'piece'}</span>
                        {/* Price Calculator Button */}
                        <button
                          onClick={() => setShowPriceCalculatorModal(true)}
                          style={{
                            padding: '2px 14px',
                            border: '1px solid #3b82f6',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#3b82f6',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#eff6ff';
                            e.target.style.borderColor = '#2563eb';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.borderColor = '#3b82f6';
                          }}
                        >
                          Price Calculator
                        </button>
                        {/* Get Final Price Button */}
                        <button
                          onClick={handleGetFinalPrice}
                          disabled={!showGetFinalPriceButton || loadingFinalPrice}
                          style={{
                            padding: '2px 14px',
                            border: `1px solid ${!showGetFinalPriceButton ? '#d1d5db' : '#10b981'}`,
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: !showGetFinalPriceButton ? '#9ca3af' : loadingFinalPrice ? '#6ee7b7' : '#10b981',
                            backgroundColor: '#fff',
                            cursor: !showGetFinalPriceButton || loadingFinalPrice ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap',
                            marginLeft: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            opacity: !showGetFinalPriceButton ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (showGetFinalPriceButton && !loadingFinalPrice) {
                              e.currentTarget.style.backgroundColor = '#f0fdf4';
                              e.currentTarget.style.borderColor = '#059669';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.borderColor = !showGetFinalPriceButton ? '#d1d5db' : '#10b981';
                          }}
                          title={!showGetFinalPriceButton ? 'Available after loading product variants' : 'Fetch final selling price from pricing API'}
                        >
                          {loadingFinalPrice && <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '10px' }} />}
                          {loadingFinalPrice ? 'Fetching...' : 'Get Final Price'}
                        </button>
                        <span style={{ fontSize: '12px', color: '#78350f', marginLeft: 'auto', fontWeight: 600 }}>MOQ: {selectedProductDetails.productSaleInfo?.minOrderQuantity || 1}</span>
                      </div>
                      {/* Price Margin Analysis */}
                      <div style={{ padding: '12px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: '8px', border: '1px solid #86efac' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#166534', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <span>📊</span>
                            <span>Price Margin Analysis</span>
                            {/* Tabs: BTPL and Reseller/Others */}
                            {/* <div style={{ display: 'flex', gap: '6px', marginLeft: '8px' }}>
                              <button
                                onClick={() => setPricingTab("BTPL")}
                                style={{
                                  padding: '1px 10px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  borderRadius: '4px',
                                  border: '1px solid #16a34a',
                                  backgroundColor: pricingTab === "BTPL" ? '#16a34a' : '#f0fdf4',
                                  color: pricingTab === "BTPL" ? '#fff' : '#166534',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                              >
                                BTPL
                              </button>
                              <button
                                onClick={() => setPricingTab("Reseller")}
                                style={{
                                  padding: '1px 10px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  borderRadius: '4px',
                                  border: '1px solid #16a34a',
                                  backgroundColor: pricingTab === "Reseller" ? '#16a34a' : '#f0fdf4',
                                  color: pricingTab === "Reseller" ? '#fff' : '#166534',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                              >
                                Reseller/Others
                              </button>
                            </div> */}
                          </div>
                          {/* Info Icon */}
                          {/* <div
                            data-breakdown-icon
                            style={{
                              position: 'relative',
                              minWidth: '16px',
                              minHeight: '16px',
                              width: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#60a5fa',
                              border: '1px solid #3b82f6',
                              borderRadius: '50%',
                              cursor: 'pointer',
                              flexShrink: 0,
                              zIndex: 0
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setBreakdownTooltipPosition({
                                top: rect.top,
                                left: rect.left,
                                width: rect.width
                              });
                              setShowBreakdown(true);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FaInfoCircle style={{ color: '#ffffff', fontSize: '9px', lineHeight: 1, pointerEvents: 'none' }} />
                          </div> */}
                        </div>
                        {(() => {
                          const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];
                          const currentVariantPrice = currentVariant?.price?.inr || 0;
                          const currentRmbPrice = currentVariant?.price?.rmb || 0;
                          const breakdown = currentVariant?.price?.breakdown || {};
                          const hasFailedOrders = failedOrderAmount && failedOrderAmount.totalAmount > 0;
                          const orderCount = hasFailedOrders ? failedOrderAmount.orderCount : 0;
                          const avgOrderValue = hasFailedOrders ? failedOrderAmount.totalAmount / orderCount : 0;
                          // Apply marketplace deduction to order value
                          // Fallback to listing price (priceToUse) when no failed order data is available
                          const baseOrderValue = hasFailedOrders ? avgOrderValue : currentVariantPrice;
                          const adjustedOrderValue = baseOrderValue * (1 - marketplaceDeduction / 100);
                          // hasOrderContext: true whenever a variant is loaded (we always have a listing price)
                          const hasOrderContext = hasFailedOrders || currentVariantPrice > 0;

                          // For Reseller tab, use finalPrice (edited) or importerrPrice; for BTPL, use importerrPrice (source price)
                          const isResellerTab = pricingTab === "Reseller";
                          const variantKey = currentVariant?.id || currentVariant?.skuId || `variant-${selectedVariantImageIdx}`;
                          const editedData = editedVariantData[variantKey] || {};
                          const editedFinalPrice = editedData.finalPrice !== undefined && editedData.finalPrice > 0 ? editedData.finalPrice : null;
                          const importerrPrice = currentVariant?.importerrPrice || 0;
                          const importerrBreakdown = currentVariant?.importerrBreakdown || {};
                          const tpPrice = importerrBreakdown?.TP || importerrBreakdown?.tp || 0;
                          // BTPL: Always use importerrPrice (source price), Reseller: Use edited finalPrice if available, otherwise importerrPrice
                          const priceToUse = isResellerTab
                            ? (editedFinalPrice || (importerrPrice > 0 ? importerrPrice : currentVariantPrice))
                            : (importerrPrice > 0 ? importerrPrice : currentVariantPrice);

                          // Calculate margin: adjusted order value - source price (or importerr price for Reseller)
                          // If finalPriceFromApi is available, use it as the cost basis for margin calculation
                          const marginPerOrder = adjustedOrderValue - (finalPriceFromApi > 0 ? finalPriceFromApi : priceToUse);
                          const isPositive = marginPerOrder > 0;
                          // Old price comparison for remap mode
                          const newMargin = adjustedOrderValue - priceToUse;
                          const marginDiff = newMargin - oldMargin;
                          const isBetterMargin = marginDiff > 0;
                          return (
                            <>
                              {/* Remap Mode: Old vs New Price Comparison */}
                              {isRemapMode && oldMappedPrice > 0 && (
                                <div style={{ marginBottom: '10px', padding: '10px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>🔄 Price Comparison (Old → New)</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                                    <div style={{ textAlign: 'center', padding: '8px', background: '#fee2e2', borderRadius: '6px' }}>
                                      <div style={{ fontSize: '9px', color: '#991b1b', marginBottom: '2px' }}>OLD FBP</div>
                                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#dc2626' }}>₹{oldMappedPrice.toFixed(0)}</div>
                                      <div style={{ fontSize: '9px', color: '#991b1b' }}>Margin: ₹{oldMargin.toFixed(0)}</div>
                                    </div>
                                    <div style={{ textAlign: 'center', padding: '8px', background: '#dcfce7', borderRadius: '6px' }}>
                                      <div style={{ fontSize: '9px', color: '#166534', marginBottom: '2px' }}>NEW FBP</div>
                                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#16a34a' }}>₹{currentVariantPrice.toFixed(0)}</div>
                                      <div style={{ fontSize: '9px', color: '#166534' }}>Margin: ₹{newMargin.toFixed(0)}</div>
                                    </div>
                                    <div style={{ textAlign: 'center', padding: '8px', background: isBetterMargin ? '#f0fdf4' : '#fef2f2', borderRadius: '6px', border: `2px solid ${isBetterMargin ? '#22c55e' : '#ef4444'}` }}>
                                      <div style={{ fontSize: '9px', color: isBetterMargin ? '#166534' : '#991b1b', marginBottom: '2px' }}>SAVINGS</div>
                                      <div style={{ fontSize: '16px', fontWeight: 700, color: isBetterMargin ? '#16a34a' : '#dc2626' }}>
                                        {isBetterMargin ? '+' : ''}₹{(oldMappedPrice - currentVariantPrice).toFixed(0)}
                                      </div>
                                      <div style={{ fontSize: '9px', color: isBetterMargin ? '#166534' : '#991b1b' }}>
                                        {isBetterMargin ? '✓ Better' : '✗ Worse'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {/* Main 3 Cards */}
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '10px' }}>
                                <div style={{ textAlign: 'center', padding: '10px 8px', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                  <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', fontWeight: 500 }}>Approx MKP Settlement</div>
                                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#3b82f6' }}>{hasOrderContext ? `₹${adjustedOrderValue.toFixed(0)}` : '---'}</div>
                                  <div style={{ fontSize: '10px', color: '#166534', fontWeight: 600 }}>
                                    Order Value (-{marketplaceDeduction}%)
                                  </div>
                                </div>
                                {/* Final Price Card - shown after API response */}
                                {(finalPriceFromApi !== null && finalPriceFromApi > 0) ? (
                                  <>
                                    <div style={{ textAlign: 'center', padding: '10px 8px', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', position: 'relative' }}>
                                      <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                        Final Price
                                        {/* Info button - shows price breakdown */}
                                        <span
                                          title="View price breakdown"
                                          onClick={() => setShowPriceBreakdown(v => !v)}
                                          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', background: '#3b82f6', color: '#fff', fontSize: '9px', fontWeight: 700, lineHeight: 1, flexShrink: 0 }}
                                        >i</span>
                                        {/* Warning icon - shown when showWarning is true */}
                                        {finalPriceBreakdown?.showWarning && (
                                          <span
                                            title="Price calculated using default values (weight/dimensions not available)"
                                            style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', background: '#f59e0b', color: '#fff', fontSize: '9px', fontWeight: 700, lineHeight: 1, flexShrink: 0 }}
                                          >!</span>
                                        )}
                                      </div>
                                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                                        ₹{finalPriceFromApi.toFixed(2)}
                                      </div>
                                      {/* Price Breakdown Popover */}
                                      {showPriceBreakdown && finalPriceBreakdown && (
                                        <div
                                          onClick={e => e.stopPropagation()}
                                          style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 999, marginTop: '6px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '10px 12px', minWidth: '220px', textAlign: 'left' }}
                                        >
                                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#1e293b', marginBottom: '6px', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>Final Price Breakdown</div>
                                          {finalPriceBreakdown.showWarning && (
                                            <div style={{ fontSize: '10px', color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '4px', padding: '4px 6px', marginBottom: '6px' }}>
                                              ⚠ Calculated using default values (weight/dimensions unavailable)
                                            </div>
                                          )}
                                          {(() => {
                                            // Calculate discount: priceToUse - finalFBP (all raw numbers)
                                            const discount = finalPriceBreakdown.finalFBP != null
                                              ? priceToUse - Number(finalPriceBreakdown.finalFBP)
                                              : 0;

                                            // If discount is negative or zero, absorb it into procurement charges
                                            const adjustedProcurementCharges = discount <= 0
                                              ? Number(finalPriceBreakdown.procurementCharges) + Math.abs(discount)
                                              : Number(finalPriceBreakdown.procurementCharges);

                                            return [
                                              { label: 'Quantity', value: '1' },
                                              { label: 'Gross Total', value: priceToUse },
                                              { label: 'Procurement Charges', value: adjustedProcurementCharges },
                                              { label: 'Total Amount', value: priceToUse + adjustedProcurementCharges },
                                              // Only show discount row when it's a genuine positive discount
                                              ...(discount > 0 ? [{ label: 'Discount', value: discount }] : []),
                                            ].map(({ label, value }) => value != null && (
                                              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: label === 'Total Amount' ? '#0a2375ff' : label === 'Discount' ? '#16a34a' : '#475569', marginBottom: '3px', fontWeight: label === 'Total Amount' ? 700 : 'normal' }}>
                                                <span>{label}</span>
                                                <span style={{ fontWeight: label === 'Total Amount' ? 700 : 600, color: label === 'Total Amount' ? '#0a2375ff' : label === 'Discount' ? '#16a34a' : '#1e293b' }}>
                                                  {label === 'Discount' ? `-₹${Number(value).toFixed(2)}` : label === 'Quantity' ? `${Number(value)} Unit` : `₹${Number(value).toFixed(2)}`}
                                                </span>
                                              </div>
                                            ));
                                          })()}
                                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700, color: '#1e293b', borderTop: '1px solid #f1f5f9', paddingTop: '4px', marginTop: '4px' }}>
                                            <span>Final Price</span>
                                            <span>₹{finalPriceFromApi.toFixed(2)}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '10px 8px', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                    <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', fontWeight: 500 }}>
                                      Listing Price
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>₹{priceToUse.toFixed(0)}</div>
                                  </div>
                                )}

                                <div style={{ textAlign: 'center', padding: '10px 8px', background: hasOrderContext ? (isPositive ? '#f0fdf4' : '#fef2f2') : '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: hasOrderContext ? (isPositive ? '2px solid #22c55e' : '2px solid #ef4444') : 'none' }}>
                                  <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', fontWeight: 500 }}>{isRemapMode ? 'NEW PROFIT' : 'PROFIT'}</div>
                                  <div style={{ fontSize: '20px', fontWeight: 700, color: hasOrderContext ? (isPositive ? '#16a34a' : '#dc2626') : '#94a3b8' }}>
                                    {hasOrderContext ? `₹${marginPerOrder.toFixed(0)}` : '---'}
                                  </div>
                                  <span style={{ fontSize: '12px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                    ({adjustedOrderValue > 0 ? ((marginPerOrder / adjustedOrderValue) * 100).toFixed(0) : 0}%)
                                  </span>
                                </div>
                              </div>
                              {/* 2-Column Layout: Green Card (Left) + Red & Yellow Cards (Right) */}
                              {/* {hasFailedOrders && (
                                <div style={{
                                  marginTop: '8px',
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr',
                                  gap: '8px'
                                }}>
                                  <div style={{
                                    padding: '6px 8px',
                                    backgroundColor: '#dcfce7',
                                    borderRadius: '4px',
                                    border: '1px solid #bbf7d0'
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', paddingBottom: '4px', borderBottom: '1px dashed #bbf7d0' }}>
                                      <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontSize: '10px', color: '#166534', marginBottom: '1px', fontWeight: 600 }}>
                                          Order Value (-{marketplaceDeduction}%)
                                        </div>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#166534' }}>
                                          ₹{adjustedOrderValue.toFixed(0)}
                                        </div>
                                      </div>
                                      <div style={{ fontSize: '10px', color: '#16a34a' }}>→</div>
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '9px', color: '#166534', marginBottom: '1px', fontWeight: 600 }}>
                                          {isResellerTab ? 'IMPORTERR PRICE' : (isRemapMode ? 'NEW FBP' : 'SOURCE PRICE')}
                                        </div>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#16a34a' }}>
                                          ₹{priceToUse.toFixed(0)}
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px', gap: '6px' }}>
                                      <span style={{ fontSize: '10px', color: '#166534', fontWeight: 600 }}>Margin/Order</span>
                                      <span style={{ fontSize: '12px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                        ₹{marginPerOrder.toFixed(0)} ({adjustedOrderValue > 0 ? ((marginPerOrder / adjustedOrderValue) * 100).toFixed(0) : 0}%)
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ fontSize: '10px', color: '#166534', fontWeight: 600 }}>Total ({orderCount} orders)</span>
                                      <span style={{ fontSize: '12px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                        ₹{(marginPerOrder * orderCount).toLocaleString('en-IN')}
                                      </span>
                                    </div>
                                  </div>

                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {currentVariant?.importerrPrice > 0 && (
                                      <div style={{
                                        padding: '4px 6px',
                                        backgroundColor: '#fee2e2',
                                        borderRadius: '4px',
                                        border: '1px solid #fecaca',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1px'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                          <span style={{ fontSize: '11px', color: '#991b1b', fontWeight: 600 }}>
                                            Importerr Price
                                          </span>
                                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>
                                            ₹{currentVariant.importerrPrice.toFixed(0)}
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '8px', color: '#b91c1c', width: '100%', marginTop: '0px' }}>
                                          Calculated using Importerr algorithm (importerr.com)
                                        </div>
                                      </div>
                                    )}

                                    <div style={{
                                      padding: '4px 8px',
                                      backgroundColor: '#fef9c3',
                                      borderRadius: '4px',
                                      border: '1px solid #fde047'
                                    }}>
                                      <div style={{ fontSize: '9px', color: '#854d0e', fontWeight: 600, marginBottom: '0px' }}>Importerr Price</div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0px', gap: '1px' }}>
                                        <span style={{ fontSize: '9px', color: '#854d0e', fontWeight: 600 }}>Amazon Price:</span>
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#ca8a04' }}>
                                          ₹{(currentVariantPrice * 1.3).toFixed(0)}
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontSize: '9px', color: '#854d0e', fontWeight: 600 }}>Selling Price:</span>
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#ca8a04' }}>
                                          ₹{currentVariant?.importerrPrice > 0 ? (currentVariant.importerrPrice * 1.3).toFixed(0) : (currentVariantPrice * 1.3).toFixed(0)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )} */}
                              {/* Cost Breakdown with Actual Algo Formula Values - Hover Tooltip */}
                              {/* {showBreakdown && breakdown && Object.keys(breakdown).length > 0 && (
                                <div
                                  ref={breakdownTooltipRef}
                                  style={{
                                    position: 'fixed',
                                    top: `${breakdownTooltipPosition.top}px`,
                                    left: `${breakdownTooltipPosition.left - 420}px`,
                                    zIndex: 99999,
                                    maxWidth: '400px',
                                    width: '400px',
                                    maxHeight: 'calc(100vh - 20px)',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    pointerEvents: 'auto'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.stopPropagation();
                                    setShowBreakdown(true);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.stopPropagation();
                                    setShowBreakdown(false);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(() => {
                                    // Get selected algorithm's formula
                                    const selectedAlgo = pricingAlgorithms.find(a => a._id === selectedAlgoId);
                                    const formula = selectedAlgo?.formula || {};

                                    // Actual algo formula values
                                    const algoRmbRate = formula.rmb_to_inr_rate || selectedAlgo?.rmbRate || 14.5;
                                    const algoFreightPerKg = formula.international_freight_per_kg || 550;
                                    const algoLocalShipRmb = formula.local_shipping_rmb || 6;
                                    const algoCustomsDuty = (formula.customs_duty_default || 0.22) * 100;
                                    const algoCustomsSurcharge = (formula.customs_duty_surcharge || 0.1) * 100;
                                    const algoGstDefault = (formula.gst_default || 0.18) * 100;
                                    const algoMarkup = (formula.markup_percent || 0) * 100;
                                    const algoWeightBuffer = (formula.weight_buffer_percent || 0.05) * 100;
                                    const algoDefaultWeight = formula.default_chargeable_weight || 0.5;

                                    // Breakdown values from API
                                    const apRmb = breakdown.AP_RMB ? parseFloat(breakdown.AP_RMB) : 0;
                                    const apInr = breakdown.AP_INR ? parseFloat(breakdown.AP_INR) : 0;
                                    const ls = breakdown.LS !== undefined ? parseFloat(breakdown.LS) : 0;
                                    const intlFreight = breakdown.IF !== undefined ? parseFloat(breakdown.IF) : 0;
                                    const cp = breakdown.CP !== undefined ? parseFloat(breakdown.CP) : 0;
                                    const cd = breakdown.CD !== undefined ? parseFloat(breakdown.CD) : 0;
                                    const fbp = breakdown.FBP !== undefined ? parseFloat(breakdown.FBP) : 0;
                                    const weight = currentVariant?.weight || algoDefaultWeight;
                                    const chargeableWeight = weight * (1 + (formula.weight_buffer_percent || 0.05));

                                    return (
                                      <div style={{
                                        background: '#fff',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                                        position: 'relative',
                                        overflowX: 'hidden',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        maxWidth: '100%'
                                      }} onClick={(e) => e.stopPropagation()}>
                                        <div style={{
                                          position: 'absolute',
                                          right: '-8px',
                                          top: '20px',
                                          width: 0,
                                          height: 0,
                                          borderTop: '8px solid transparent',
                                          borderBottom: '8px solid transparent',
                                          borderLeft: '8px solid #e5e7eb'
                                        }}></div>
                                        <div style={{
                                          position: 'absolute',
                                          right: '-7px',
                                          top: '20px',
                                          width: 0,
                                          height: 0,
                                          borderTop: '8px solid transparent',
                                          borderBottom: '8px solid transparent',
                                          borderLeft: '8px solid #fff'
                                        }}></div>

                                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>💰 Landed Cost Breakdown</span>
                                            <span style={{ fontSize: '9px', color: '#3b82f6', background: '#eff6ff', padding: '2px 6px', borderRadius: '4px' }}>Algo: {selectedAlgo?.name || 'Actual'}</span>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowBreakdown(false);
                                            }}
                                            style={{
                                              background: 'transparent',
                                              border: 'none',
                                              fontSize: '18px',
                                              color: '#64748b',
                                              cursor: 'pointer',
                                              padding: '0',
                                              width: '24px',
                                              height: '24px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              borderRadius: '4px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                          >
                                            ×
                                          </button>
                                        </div>

                                        <div style={{ background: '#f0f9ff', borderRadius: '6px', padding: '6px 8px', marginBottom: '8px', border: '1px solid #bae6fd', fontSize: '9px' }}>
                                          <div style={{ fontWeight: 600, color: '#0369a1', marginBottom: '4px' }}>📐 Algorithm Parameters</div>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', color: '#0c4a6e' }}>
                                            <span>Rate: <b>₹{algoRmbRate}/¥</b></span>
                                            <span>Freight: <b>₹{algoFreightPerKg}/kg</b></span>
                                            <span>Local Ship: <b>¥{algoLocalShipRmb}</b></span>
                                            <span>Duty: <b>{algoCustomsDuty}%</b></span>
                                            <span>GST: <b>{algoGstDefault}%</b></span>
                                            <span>Wt Buffer: <b>{algoWeightBuffer}%</b></span>
                                          </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px', fontSize: '10px' }}>
                                          {apRmb > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#fff7ed', borderRadius: '4px', borderLeft: '3px solid #ea580c' }}>
                                              <div>
                                                <span style={{ color: '#9a3412', fontWeight: 600 }}>🏭 Alibaba Price (AP)</span>
                                                <span style={{ color: '#b45309', fontSize: '9px', marginLeft: '6px' }}>1688 product cost</span>
                                              </div>
                                              <span style={{ fontWeight: 700, color: '#ea580c', fontSize: '12px' }}>¥{apRmb.toFixed(2)}</span>
                                            </div>
                                          )}
                                          {apInr > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#fef3c7', borderRadius: '4px', borderLeft: '3px solid #f59e0b' }}>
                                              <div>
                                                <span style={{ color: '#92400e', fontWeight: 600 }}>💱 AP in INR</span>
                                                <span style={{ color: '#a16207', fontSize: '9px', marginLeft: '6px' }}>= ¥{apRmb.toFixed(2)} × ₹{algoRmbRate}</span>
                                              </div>
                                              <span style={{ fontWeight: 700, color: '#b45309', fontSize: '12px' }}>₹{apInr.toFixed(0)}</span>
                                            </div>
                                          )}
                                          {ls > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#f0f9ff', borderRadius: '4px', borderLeft: '3px solid #0ea5e9' }}>
                                              <div>
                                                <span style={{ color: '#0369a1', fontWeight: 600 }}>🚚 Local Shipping (LS)</span>
                                                <span style={{ color: '#0284c7', fontSize: '9px', marginLeft: '6px' }}>= ¥{algoLocalShipRmb} × ₹{algoRmbRate}</span>
                                              </div>
                                              <span style={{ fontWeight: 700, color: '#0c4a6e', fontSize: '12px' }}>₹{ls.toFixed(0)}</span>
                                            </div>
                                          )}
                                          {intlFreight > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#f0f9ff', borderRadius: '4px', borderLeft: '3px solid #0ea5e9' }}>
                                              <div>
                                                <span style={{ color: '#0369a1', fontWeight: 600 }}>✈️ Int'l Freight (IF)</span>
                                                <span style={{ color: '#0284c7', fontSize: '9px', marginLeft: '6px' }}>= {chargeableWeight.toFixed(2)}kg × ₹{algoFreightPerKg}/kg</span>
                                              </div>
                                              <span style={{ fontWeight: 700, color: '#0c4a6e', fontSize: '12px' }}>₹{intlFreight.toFixed(0)}</span>
                                            </div>
                                          )}
                                          {cp > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#faf5ff', borderRadius: '4px', borderLeft: '3px solid #8b5cf6' }}>
                                              <div>
                                                <span style={{ color: '#7c3aed', fontWeight: 600 }}>📋 Customs Processing (CP)</span>
                                                <span style={{ color: '#7c3aed', fontSize: '9px', marginLeft: '6px' }}>Clearance fee</span>
                                              </div>
                                              <span style={{ fontWeight: 700, color: '#6d28d9', fontSize: '12px' }}>₹{cp.toFixed(0)}</span>
                                            </div>
                                          )}
                                          {cd > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#faf5ff', borderRadius: '4px', borderLeft: '3px solid #8b5cf6' }}>
                                              <div>
                                                <span style={{ color: '#7c3aed', fontWeight: 600 }}>🏛️ Customs Duty (CD)</span>
                                                <span style={{ color: '#7c3aed', fontSize: '9px', marginLeft: '6px' }}>= (₹{apInr.toFixed(0)} + ₹{intlFreight.toFixed(0)}) × {algoCustomsDuty}%</span>
                                              </div>
                                              <span style={{ fontWeight: 700, color: '#6d28d9', fontSize: '12px' }}>₹{cd.toFixed(0)}</span>
                                            </div>
                                          )}
                                        </div>

                                        {fbp > 0 && (
                                          <div style={{ marginTop: '8px', padding: '10px 12px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: '6px', border: '2px solid #22c55e' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              <div>
                                                <span style={{ color: '#166534', fontWeight: 700, fontSize: '12px' }}>✅ Final Buying Price (FBP)</span>
                                                <div style={{ fontSize: '9px', color: '#15803d', marginTop: '2px', fontFamily: 'monospace' }}>
                                                  = ₹{apInr.toFixed(0)} + ₹{ls.toFixed(0)} + ₹{intlFreight.toFixed(0)} + ₹{cp.toFixed(0)} + ₹{cd.toFixed(0)}
                                                </div>
                                              </div>
                                              <span style={{ fontWeight: 700, color: '#16a34a', fontSize: '20px' }}>₹{fbp.toFixed(0)}</span>
                                            </div>
                                          </div>
                                        )}

                                      </div>
                                    );
                                  })()}
                                </div>
                              )} */}
                            </>
                          );
                        })()}
                      </div>

                      {/* Offer, Stock, WT, Seller Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '10px' }}>
                        <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>OFFER</div>
                          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '12px' }}>{selectedProductDetails._selectedInfo?.offerId || 'N/A'}</div>
                        </div>
                        <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>STOCK</div>
                          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '12px' }}>{selectedProductDetails.productSaleInfo?.amountOnSale || 'N/A'}</div>
                        </div>
                        <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', textAlign: 'center', position: 'relative' }}>
                          <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>WT</div>
                          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '12px' }}>
                            {(() => {
                              const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];
                              const variantKey = currentVariant?.id || currentVariant?.skuId || `variant-${selectedVariantImageIdx}`;
                              const editedData = editedVariantData[variantKey] || {};

                              // Use edited weight if available, otherwise use variant weight
                              let displayWeight = editedData.weight !== undefined ? editedData.weight : null;
                              if (displayWeight === null && currentVariant?.weight !== undefined && currentVariant.weight !== null) {
                                displayWeight = parseFloat(currentVariant.weight);
                              }
                              if (displayWeight === null) {
                                // Fallback to product shipping info
                                const productWeight = selectedProductDetails?.productShippingInfo?.skuShippingDetails?.[0]?.weight;
                                if (productWeight !== undefined && productWeight !== null) {
                                  displayWeight = parseFloat(productWeight);
                                }
                              }
                              return displayWeight !== null ? `${displayWeight.toFixed(3)}kg` : '0kg';
                            })()}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];
                              openDimensionsModal(currentVariant);
                            }}
                            disabled={!showGetFinalPriceButton}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: 'transparent',
                              border: 'none',
                              cursor: !showGetFinalPriceButton ? 'not-allowed' : 'pointer',
                              padding: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: !showGetFinalPriceButton ? '#93c5fd' : '#3b82f6',
                              fontSize: '10px',
                              opacity: !showGetFinalPriceButton ? 0.4 : 1
                            }}
                            title={!showGetFinalPriceButton ? 'Available after loading product variants' : 'Edit weight and dimensions'}
                          >
                            <FaEdit />
                          </button>
                        </div>
                        {/* <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', textAlign: 'center', overflow: 'hidden' }}>
                          <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '4px', fontWeight: 500 }}>SELLER</div>
                          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={selectedProductDetails.companyName || '1688'}>{selectedProductDetails.companyName || '1688'}</div>
                        </div> */}
                      </div>
                    </div>
                  </div>

                  {/* Available Variants Section */}
                  <div style={{ padding: '10px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Available Variants
                      <span style={{ fontSize: '10px', fontWeight: 500, color: '#fff', background: '#3b82f6', padding: '1px 6px', borderRadius: '8px' }}>{selectedProductVariants.length}</span>
                    </div>

                    {selectedProductVariants.length > 0 ? (
                      (() => {
                        // Group variants by first attribute (usually color)
                        const firstAttrName = selectedProductVariants[0]?.attributes?.[0]?.name || 'Color';
                        const groupedByFirstAttr = {};
                        const firstAttrImages = {};

                        selectedProductVariants.forEach(variant => {
                          const firstAttrValue = variant.attributes?.[0]?.value || 'Default';
                          if (!groupedByFirstAttr[firstAttrValue]) {
                            groupedByFirstAttr[firstAttrValue] = [];
                            // Store image for this attribute value
                            firstAttrImages[firstAttrValue] = variant.image || selectedProductDetails._selectedInfo?.image || noImage;
                          }
                          groupedByFirstAttr[firstAttrValue].push(variant);
                        });

                        const attrKeys = Object.keys(groupedByFirstAttr);
                        const selectedFirstAttr = attrKeys.includes(selectedProductVariants[selectedVariantImageIdx]?.attributes?.[0]?.value)
                          ? selectedProductVariants[selectedVariantImageIdx]?.attributes?.[0]?.value
                          : attrKeys[0];

                        // Get variants for selected first attribute
                        const filteredVariants = groupedByFirstAttr[selectedFirstAttr] || selectedProductVariants;

                        return (
                          <>
                            {/* First Attribute (Color) - Horizontal Scroll */}
                            {attrKeys.length > 1 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>{firstAttrName}</div>
                                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px' }}>
                                  {attrKeys.map((attrValue) => {
                                    const isSelected = attrValue === selectedFirstAttr;
                                    const variantWithThisAttr = groupedByFirstAttr[attrValue][0];
                                    const variantIdx = selectedProductVariants.findIndex(v => v.id === variantWithThisAttr.id);

                                    return (
                                      <div
                                        key={attrValue}
                                        onClick={() => setSelectedVariantImageIdx(variantIdx >= 0 ? variantIdx : 0)}
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          gap: '6px',
                                          padding: '8px',
                                          minWidth: '80px',
                                          border: isSelected ? '2px solid #3DD0E5' : '1px solid #e5e7eb',
                                          borderRadius: '8px',
                                          background: isSelected ? '#f0f9ff' : '#fff',
                                          cursor: 'pointer',
                                          flexShrink: 0,
                                          transition: 'all 0.2s ease'
                                        }}
                                      >
                                        <img
                                          src={firstAttrImages[attrValue]}
                                          alt={attrValue}
                                          style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px' }}
                                          onError={(e) => { e.currentTarget.src = noImage; }}
                                        />
                                        <span style={{
                                          fontSize: '11px',
                                          color: '#1e293b',
                                          textAlign: 'center',
                                          wordBreak: 'break-word',
                                          lineHeight: 1.3,
                                          maxWidth: '80px'
                                        }}>
                                          {attrValue}
                                        </span>
                                        <span style={{ fontSize: '10px', color: '#64748b' }}>
                                          {groupedByFirstAttr[attrValue].length} variants
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Variant List - Vertical with full details */}
                            <div style={{
                              maxHeight: '400px',
                              overflowY: 'auto',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              background: '#fff'
                            }}>
                              {filteredVariants.map((variant, idx) => {
                                // console.log('variant at: ', idx, ' ->', variant)
                                const isSelected = selectedProductVariants[selectedVariantImageIdx]?.id === variant.id;

                                return (
                                  <div
                                    key={variant.id}
                                    onClick={() => {
                                      const globalIdx = selectedProductVariants.findIndex(v => v.id === variant.id);
                                      setSelectedVariantImageIdx(globalIdx >= 0 ? globalIdx : 0);
                                    }}
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr auto',
                                      gap: '12px',
                                      padding: '12px',
                                      borderBottom: idx < filteredVariants.length - 1 ? '1px solid #e5e7eb' : 'none',
                                      background: isSelected ? '#f0f9ff' : (idx % 2 === 0 ? '#fff' : '#f8fafc'),
                                      borderLeft: isSelected ? '3px solid #3DD0E5' : '3px solid transparent',
                                      cursor: 'pointer',
                                      transition: 'all 0.15s ease',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {/* Left: Image + Details */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
                                      <img
                                        src={variant.image || selectedProductDetails._selectedInfo?.image || noImage}
                                        alt={variant.attributeText}
                                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
                                        onError={(e) => { e.currentTarget.src = noImage; }}
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        {/* Full attribute text - no truncation */}
                                        <div style={{
                                          fontSize: '12px',
                                          fontWeight: 500,
                                          color: '#1e293b',
                                          marginBottom: '4px',
                                          wordBreak: 'break-word',
                                          lineHeight: 1.4
                                        }}>
                                          {variant.attributeText}
                                        </div>
                                        {/* Weight & Dimensions */}
                                        <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>
                                          Wt: {(variant.weight || 0).toFixed(3)}kg
                                          {variant.dimensions && ` | L: ${variant.dimensions.length}cm × W: ${variant.dimensions.width}cm × H: ${variant.dimensions.height}cm`}
                                        </div>
                                        {/* Stock */}
                                        <div style={{
                                          fontSize: '10px',
                                          color: variant.stock > 0 ? '#16a34a' : '#dc2626',
                                          fontWeight: 500
                                        }}>
                                          {variant.stock > 0 ? `${variant.stock} units in stock` : 'Out of stock'}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Right: Price + Map Button */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                      {/* Pricing Details */}
                                      <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        {/* RMB Price */}
                                        <div style={{ fontSize: '10px', color: '#ea580c', fontWeight: 500 }}>
                                          ¥{variant.price.rmb.toFixed(2)}
                                        </div>

                                        {/* FBP Price from breakdown (Primary Display) */}
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#3b82f6' }}>
                                          ₹{(variant?.importerrPrice || 0).toFixed(0)}
                                        </div>

                                        {/* COMMENTED OUT: INR Price from /calculate route */}
                                        {/* 
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#3b82f6' }}>
                                          ₹{variant.price.inr.toFixed(0)}
                                        </div>
                                        */}

                                        {/* Importerr Price (if available) */}
                                        {variant.importerrPrice > 0 && (
                                          <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 500 }}>
                                            Imp: ₹{variant.importerrPrice.toFixed(0)}
                                          </div>
                                        )}

                                        {/* COMMENTED OUT: Margin per order (if failed orders available) */}
                                        {/* 
                                        {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                                          (() => {
                                            const avgOrderValue = failedOrderAmount.totalAmount / failedOrderAmount.orderCount;
                                            const adjustedOrderValue = avgOrderValue * (1 - marketplaceDeduction / 100);
                                            const margin = adjustedOrderValue - variant.price.inr;
                                            const marginPercent = adjustedOrderValue > 0 ? ((margin / adjustedOrderValue) * 100).toFixed(0) : 0;
                                            const isPositive = margin > 0;

                                            return (
                                              <div style={{
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                color: isPositive ? '#16a34a' : '#dc2626',
                                                backgroundColor: isPositive ? '#f0fdf4' : '#fef2f2',
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                marginTop: '2px'
                                              }}>
                                                {isPositive ? '+' : ''}₹{margin.toFixed(0)} ({marginPercent}%)
                                              </div>
                                            );
                                          })()
                                        )}
                                        */}
                                      </div>

                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openConfirmModal(variant);
                                        }}
                                        disabled={isMapping}
                                        style={{
                                          padding: '6px 12px',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          color: '#fff',
                                          background: isMapping ? '#94a3b8' : '#16a34a',
                                          border: 'none',
                                          borderRadius: '6px',
                                          cursor: isMapping ? 'not-allowed' : 'pointer',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        Map to ASIN
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div >
                          </>
                        );
                      })()
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                        No variant information available
                      </div>
                    )}
                  </div>

                  {/* ========== FULL 1688 API DATA SECTIONS ========== */}
                  <div style={{ padding: '0 20px 20px' }}>

                    {/* Product Sale Info - Clean Table Layout */}
                    {/* <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">📊 Product Sale Info</div>
                      </div>
                      <div className="alibaba-section-content">
                        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <tbody>
                              {Object.entries(selectedProductDetails.productSaleInfo || {})
                                .filter(([key]) => key !== 'priceRangeList')
                                .map(([key, value], idx) => {
                                  // Format value based on type
                                  let displayValue = '';
                                  if (Array.isArray(value)) {
                                    displayValue = value.length > 0 ? value.join(', ') : '-';
                                  } else if (typeof value === 'object' && value !== null) {
                                    // For objects, show key-value pairs
                                    displayValue = Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
                                  } else {
                                    displayValue = String(value);
                                  }

                                  return (
                                    <tr key={key} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 500, textTransform: 'uppercase', fontSize: '10px', width: '140px' }}>
                                        {key}
                                      </td>
                                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#1e293b', fontWeight: 500 }}>
                                        {displayValue}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                        {(!selectedProductDetails.productSaleInfo || Object.keys(selectedProductDetails.productSaleInfo).length === 0) && (
                          <div style={{ color: '#94a3b8', fontSize: '12px', padding: '12px', textAlign: 'center' }}>No sale info available</div>
                        )}
                      </div>
                    </div> */}

                    {/* Price Range List - Clean Cards */}
                    {(selectedProductDetails.productSaleInfo?.priceRangeList || []).length > 1 && (
                      <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className="alibaba-section-header">
                          <div className="alibaba-section-title">💰 Price Tiers ({(selectedProductDetails.productSaleInfo?.priceRangeList || []).length})</div>
                        </div>
                        <div className="alibaba-section-content">
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {(calculatedPriceRanges.length > 0 ? calculatedPriceRanges : selectedProductDetails.productSaleInfo?.priceRangeList || []).map((tier, idx) => (
                              <div key={idx} style={{ flex: '1', minWidth: '100px', maxWidth: '150px', padding: '12px', background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)', border: '1px solid #fed7aa', borderRadius: '8px', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', color: '#9a3412', marginBottom: '4px' }}>
                                  {tier.startQuantity || tier.beginAmount || 1}{tier.endQuantity ? ` - ${tier.endQuantity}` : '+'} pcs
                                </div>
                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#ea580c' }}>
                                  ¥{parseFloat(tier.price || tier.consignPrice || 0).toFixed(2)}
                                </div>
                                {tier.calculatedInr != null && tier.calculatedInr > 0 && (
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#15803d', marginTop: '4px' }}>
                                    ₹{tier.calculatedInr.toFixed(0)}
                                  </div>
                                )}
                                {calculatedPriceRanges.length === 0 && (
                                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>calculating...</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Shipping Info - Clean Layout */}
                    <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">🚚 Shipping Info</div>
                      </div>
                      <div className="alibaba-section-content">
                        {(() => {
                          const shippingInfo = selectedProductDetails.productShippingInfo || {};
                          const skuShippingDetails = shippingInfo.skuShippingDetails || [];

                          // Extract simple fields (non-array)
                          const simpleFields = Object.entries(shippingInfo)
                            .filter(([key, value]) => key !== 'skuShippingDetails' && !Array.isArray(value));

                          return (
                            <>
                              {/* Simple shipping fields */}
                              {/* {simpleFields.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: skuShippingDetails.length > 0 ? '16px' : 0 }}>
                                  {simpleFields.map(([key, value]) => (
                                    <div key={key} style={{ padding: '10px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                                      <div style={{ fontSize: '10px', color: '#0369a1', marginBottom: '4px', textTransform: 'uppercase' }}>{key}</div>
                                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#0c4a6e' }}>
                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )} */}

                              {/* SKU Shipping Details Table */}
                              {skuShippingDetails.length > 0 && (
                                <div>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#0369a1', marginBottom: '8px' }}>
                                    SKU Shipping Details ({skuShippingDetails.length} variants)
                                  </div>
                                  <div style={{ overflowX: 'auto', border: '1px solid #bae6fd', borderRadius: '8px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                      <thead>
                                        <tr style={{ background: '#f0f9ff' }}>
                                          <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>SKU ID</th>
                                          <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>Weight (kg)</th>
                                          <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>L × W × H (cm)</th>
                                          <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>Spec ID</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {skuShippingDetails.slice(0, 20).map((sku, idx) => (
                                          <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                            <td style={{ padding: '6px 10px', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace', color: '#1e293b' }}>
                                              {sku.skuId || '-'}
                                            </td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#1e293b', fontWeight: 500 }}>
                                              {sku.weight || '-'}
                                            </td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#1e293b' }}>
                                              {sku.length && sku.width && sku.height
                                                ? `${sku.length} × ${sku.width} × ${sku.height}`
                                                : '-'}
                                            </td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontFamily: 'monospace', fontSize: '10px' }}>
                                              {sku.specId || '-'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {skuShippingDetails.length > 20 && (
                                      <div style={{ padding: '8px', textAlign: 'center', background: '#f0f9ff', color: '#0369a1', fontSize: '11px' }}>
                                        ... and {skuShippingDetails.length - 20} more variants
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {simpleFields.length === 0 && skuShippingDetails.length === 0 && (
                                <div style={{ color: '#94a3b8', fontSize: '12px' }}>No shipping info available</div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Seller Data Info */}
                    {/* <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">🏪 Seller Info</div>
                      </div>
                      <div className="alibaba-section-content">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                          {selectedProductDetails.companyName && (
                            <div style={{ padding: '10px', background: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                              <div style={{ fontSize: '10px', color: '#9a3412', marginBottom: '4px', textTransform: 'uppercase' }}>Company Name</div>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#c2410c' }}>{selectedProductDetails.companyName}</div>
                            </div>
                          )}
                          {selectedProductDetails.sellerLoginId && (
                            <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Seller Login ID</div>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{selectedProductDetails.sellerLoginId}</div>
                            </div>
                          )}
                          {selectedProductDetails.sellerMemberId && (
                            <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Seller Member ID</div>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{selectedProductDetails.sellerMemberId}</div>
                            </div>
                          )}
                          {Object.entries(selectedProductDetails.sellerDataInfo || {}).map(([key, value]) => (
                            <div key={key} style={{ padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>{key}</div>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', wordBreak: 'break-all' }}>
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </div>
                            </div>
                          ))}
                          {!selectedProductDetails.companyName && !selectedProductDetails.sellerLoginId && (!selectedProductDetails.sellerDataInfo || Object.keys(selectedProductDetails.sellerDataInfo).length === 0) && (
                            <div style={{ color: '#94a3b8', fontSize: '12px' }}>No seller info available</div>
                          )}
                        </div>
                      </div>
                    </div> */}

                    {/* Product Attributes */}
                    {(selectedProductDetails.productAttribute || []).length > 0 && (
                      <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className="alibaba-section-header">
                          <div className="alibaba-section-title">📋 Product Attributes ({(selectedProductDetails.productAttribute || []).length})</div>
                        </div>
                        <div className="alibaba-section-content">
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                            {(selectedProductDetails.productAttribute || []).map((attr, idx) => (
                              <div key={idx} style={{ display: 'flex', padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                <span style={{ fontSize: '11px', color: '#64748b', minWidth: '100px', flexShrink: 0 }}>
                                  {attr.attributeNameTrans || attr.attributeName || attr.name || `Attr ${idx + 1}`}
                                </span>
                                <span style={{ fontSize: '11px', fontWeight: 500, color: '#1e293b', wordBreak: 'break-word' }}>
                                  {attr.valueTrans || attr.value || attr.attributeValue || 'N/A'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tag Info List */}
                    {/* {(selectedProductDetails.tagInfoList || []).length > 0 && (
                      <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className="alibaba-section-header">
                          <div className="alibaba-section-title">🏷️ Tags ({(selectedProductDetails.tagInfoList || []).length})</div>
                        </div>
                        <div className="alibaba-section-content">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {(selectedProductDetails.tagInfoList || []).map((tag, idx) => (
                              <span key={idx} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '11px',
                                fontWeight: 500,
                                background: tag.value === true || tag.value === 'true' ? '#dcfce7' : '#f3f4f6',
                                color: tag.value === true || tag.value === 'true' ? '#166534' : '#374151',
                                border: `1px solid ${tag.value === true || tag.value === 'true' ? '#bbf7d0' : '#e5e7eb'}`
                              }}>
                                {tag.key}: {String(tag.value)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )} */}

                    {/* Product Feature */}
                    {selectedProductDetails.productFeature && Object.keys(selectedProductDetails.productFeature).length > 0 && (
                      <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className="alibaba-section-header">
                          <div className="alibaba-section-title">⭐ Product Features</div>
                        </div>
                        <div className="alibaba-section-content">
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                            {Object.entries(selectedProductDetails.productFeature || {}).map(([key, value]) => (
                              <div key={key} style={{ padding: '10px', background: '#fefce8', borderRadius: '6px', border: '1px solid #fef08a' }}>
                                <div style={{ fontSize: '10px', color: '#a16207', marginBottom: '4px', textTransform: 'uppercase' }}>{key}</div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#854d0e', wordBreak: 'break-all' }}>
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Product Images - Thumbnails Gallery */}
                    {selectedProductDetails.productImage && (selectedProductDetails.productImage.images || []).length > 0 && (
                      <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className="alibaba-section-header">
                          <div className="alibaba-section-title">🖼️ Product Images ({(selectedProductDetails.productImage?.images || []).length})</div>
                        </div>
                        <div className="alibaba-section-content">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {(selectedProductDetails.productImage?.images || []).map((img, idx) => (
                              <div
                                key={idx}
                                style={{
                                  width: '70px',
                                  height: '70px',
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                  border: '1px solid #e5e7eb',
                                  background: '#f8fafc',
                                  cursor: 'pointer'
                                }}
                                onClick={() => window.open(img, '_blank')}
                                title={`Image ${idx + 1} - Click to open`}
                              >
                                <img
                                  src={img}
                                  alt={`Product ${idx + 1}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Other Top-Level Fields - Clean Table Layout */}
                    {/* <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">📝 Other Product Data</div>
                      </div>
                      <div className="alibaba-section-content">
                        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 600, width: '180px' }}>Field</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 600 }}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(selectedProductDetails)
                                .filter(([key]) => !['productSaleInfo', 'productShippingInfo', 'sellerDataInfo', 'productAttribute', 'tagInfoList', 'productFeature', 'productImage', 'productSkuInfos', 'skuInfos', '_selectedInfo', 'variations'].includes(key))
                                .filter(([key, value]) => value !== null && value !== undefined && value !== '')
                                .map(([key, value], idx) => (
                                  <tr key={key} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 500, textTransform: 'uppercase', fontSize: '10px' }}>
                                      {key}
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#1e293b', wordBreak: 'break-all' }}>
                                      {typeof value === 'object'
                                        ? (Array.isArray(value) ? `[${value.length} items]` : JSON.stringify(value).substring(0, 300))
                                        : String(value).substring(0, 300)}
                                      {(typeof value === 'string' && value.length > 300) && '...'}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div> */}
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Similar Products Grid */}
              <div className="apd-sidebar-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, flexShrink: 0 }}>Similar Products from BTPL/Global</h3>
                <SearchBySkuUrl
                  onSearchStart={handleSearchStart}
                  onSearchComplete={handleSearchComplete}
                  fetchMinVariantPrice={fetchMinVariantPrice}
                  disabled={loadingSimilar}
                />
                <button className="apd-sidebar-close" onClick={() => setShowSimilar(false)} style={{ flexShrink: 0 }}>×</button>
              </div>

              {/* Top Bar - Pricing Algorithm + Count + Pagination */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 16px',
                backgroundColor: '#fff',
                borderBottom: '1px solid #e2e8f0',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                {/* Left - Pricing Algorithm */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b' }}>Pricing Algorithm:</span>
                  {loadingAlgos ? (
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Loading...</span>
                  ) : pricingAlgorithms.length === 0 ? (
                    <span style={{ fontSize: '11px', color: '#dc2626', backgroundColor: '#fef2f2', padding: '4px 8px', borderRadius: '4px' }}>
                      ⚠️ No "actual" algorithm found - prices will show as ₹0
                    </span>
                  ) : (
                    pricingAlgorithms.map(algo => (
                      <button
                        key={algo._id}
                        onClick={() => handleAlgoChange(algo._id)}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: selectedAlgoId === algo._id
                            ? (algo.domain === 'boundless.co.in' ? '#16a34a' : '#3b82f6')
                            : '#e2e8f0',
                          color: selectedAlgoId === algo._id ? '#fff' : '#475569',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {algo.name}
                      </button>
                    ))
                  )}
                </div>

                {/* Center - Count + Sort */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>
                    {similarProducts.length} similar products found
                  </span>

                  {/* Multi-Select Filter Dropdown */}
                  <div ref={filterDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: 500,
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        backgroundColor: selectedFilters.length > 0 ? '#eff6ff' : '#fff',
                        color: '#475569',
                        cursor: 'pointer',
                        outline: 'none',
                        minWidth: '150px',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '6px'
                      }}
                    >
                      <span>
                        {selectedFilters.length === 0
                          ? 'Filter'
                          : selectedFilters.length === 1
                            ? selectedFilters[0].replace('-', ': ').replace('low', 'Low to High').replace('high', 'High to Low')
                            : `${selectedFilters.length} filters selected`
                        }
                      </span>
                      <span style={{ fontSize: '10px' }}>▼</span>
                    </button>

                    {showFilterDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '4px',
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        minWidth: '180px',
                        padding: '4px'
                      }}>
                        {[
                          { value: 'price-low', label: 'Importerr Price: Low to High' },
                          { value: 'price-high', label: 'Importerr Price: High to Low' },
                          // { value: 'margin-low', label: 'Margin: Low to High' },
                          // { value: 'margin-high', label: 'Margin: High to Low' },
                          { value: 'moq-low', label: 'MOQ: Low to High' },
                          { value: 'moq-high', label: 'MOQ: High to Low' }
                        ].map(option => (
                          <label
                            key={option.value}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              fontSize: '11px',
                              color: '#475569',
                              backgroundColor: selectedFilters.includes(option.value) ? '#eff6ff' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (!selectedFilters.includes(option.value)) {
                                e.currentTarget.style.backgroundColor = '#f8fafc';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!selectedFilters.includes(option.value)) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedFilters.includes(option.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFilters([...selectedFilters, option.value]);
                                } else {
                                  setSelectedFilters(selectedFilters.filter(f => f !== option.value));
                                }
                                setSortOption('default');
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                        {selectedFilters.length > 0 && (
                          <div style={{
                            borderTop: '1px solid #e2e8f0',
                            marginTop: '4px',
                            paddingTop: '4px'
                          }}>
                            <button
                              onClick={() => {
                                setSelectedFilters([]);
                                setSortOption('default');
                                setShowFilterDropdown(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: '#f8fafc',
                                color: '#64748b',
                                cursor: 'pointer',
                                outline: 'none'
                              }}
                            >
                              Clear All
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right - Pagination */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => handleFindSimilar(currentPage - 1)}
                    disabled={currentPage <= 1 || loadingSimilar}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: currentPage <= 1 ? '#f1f5f9' : '#3b82f6',
                      color: currentPage <= 1 ? '#94a3b8' : '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Prev
                  </button>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => handleFindSimilar(currentPage + 1)}
                    disabled={currentPage >= totalPages || loadingSimilar}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: currentPage >= totalPages ? '#f1f5f9' : '#3b82f6',
                      color: currentPage >= totalPages ? '#94a3b8' : '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>

              {loadingSimilar ? (
                <div className="apd-sidebar-loading">
                  <FaSpinner className="apd-spinner" />
                  <p>Processing Products...</p>
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Please wait while we fetch variant details for accurate pricing</span>
                  {processingProgress.total > 0 && (
                    <div style={{ marginTop: '24px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {/* Discrete Progress Indicators */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {Array.from({ length: processingProgress.total }).map((_, idx) => (
                          <div
                            key={idx}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'all 0.3s ease',
                              backgroundColor: idx < processingProgress.current ? '#10b981' : '#e2e8f0',
                              color: idx < processingProgress.current ? '#ffffff' : '#64748b',
                              border: idx === processingProgress.current ? '2px solid #059669' : 'none',
                              boxShadow: idx < processingProgress.current ? '0 2px 4px rgba(16, 185, 129, 0.3)' : 'none'
                            }}
                          >
                            {idx < processingProgress.current ? '✓' : idx + 1}
                          </div>
                        ))}
                      </div>

                      {/* Progress Bar */}
                      <div style={{
                        width: 'auto',
                        minWidth: '280px',
                        maxWidth: '90%',
                        height: '32px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        position: 'relative',
                        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
                      }}>
                        <div style={{
                          width: `${(processingProgress.current / processingProgress.total) * 100}%`,
                          height: '100%',
                          background: 'linear-gradient(to right, #10b981, #34d399)',
                          transition: 'width 0.3s ease',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          minWidth: processingProgress.current > 0 ? '60px' : '0'
                        }}>
                          <span style={{
                            color: '#ffffff',
                            fontSize: '13px',
                            fontWeight: '600',
                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                            whiteSpace: 'nowrap'
                          }}>
                            {Math.round((processingProgress.current / processingProgress.total) * 100)}%
                          </span>
                        </div>
                      </div>

                      {/* Status Text */}
                      <div style={{
                        fontSize: '11px',
                        color: '#64748b',
                        marginTop: '8px',
                        textAlign: 'center'
                      }}>
                        <div>{processingProgress.stage || `Processing product ${processingProgress.current} of ${processingProgress.total}...`}</div>
                        <div style={{ fontSize: '10px', marginTop: '4px', color: '#94a3b8' }}>
                          {processingProgress.current} of {processingProgress.total} completed
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : similarProducts.length > 0 ? (
                <>
                  {/* Failed Order Summary - Segregated at Top */}
                  {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                    <div className="apd-order-summary">
                      <div className="apd-order-summary-title">Failed Order Demand for ASIN: {asinFromQuery}</div>
                      <div className="apd-order-summary-grid">
                        <div className="apd-order-stat">
                          <div className="apd-order-stat-label">Total Value</div>
                          <div className="apd-order-stat-value">₹{failedOrderAmount.totalAmount.toLocaleString('en-IN')}</div>
                        </div>
                        <div className="apd-order-stat">
                          <div className="apd-order-stat-label">Orders</div>
                          <div className="apd-order-stat-value">{failedOrderAmount.orderCount}</div>
                        </div>
                        <div className="apd-order-stat">
                          <div className="apd-order-stat-label">Avg Order Value</div>
                          <div className="apd-order-stat-value">₹{(failedOrderAmount.totalAmount / failedOrderAmount.orderCount).toFixed(0)}</div>
                        </div>
                        <div className="apd-order-stat">
                          <div className="apd-order-stat-label">Order Quantity</div>
                          <div className="apd-order-stat-value">{failedOrderAmount.totalQuantity || 0}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="apd-similar-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: '100%' }}>
                    {sortedSimilarProducts.map((product, index) => {
                      const orderCount = failedOrderAmount ? failedOrderAmount.orderCount : 0;
                      const avgOrderValue = failedOrderAmount ? failedOrderAmount.totalAmount / failedOrderAmount.orderCount : 0;
                      // Apply marketplace deduction to order value
                      const adjustedOrderValue = avgOrderValue * (1 - marketplaceDeduction / 100);
                      // Calculate margin: adjusted order value - min FBP
                      const marginPerOrder = adjustedOrderValue - product.price.inr;
                      const totalMargin = marginPerOrder * orderCount;
                      const marginPercent = adjustedOrderValue > 0 ? ((marginPerOrder / adjustedOrderValue) * 100).toFixed(0) : 0;
                      const isPositive = marginPerOrder > 0;
                      // Match prediction - higher for first results, decreases as you go down (only for default sort)
                      const matchPercent = (selectedFilters.length === 0 && sortOption === 'default') ? Math.max(95 - (index * 3), 45) : null;

                      return (
                        <div
                          key={product.id}
                          className="apd-pcard"
                          onClick={() => openProductVariants(product)}
                        >
                          {/* Image with Match Prediction Badge */}
                          <div className="apd-pcard-img-wrap">
                            <img
                              src={product.image}
                              alt={product.title}
                              className="apd-pcard-img"
                              onError={(e) => { e.currentTarget.src = noImage; }}
                            />
                            {/* Match Prediction Badge - Top Right (only show for default sort) */}
                            {matchPercent !== null && (
                              <div style={{
                                position: 'absolute',
                                top: '6px',
                                right: '6px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                                backgroundColor: matchPercent >= 80 ? '#dcfce7' : matchPercent >= 60 ? '#fef3c7' : '#fee2e2',
                                color: matchPercent >= 80 ? '#16a34a' : matchPercent >= 60 ? '#d97706' : '#dc2626',
                                border: `1px solid ${matchPercent >= 80 ? '#bbf7d0' : matchPercent >= 60 ? '#fcd34d' : '#fecaca'}`,
                                zIndex: 2
                              }}>
                                {matchPercent}%
                              </div>
                            )}
                            {/* Sort indicator badge when filtering by margin/price/moq */}
                            {(selectedFilters.length > 0 || sortOption !== 'default') && (
                              <div style={{
                                position: 'absolute',
                                top: '6px',
                                right: '6px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 600,
                                backgroundColor: selectedFilters.some(f => f.includes('margin'))
                                  ? (isPositive ? '#dcfce7' : '#fee2e2')
                                  : selectedFilters.some(f => f.includes('moq'))
                                    ? '#fef3c7'
                                    : '#e0f2fe',
                                color: selectedFilters.some(f => f.includes('margin'))
                                  ? (isPositive ? '#16a34a' : '#dc2626')
                                  : selectedFilters.some(f => f.includes('moq'))
                                    ? '#d97706'
                                    : '#0284c7',
                                border: `1px solid ${selectedFilters.some(f => f.includes('margin'))
                                  ? (isPositive ? '#bbf7d0' : '#fecaca')
                                  : selectedFilters.some(f => f.includes('moq'))
                                    ? '#fcd34d'
                                    : '#7dd3fc'}`,
                                zIndex: 2
                              }}>
                                #{index + 1}
                              </div>
                            )}
                          </div>

                          {/* Variant Badge */}
                          <div className="apd-pcard-variants">
                            {/* {product.variantCount || Math.floor(Math.random() * 10) + 1} Variants */}
                            {product?.detailed_info?.productSkuInfos?.length || 1} Variants
                          </div>

                          {/* Title */}
                          <div className="apd-pcard-title" title={product.title}>
                            {product.title}
                          </div>

                          {/* Prices - Shows minimum variant FBP */}
                          <div className="apd-pcard-prices" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '12px' }}>
                            {/* COMMENTED OUT: Left side: Min FBP and main price */}
                            {/* <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                              {/* Row 1: Min FBP label and value */}
                            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '9px', color: '#64748b' }}>Min FBP</span>
                                <span className="apd-pcard-price-rmb">¥{(product.price?.rmb || 0).toFixed(0)}</span>
                              </div> */}
                            {/* Row 2: Main price (₹813) on left, info icon on right */}
                            {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px' }}>
                                <span className="apd-pcard-price-inr">₹{(product.price?.inr || 0).toFixed(0)}</span> */}
                            {/* Light blue area with info icon - COMMENTED OUT */}
                            {/* <div
                                  title="View pricing details"
                                  style={{
                                    minWidth: '16px',
                                    minHeight: '16px',
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: isPositive ? '#60a5fa' : '#ef4444',
                                    borderRadius: '3px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    flexShrink: 0,
                                    zIndex: 10,
                                    boxSizing: 'border-box',
                                    border: `1px solid ${isPositive ? '#3b82f6' : '#dc2626'}`,
                                    marginLeft: 'auto'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.stopPropagation();
                                    const productKey = `product-${index}-${product.id || product._id || 'unknown'}`;
                                    const rect = e.currentTarget.getBoundingClientRect();

                                    // Estimate tooltip height (approximately 350px for full content)
                                    const tooltipHeight = 350;
                                    const tooltipWidth = 300;
                                    const spacing = 6;

                                    // Check if tooltip would go below viewport
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    const spaceAbove = rect.top;
                                    const showAbove = spaceBelow < tooltipHeight && spaceAbove > spaceBelow;

                                    // Calculate top position
                                    let top = showAbove ? rect.top - tooltipHeight - spacing : rect.bottom + spacing;

                                    // Ensure tooltip doesn't go above viewport
                                    if (top < 10) {
                                      top = 10;
                                    }

                                    // Ensure tooltip doesn't go below viewport
                                    if (top + tooltipHeight > window.innerHeight - 10) {
                                      top = window.innerHeight - tooltipHeight - 10;
                                    }

                                    // Calculate left position (center of icon)
                                    let left = rect.left + rect.width / 2;

                                    // Ensure tooltip doesn't go off left edge
                                    if (left - tooltipWidth / 2 < 10) {
                                      left = tooltipWidth / 2 + 10;
                                    }

                                    // Ensure tooltip doesn't go off right edge
                                    if (left + tooltipWidth / 2 > window.innerWidth - 10) {
                                      left = window.innerWidth - tooltipWidth / 2 - 10;
                                    }

                                    setTooltipPosition({
                                      top: top,
                                      left: left
                                    });
                                    setHoveredProductId(productKey);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.stopPropagation();
                                    setHoveredProductId(null);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FaInfoCircle style={{ color: '#ffffff', fontSize: '9px', lineHeight: 1, pointerEvents: 'none' }} />
                                  {/* Tooltip on hover */}
                            {/* {hoveredProductId === `product-${index}-${product.id || product._id || 'unknown'}` && (
                                    <div
                                      onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        const productKey = `product-${index}-${product.id || product._id || 'unknown'}`;
                                        setHoveredProductId(productKey);
                                      }}
                                      onMouseLeave={(e) => {
                                        e.stopPropagation();
                                        setHoveredProductId(null);
                                      }}
                                      style={{
                                        position: 'fixed',
                                        top: `${tooltipPosition.top}px`,
                                        left: `${tooltipPosition.left}px`,
                                        transform: 'translateX(-50%)',
                                        zIndex: 99999,
                                        minWidth: '280px',
                                        maxWidth: '300px',
                                        maxHeight: 'calc(100vh - 20px)',
                                        padding: '12px',
                                        backgroundColor: '#fff',
                                        borderRadius: '6px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        border: '1px solid #e2e8f0',
                                        pointerEvents: 'auto',
                                        overflowY: 'auto',
                                        overflowX: 'hidden'
                                      }}>
                                      {/* Arrow pointing up */}
                            {/* <div style={{
                                        position: 'absolute',
                                        top: '-5px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 0,
                                        height: 0,
                                        borderLeft: '5px solid transparent',
                                        borderRight: '5px solid transparent',
                                        borderBottom: '5px solid #e2e8f0'
                                      }}></div>
                                      <div style={{
                                        position: 'absolute',
                                        top: '-4px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 0,
                                        height: 0,
                                        borderLeft: '5px solid transparent',
                                        borderRight: '5px solid transparent',
                                        borderBottom: '5px solid #fff'
                                      }}></div>
                                      {/* Red Importerr Price Card */}
                            {/* {product.importerrPrice > 0 && (
                                        <div style={{
                                          padding: '6px 8px',
                                          backgroundColor: '#fee2e2',
                                          borderRadius: '4px',
                                          border: '1px solid #fecaca',
                                          marginBottom: '6px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: '6px'
                                        }}>
                                          <span style={{ fontSize: '10px', color: '#991b1b', fontWeight: 600 }}>
                                            Importerr Price
                                          </span>
                                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>
                                            ₹{(product.importerrPrice || 0).toFixed(0)}
                                          </span>
                                        </div>
                                      )} */}

                            {/* Green Margin Order Value Card */}
                            {/* {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                                        <div style={{
                                          padding: '10px 12px',
                                          backgroundColor: '#dcfce7', borderRadius: '4px',
                                          border: '1px solid #bbf7d0',
                                          marginBottom: '6px'
                                        }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', paddingBottom: '5px', borderBottom: '1px dashed #bbf7d0' }}>
                                            <div style={{ textAlign: 'left' }}>
                                              <div style={{ fontSize: '10px', color: '#166534', marginBottom: '2px', fontWeight: 600 }}>
                                                Order Value (-30%)
                                              </div>
                                              <div style={{ fontSize: '14px', fontWeight: 700, color: '#166534' }}>
                                                ₹{adjustedOrderValue.toFixed(0)}
                                              </div>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#16a34a' }}>→</div>
                                            <div style={{ textAlign: 'right' }}>
                                              <div style={{ fontSize: '10px', color: '#166534', marginBottom: '2px', fontWeight: 600 }}>
                                                Min FBP
                                              </div>
                                              <div style={{ fontSize: '14px', fontWeight: 700, color: '#16a34a' }}>
                                                ₹{(product.price?.inr || 0).toFixed(0)}
                                              </div>
                                            </div>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '6px' }}>
                                            <span style={{ fontSize: '10px', color: '#166534', fontWeight: 600 }}>Margin/Order</span>
                                            <span style={{ fontSize: '14px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                              ₹{marginPerOrder.toFixed(0)} ({adjustedOrderValue > 0 ? ((marginPerOrder / adjustedOrderValue) * 100).toFixed(0) : 0}%)
                                            </span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '10px', color: '#166534', fontWeight: 600 }}>Total ({orderCount} orders)</span>
                                            <span style={{ fontSize: '14px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                              ₹{totalMargin.toLocaleString('en-IN')}
                                            </span>
                                          </div>
                                        </div>
                                      )} */}

                            {/* Yellow Card with Amazon Price and SP */}
                            {/* <div style={{
                                        padding: '8px 10px',
                                        backgroundColor: '#fef9c3',
                                        borderRadius: '4px',
                                        border: '1px solid #fde047',
                                        marginBottom: '6px'
                                      }}>
                                        <div style={{ fontSize: '10px', color: '#854d0e', fontWeight: 600, marginBottom: '2px' }}>Importerr Price</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '6px' }}>
                                          <span style={{ fontSize: '10px', color: '#854d0e', fontWeight: 600 }}>Amazon Price:</span>
                                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#ca8a04' }}>
                                            ₹{((product.price?.inr || 0) * 1.3).toFixed(0)}
                                          </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                                          <span style={{ fontSize: '10px', color: '#854d0e', fontWeight: 600 }}>Selling Price:</span>
                                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#ca8a04' }}>
                                            ₹{product.importerrPrice > 0 ? (product.importerrPrice * 1.3).toFixed(0) : ((product.price?.inr || 0) * 1.3).toFixed(0)}
                                          </span>
                                        </div>
                                      </div> */}
                            {/* </div>
                                  )} */}
                            {/* </div> */}
                            {/* </div> */}
                            {/* </div> */}

                            {/* Left side: Importerr Price - Moved to left */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                              gap: '2px',
                              minWidth: '110px'
                            }}>
                              <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>Importerr Price</span>
                              <span style={{ fontSize: '16px', fontWeight: 700, color: '#059669' }}>₹{(product.importerrPrice || 0).toFixed(0)}</span>
                            </div>
                          </div>

                          {/* Meta Row */}
                          <div className="apd-pcard-meta">
                            <span className="apd-pcard-moq">MOQ: {product.moq || 1}</span>
                            <span className="apd-pcard-rating">
                              <FaStar /> {product.rating || (Math.random() * 1.5 + 3.5).toFixed(1)}
                            </span>
                          </div>

                          {/* Actions - Always Show but Disabled until Hover */}
                          <div className="apd-pcard-actions">
                            <button
                              className="apd-pcard-btn apd-pcard-btn-disabled"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!e.currentTarget.disabled) {
                                  openProductVariants(product);
                                }
                              }}
                            >
                              View Variants
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom Pagination */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    padding: '12px 16px',
                    backgroundColor: '#fff',
                    borderTop: '1px solid #e2e8f0',
                    gap: '12px'
                  }}>
                    <button
                      onClick={() => handleFindSimilar(currentPage - 1)}
                      disabled={currentPage <= 1 || loadingSimilar}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentPage <= 1 ? '#f1f5f9' : '#3b82f6',
                        color: currentPage <= 1 ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ← Previous
                    </button>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handleFindSimilar(currentPage + 1)}
                      disabled={currentPage >= totalPages || loadingSimilar}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentPage >= totalPages ? '#f1f5f9' : '#3b82f6',
                        color: currentPage >= totalPages ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next →
                    </button>
                  </div>
                </>
              ) : (
                <div className="apd-sidebar-empty">
                  <p>No similar products found</p>
                </div>
              )}
            </>
          )}
        </div>
      )
      }

      {/* Confirmation Modal - Before Mapping */}
      {
        showConfirmModal && confirmVariant && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '10px',
              padding: '16px 20px',
              maxWidth: '580px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
                paddingBottom: '10px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: isRemapMode ? '#b45309' : '#1e293b' }}>
                  {isRemapMode ? 'Confirm Order Re-mapping' : 'Confirm ASIN Mapping'}
                </h3>
                <button
                  onClick={() => { setShowConfirmModal(false); setConfirmVariant(null); }}
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '0 4px', lineHeight: 1 }}
                >×</button>
              </div>

              {/* Remap Mode Info Banner */}
              {isRemapMode && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '8px 12px',
                  marginBottom: '12px',
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: '#92400e'
                }}>
                  <i className="fas fa-info-circle" style={{ marginTop: '2px' }}></i>
                  <div>
                    <div>
                      This will update the existing order's product mapping. Order ID:
                      <strong style={{
                        fontFamily: 'monospace',
                        backgroundColor: '#fef3c7',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        marginLeft: '4px',
                        border: '1px solid #f59e0b'
                      }}>
                        {remapUpsaleoOrderId || remapOrderId.slice(-8)}
                      </strong>
                    </div>
                    {failedOrderAmount && failedOrderAmount.orderCount > 1 && (
                      <div style={{
                        marginTop: '6px',
                        color: '#1e40af',
                        fontWeight: 500,
                        backgroundColor: '#dbeafe',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #93c5fd'
                      }}>
                        <i className="fas fa-sync-alt" style={{ marginRight: '4px' }}></i>
                        {failedOrderAmount.orderCount - 1} other order{failedOrderAmount.orderCount > 2 ? 's' : ''} with the same ASIN will also be updated automatically.
                      </div>
                    )}
                    {(!failedOrderAmount || failedOrderAmount.orderCount <= 1) && (
                      <div style={{ marginTop: '4px', color: '#64748b', fontStyle: 'italic' }}>
                        No other orders with the same ASIN.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mapping Summary - Compact */}
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                {isRemapMode ? 'Re-mapping Summary' : 'Mapping Summary'}
              </div>

              {/* ASIN & Offer ID - Single Row */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                <div>
                  <span style={{ fontSize: '9px', color: '#64748b' }}>ASIN: </span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', fontFamily: 'monospace' }}>{asinFromQuery}</span>
                </div>
                <div>
                  <span style={{ fontSize: '9px', color: '#64748b' }}>Offer ID: </span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#ea580c', fontFamily: 'monospace' }}>{selectedProductDetails?._selectedInfo?.offerId}</span>
                </div>
              </div>

              {/* Selected Variant - Compact with Weight/Dimensions */}
              <div style={{ padding: '10px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe', marginBottom: '10px' }}>
                <div style={{ fontSize: '9px', color: '#3b82f6', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase' }}>Selected Variant</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <img
                    src={confirmVariant.image || selectedProductDetails?._selectedInfo?.image || noImage}
                    alt={confirmVariant.attributeText}
                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #bfdbfe' }}
                    onError={(e) => { e.currentTarget.src = noImage; }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* SKU ID + Attributes in one row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', backgroundColor: '#e0e7ff', padding: '1px 5px', borderRadius: '3px' }}>
                        SKU: {confirmVariant.skuId || confirmVariant.id || 'N/A'}
                      </span>
                      {confirmVariant.attributes && confirmVariant.attributes.length > 0 && confirmVariant.attributes.map((attr, idx) => (
                        <span key={idx} style={{ fontSize: '10px', backgroundColor: '#fff', padding: '1px 5px', borderRadius: '3px', border: '1px solid #c7d2fe' }}>
                          <span style={{ color: '#64748b' }}>{attr.name}:</span> <span style={{ fontWeight: 500, color: '#1e293b' }}>{attr.value}</span>
                        </span>
                      ))}
                    </div>
                    {/* Price */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#ea580c' }}>¥{confirmVariant.price.rmb.toFixed(2)}</span>
                      <span style={{ color: '#cbd5e1' }}>|</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>₹{confirmVariant.price.inr.toFixed(0)}</span>
                    </div>
                    {/* Weight & Dimensions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {confirmVariant.weight > 0 && (
                        <span style={{ fontSize: '9px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 5px', borderRadius: '3px' }}>
                          ⚖️ {confirmVariant.weight.toFixed(2)}kg
                        </span>
                      )}
                      {confirmVariant.dimensions && confirmVariant.dimensions.length > 0 && (
                        <span style={{ fontSize: '9px', backgroundColor: '#e0e7ff', color: '#3730a3', padding: '2px 5px', borderRadius: '3px' }}>
                          📦 {confirmVariant.dimensions.length}×{confirmVariant.dimensions.width}×{confirmVariant.dimensions.height}cm
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Remap Mode: Old vs New Price & Margin Comparison */}
              {isRemapMode && oldMappedPrice > 0 && remapOrderValue > 0 && (
                (() => {
                  const newPrice = confirmVariant.price.inr;
                  // Apply marketplace deduction to order value for new margin calculation
                  const adjustedRemapOrderValue = remapOrderValue * (1 - marketplaceDeduction / 100);
                  const newMargin = adjustedRemapOrderValue - newPrice;
                  const marginChange = newMargin - oldMargin;
                  const isImproved = marginChange > 0;
                  const priceSavings = oldMappedPrice - newPrice;

                  return (
                    <div style={{
                      marginBottom: '12px',
                      padding: '12px',
                      backgroundColor: isImproved ? '#f0fdf4' : '#fef2f2',
                      borderRadius: '8px',
                      border: `2px solid ${isImproved ? '#86efac' : '#fca5a5'}`
                    }}>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '10px', textTransform: 'uppercase' }}>
                        Price & Margin Comparison
                      </div>

                      {/* Comparison Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                        {/* Old Mapping */}
                        <div style={{
                          padding: '10px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          opacity: 0.8
                        }}>
                          <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '6px', textTransform: 'uppercase', fontWeight: 600 }}>Current Mapping</div>
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>FBP: </span>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', textDecoration: 'line-through' }}>₹{oldMappedPrice.toFixed(0)}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>Margin: </span>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: oldMargin > 0 ? '#16a34a' : '#dc2626' }}>₹{oldMargin.toFixed(0)}</span>
                          </div>
                          {oldMappedSkuId && (
                            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '4px' }}>SKU: {oldMappedSkuId}</div>
                          )}
                        </div>

                        {/* Arrow */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <div style={{ fontSize: '24px', color: isImproved ? '#16a34a' : '#dc2626' }}>→</div>
                          <div style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: isImproved ? '#16a34a' : '#dc2626',
                            backgroundColor: isImproved ? '#dcfce7' : '#fee2e2',
                            padding: '2px 8px',
                            borderRadius: '10px'
                          }}>
                            {isImproved ? '↑' : '↓'} ₹{Math.abs(marginChange).toFixed(0)}
                          </div>
                        </div>

                        {/* New Mapping */}
                        <div style={{
                          padding: '10px',
                          backgroundColor: isImproved ? '#dcfce7' : '#fee2e2',
                          borderRadius: '6px',
                          border: `2px solid ${isImproved ? '#86efac' : '#fca5a5'}`
                        }}>
                          <div style={{ fontSize: '9px', color: isImproved ? '#166534' : '#991b1b', marginBottom: '6px', textTransform: 'uppercase', fontWeight: 600 }}>New Mapping</div>
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>FBP: </span>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#ea580c' }}>₹{newPrice.toFixed(0)}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>Margin: </span>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: newMargin > 0 ? '#16a34a' : '#dc2626' }}>₹{newMargin.toFixed(0)}</span>
                          </div>
                          <div style={{ fontSize: '9px', color: '#64748b', marginTop: '4px' }}>SKU: {confirmVariant.skuId || 'N/A'}</div>
                        </div>
                      </div>

                      {/* Summary Row */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', gap: '16px' }}>
                          <div>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>Order Value: </span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>₹{remapOrderValue.toFixed(0)}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>Price Savings: </span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: priceSavings > 0 ? '#16a34a' : '#dc2626' }}>
                              {priceSavings > 0 ? '+' : ''}₹{priceSavings.toFixed(0)}
                            </span>
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          backgroundColor: isImproved ? '#dcfce7' : '#fee2e2',
                          borderRadius: '20px'
                        }}>
                          <span style={{ fontSize: '14px' }}>{isImproved ? '📈' : '📉'}</span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: isImproved ? '#16a34a' : '#dc2626' }}>
                            Margin {isImproved ? 'Increase' : 'Decrease'}: ₹{Math.abs(marginChange).toFixed(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}

              {/* Remap Mode: Simple Summary (fallback when old price not available) */}
              {isRemapMode && (oldMappedPrice === 0 || remapOrderValue === 0) && (
                <div style={{
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: '#eff6ff',
                  borderRadius: '8px',
                  border: '2px solid #93c5fd'
                }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#1e40af', marginBottom: '10px', textTransform: 'uppercase' }}>
                    Re-mapping Order
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <span style={{ fontSize: '9px', color: '#64748b' }}>New FBP: </span>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#ea580c' }}>₹{confirmVariant.price.inr.toFixed(0)}</span>
                    </div>
                    <div>
                      <span style={{ fontSize: '9px', color: '#64748b' }}>New SKU: </span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{confirmVariant.skuId || 'N/A'}</span>
                    </div>
                    {remapOrderValue > 0 && (
                      <div>
                        <span style={{ fontSize: '9px', color: '#64748b' }}>New Margin: </span>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: 700,
                          color: (remapOrderValue - confirmVariant.price.inr) > 0 ? '#16a34a' : '#dc2626'
                        }}>
                          ₹{(remapOrderValue - confirmVariant.price.inr).toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Orders Table with Frozen Header - Only show for failed order mapping, NOT for remap mode */}
              {!isRemapMode && failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#92400e', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Failed Orders to Convert ({failedOrderAmount.orderCount})
                  </div>

                  {/* Table Container with Frozen Header */}
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                    {/* Frozen Header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b' }}>Order ID</div>
                      <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Order Value</div>
                      <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Source</div>
                      <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Margin</div>
                    </div>
                    {/* Scrollable Body */}
                    <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                      {(failedOrderAmount.orders || []).map((order, idx) => {
                        const orderValue = (order.consumerPrice || 0) * (order.quantity || 1);
                        const sourcePrice = confirmVariant.price.inr * (order.quantity || 1);
                        const margin = orderValue - sourcePrice;
                        const marginPercent = orderValue > 0 ? ((margin / orderValue) * 100).toFixed(0) : 0;
                        const isPositive = margin > 0;

                        return (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', borderBottom: '1px solid #f3f4f6' }}>
                            <div style={{ padding: '5px 8px', fontSize: '10px', color: '#374151', fontFamily: 'monospace' }}>{order.orderId?.slice(-12) || '-'}</div>
                            <div style={{ padding: '5px 8px', fontSize: '11px', color: '#1e293b', fontWeight: 500, textAlign: 'right' }}>₹{orderValue.toLocaleString('en-IN')}</div>
                            <div style={{ padding: '5px 8px', fontSize: '11px', color: '#ea580c', fontWeight: 500, textAlign: 'right' }}>₹{sourcePrice.toFixed(0)}</div>
                            <div style={{ padding: '5px 8px', fontSize: '11px', fontWeight: 600, textAlign: 'right', color: isPositive ? '#16a34a' : '#dc2626' }}>
                              ₹{margin.toFixed(0)} <span style={{ fontSize: '9px', opacity: 0.8 }}>({isPositive ? '+' : ''}{marginPercent}%)</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gross Total - Compact */}
                  {(() => {
                    const totalSourcePrice = confirmVariant.price.inr * failedOrderAmount.totalQuantity;
                    const grossTotalMargin = failedOrderAmount.totalAmount - totalSourcePrice;
                    const grossMarginPercent = failedOrderAmount.totalAmount > 0 ? ((grossTotalMargin / failedOrderAmount.totalAmount) * 100).toFixed(1) : 0;
                    const isPositive = grossTotalMargin > 0;

                    return (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 10px',
                        backgroundColor: isPositive ? '#f0fdf4' : '#fef2f2',
                        borderRadius: '6px',
                        border: `1px solid ${isPositive ? '#bbf7d0' : '#fecaca'}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        <div style={{ display: 'flex', gap: '16px' }}>
                          <div>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>Total Value: </span>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>₹{failedOrderAmount.totalAmount.toLocaleString('en-IN')}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>Source: </span>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#ea580c' }}>₹{totalSourcePrice.toLocaleString('en-IN')}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#64748b' }}>Qty: </span>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>{failedOrderAmount.totalQuantity}</span>
                          </div>
                        </div>
                        <div>
                          <span style={{ fontSize: '10px', fontWeight: 600, color: isPositive ? '#166534' : '#991b1b' }}>GROSS MARGIN: </span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                            ₹{grossTotalMargin.toLocaleString('en-IN')} ({isPositive ? '+' : ''}{grossMarginPercent}%)
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* HSN Category Selection - Required */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                  HSN Mapping <span style={{ color: '#dc2626' }}>*</span>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                    Category <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <select
                    value={selectedHsnCategory}
                    onChange={(e) => setSelectedHsnCategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: `1px solid ${selectedHsnCategory ? '#d1d5db' : '#fca5a5'}`,
                      borderRadius: '5px',
                      fontSize: '12px',
                      backgroundColor: selectedHsnCategory ? '#fff' : '#fef2f2',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select Category</option>
                    {hsnCategories.map((cat, idx) => (
                      <option key={idx} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {!selectedHsnCategory && (
                    <div style={{ fontSize: '10px', color: '#dc2626', marginTop: '2px' }}>HSN Category is required</div>
                  )}
                </div>

                {/* HSN Details Card - Shows automatically when category selected */}
                {loadingHsn ? (
                  <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '6px', textAlign: 'center' }}>
                    <FaSpinner className="apd-spinner" style={{ width: '14px', height: '14px' }} />
                    <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px' }}>Loading HSN details...</span>
                  </div>
                ) : selectedHsnMapping && (
                  <div style={{
                    marginTop: '8px',
                    padding: '10px',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '6px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#166534',
                        fontFamily: 'monospace',
                        backgroundColor: '#dcfce7',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>
                        HSN: {selectedHsnMapping.hsnCode || selectedHsnMapping.HSN}
                      </span>
                      <span style={{ fontSize: '11px', color: '#166534' }}>
                        {selectedHsnMapping.category || selectedHsnMapping.Item}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                        <div style={{ fontSize: '9px', color: '#64748b' }}>BCD</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                          {selectedHsnMapping.bcd !== undefined ? `${(selectedHsnMapping.bcd * 100).toFixed(1)}%` :
                            selectedHsnMapping.BCD !== undefined ? `${(selectedHsnMapping.BCD * 100).toFixed(1)}%` : '0%'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                        <div style={{ fontSize: '9px', color: '#64748b' }}>Surcharge</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                          {selectedHsnMapping.surcharge !== undefined ? `${(selectedHsnMapping.surcharge * 100).toFixed(1)}%` :
                            selectedHsnMapping.Surcharge !== undefined ? `${(selectedHsnMapping.Surcharge * 100).toFixed(1)}%` : '0%'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                        <div style={{ fontSize: '9px', color: '#64748b' }}>Custom Duty</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#ea580c' }}>
                          {selectedHsnMapping.customsDutyRate ||
                            (selectedHsnMapping.FinalCustomDuty !== undefined ? `${(selectedHsnMapping.FinalCustomDuty * 100).toFixed(1)}%` : '0%')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                        <div style={{ fontSize: '9px', color: '#64748b' }}>IGST</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#16a34a' }}>
                          {selectedHsnMapping.igstRate ||
                            (selectedHsnMapping.IGST !== undefined ? `${(selectedHsnMapping.IGST * 100).toFixed(1)}%` : '18%')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Remark - After HSN */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                  Remark <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  value={mappingRemark}
                  onChange={(e) => setMappingRemark(e.target.value)}
                  placeholder="Add notes about this mapping (required)..."
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: `1px solid ${mappingRemark.trim() ? '#d1d5db' : '#fca5a5'}`,
                    borderRadius: '5px',
                    fontSize: '12px',
                    resize: 'vertical',
                    minHeight: '50px',
                    fontFamily: 'inherit',
                    backgroundColor: mappingRemark.trim() ? '#fff' : '#fef2f2'
                  }}
                />
                {!mappingRemark.trim() && (
                  <div style={{ fontSize: '10px', color: '#dc2626', marginTop: '2px' }}>Remark is required</div>
                )}
              </div>


              {/* Custom Prices by Variant - Show if any custom prices are set */}
              {Object.keys(customPricesByOrderDetail).length > 0 && customPricesByOrderDetail && (() => {
                const variantsWithCustomPrices = failedOrdersWithSellers.flatMap(order => {
                  const orderDetails = order.orderDetails || [];
                  return orderDetails
                    .filter(detail => {
                      const detailId = detail._id?.toString() || `${order.orderId}-${orderDetails.indexOf(detail)}`;
                      return customPricesByOrderDetail[detailId] && parseFloat(customPricesByOrderDetail[detailId]) > 0;
                    })
                    .map(detail => {
                      const detailId = detail._id?.toString() || `${order.orderId}-${orderDetails.indexOf(detail)}`;
                      return {
                        orderId: order.orderId,
                        amazonOrderId: order.amazonOrderId || order.orderId,
                        sellerId: order.sellerId,
                        sellerName: order.sellerName || order.sellerEmail || 'Unknown Seller',
                        sellerEmail: order.sellerEmail,
                        detailId: detailId,
                        productName: detail.productName || 'Variant',
                        sku: detail.sku,
                        customPrice: parseFloat(customPricesByOrderDetail[detailId])
                      };
                    });
                });

                if (variantsWithCustomPrices.length === 0) return null;

                return (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                      Variant Custom Prices ({variantsWithCustomPrices.length} variants)
                    </div>
                    <div style={{
                      padding: '10px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '6px',
                      border: '1px solid #fcd34d'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '150px', overflowY: 'auto', scrollBehavior: 'smooth' }}>
                        {variantsWithCustomPrices.map((variant, idx) => (
                          <div
                            key={variant.detailId || idx}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 8px',
                              backgroundColor: '#fff',
                              borderRadius: '4px',
                              border: '1px solid #fde68a'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '10px', fontWeight: 600, color: '#1e293b' }}>
                                {variant.sellerName}  <span style={{ maarginLeft: '8px', fontSize: '9px', color: '#64748b' }}>{variant.sellerEmail}</span>
                              </div>
                              <div style={{ fontSize: '9px', color: '#64748b', marginTop: '2px' }}>
                                {variant.amazonOrderId && <span style={{ fontFamily: 'monospace' }}>Amazon Order ID:  {variant.amazonOrderId}</span>}
                                <span style={{ marginLeft: '8px', fontFamily: 'monospace' }}>SKU: {variant.sku}</span>
                              </div>
                              <div style={{ fontSize: '9px', color: '#1e293b', marginTop: '2px' }}>
                                {variant.productName}
                              </div>
                            </div>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: 700,
                              color: '#b45309',
                              backgroundColor: '#fef3c7',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: '1px solid #fcd34d'
                            }}>
                              ₹{variant.customPrice.toFixed(0)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}


              {/* Quantity Changes Summary */}
              {Object.keys(customQuantitiesByOrderDetail).length > 0 && (
                <div style={{
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  border: '2px solid #86efac'
                }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#166534', marginBottom: '10px' }}>
                    Quantity Changes For Cross Border Order
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {failedOrdersWithSellers.map((order) => {
                      const orderDetails = order.orderDetails || [];
                      return orderDetails.map((detail, detailIdx) => {
                        const detailId = detail._id?.toString() || `${order.orderId}-${detailIdx}`;
                        const newQty = customQuantitiesByOrderDetail[detailId];
                        const oldQty = detail.quantity || 0;

                        if (newQty === undefined || parseInt(newQty) === oldQty) return null;

                        return (
                          <div key={detailId} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 8px',
                            marginBottom: '4px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            border: '1px solid #bbf7d0'
                          }}>
                            <div style={{ fontSize: '11px', color: '#166534' }}>
                              <span style={{ fontWeight: 600 }}>Order {order.amazonOrderId || order.orderId}:</span> Item ID: {detail.orderItemId || 'N/A'} (SKU: {detail.sku})
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#64748b', textDecoration: 'line-through' }}>{oldQty}</span>
                              <span style={{ fontSize: '12px', color: '#166534' }}>→</span>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: '#16a34a' }}>{newQty}</span>
                            </div>
                          </div>
                        );
                      });
                    }).flat().filter(Boolean)}
                  </div>
                </div>
              )}


              {/* Action Buttons - Compact */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowConfirmModal(false); setConfirmVariant(null); }}
                  disabled={isMapping}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '5px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: isMapping ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMapVariant}
                  disabled={isMapping || !mappingRemark.trim() || !selectedHsnMapping}
                  style={{
                    padding: '8px 20px',
                    backgroundColor: (isMapping || !mappingRemark.trim() || !selectedHsnMapping) ? '#9ca3af' : (isRemapMode ? '#f59e0b' : '#3b82f6'),
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: (isMapping || !mappingRemark.trim() || !selectedHsnMapping) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {isMapping ? (
                    <>
                      <FaSpinner className="apd-spinner" style={{ width: '12px', height: '12px' }} />
                      {isRemapMode ? 'Re-mapping...' : 'Mapping...'}
                    </>
                  ) : (
                    isRemapMode ? 'Confirm & Re-map' : 'Confirm & Map'
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Custom Price Modal */}
      {
        showCustomPriceModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCustomPriceModal(false);
                setLoadingFailedOrders(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '900px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                scrollBehavior: 'smooth',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                  Unit Price for Orders
                </h2>
                <button
                  onClick={() => {
                    setShowCustomPriceModal(false);
                    setLoadingFailedOrders(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>

              {loadingFailedOrders ? (
                <>
                  <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #e5e7eb',
                        borderTop: '4px solid #3b82f6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <div style={{ fontSize: '14px', fontWeight: 500 }}>Loading seller and order data...</div>
                    </div>
                  </div>
                </>
              ) : failedOrdersWithSellers.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                  No orders found for this ASIN
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '15px', fontSize: '12px', color: '#64748b' }}>
                    Set Order unit price (₹) and Cross Border Order quantity for each variant. Leave empty to use default pricing.
                  </div>

                  {/* Orders with Variant Rows */}
                  <div style={{ maxHeight: '500px', overflowY: 'auto', scrollBehavior: 'smooth' }}>
                    {failedOrdersWithSellers.map((order, orderIdx) => {
                      // Determine account type color
                      const getAccountTypeColor = (accountType) => {
                        switch (accountType) {
                          case 'BTPL':
                            return { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' };
                          case 'Reseller':
                            return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' };
                          default:
                            return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
                        }
                      };
                      const accountTypeStyle = getAccountTypeColor(order.accountType || 'Other');

                      // Get orderDetails for this order (variant-wise)
                      const orderDetails = order.orderDetails || [];

                      return (
                        <div key={order.orderId || orderIdx} style={{ marginBottom: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                          {/* Order Header */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1.2fr 1fr 0.7fr 1fr',
                            gap: '12px',
                            padding: '10px',
                            backgroundColor: '#f8fafc',
                            borderBottom: '2px solid #e5e7eb'
                          }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Seller Email</div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Seller Name</div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Account Type</div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Order ID</div>
                          </div>

                          {/* Order Info Row */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1.2fr 1fr 0.7fr 1fr',
                            gap: '12px',
                            padding: '10px',
                            backgroundColor: '#fff',
                            borderBottom: orderDetails.length > 0 ? '1px solid #e5e7eb' : 'none',
                            alignItems: 'center'
                          }}>
                            <div style={{ fontSize: '13px', color: '#1e293b', wordBreak: 'break-word' }}>
                              {order.sellerEmail || 'No email available'}
                            </div>
                            <div style={{ fontSize: '13px', color: '#1e293b', wordBreak: 'break-word' }}>
                              {order.sellerName || 'No name available'}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              color: accountTypeStyle.text,
                              backgroundColor: accountTypeStyle.bg,
                              border: `1px solid ${accountTypeStyle.border}`,
                              padding: '4px 6px',
                              borderRadius: '4px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              width: 'fit-content',
                              minWidth: 'auto'
                            }}>
                              {order.accountType || 'Other'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#1e293b', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                              {order.amazonOrderId || order.orderId || 'N/A'}
                            </div>
                          </div>

                          {/* Variant Rows - One row per orderDetails object */}
                          {orderDetails.length > 0 && (
                            <div style={{ backgroundColor: '#fafbfc' }}>
                              {/* Variant Header */}
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 1fr 0.8fr 1fr 1fr',
                                gap: '12px',
                                padding: '8px 10px 8px 40px',
                                backgroundColor: '#f1f5f9',
                                borderBottom: '1px solid #e5e7eb',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#64748b'
                              }}>
                                <div>Product Name</div>
                                <div>SKU</div>
                                <div>Quantity</div>
                                <div>Cross Border Order Qty</div>
                                <div>Order Unit Price (₹)</div>
                              </div>

                              {/* Variant Rows */}
                              {orderDetails.map((detail, detailIdx) => {
                                const detailId = detail._id?.toString() || `${order.orderId}-${detailIdx}`;

                                return (
                                  <div key={detailId} style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1.5fr 1fr 0.8fr 1fr 1fr',
                                    gap: '12px',
                                    padding: '10px 10px 10px 40px',
                                    borderBottom: detailIdx < orderDetails.length - 1 ? '1px solid #f1f5f9' : 'none',
                                    alignItems: 'center'
                                  }}>
                                    {/* Product Name */}
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#1e293b',
                                        wordBreak: 'break-word',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        lineHeight: '1.4',
                                        maxHeight: '2.8em'
                                      }}
                                      title={detail.productName || 'N/A'}
                                    >
                                      {detail.productName || 'N/A'}
                                    </div>

                                    {/* SKU */}
                                    <div style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monospace' }}>
                                      {detail.sku || 'N/A'}
                                    </div>

                                    {/* Current Quantity */}
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                                      {detail.quantity || 0}
                                    </div>

                                    {/* Quantity Input */}
                                    <input
                                      type="number"
                                      min="1"
                                      value={customQuantitiesByOrderDetail[detailId] !== undefined ? customQuantitiesByOrderDetail[detailId] : ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setCustomQuantitiesByOrderDetail(prev => ({
                                          ...prev,
                                          [detailId]: value
                                        }));
                                      }}
                                      placeholder="Enter CB Order Qty"
                                      style={{
                                        padding: '4px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        width: '100%'
                                      }}
                                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                      onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                    />

                                    {/* Custom Price Input */}
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={customPricesByOrderDetail[detailId] !== undefined ? customPricesByOrderDetail[detailId] : ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setCustomPricesByOrderDetail(prev => ({
                                          ...prev,
                                          [detailId]: value
                                        }));
                                      }}
                                      placeholder="Enter price (optional)"
                                      style={{
                                        padding: '4px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        width: '100%'
                                      }}
                                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                      onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button
                      onClick={() => {
                        setShowCustomPriceModal(false);
                        setLoadingFailedOrders(false);
                        setCustomPricesByOrderDetail({});
                        setCustomQuantitiesByOrderDetail({});
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setShowCustomPriceModal(false);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Save
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      }

      {/* Image Zoom Modal */}
      {
        showImageZoom && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              cursor: 'zoom-out'
            }}
            onClick={() => setShowImageZoom(false)}
          >
            <div
              style={{
                position: 'relative',
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoomedImageUrl || noImage}
                alt="Zoomed Product"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
                onError={(e) => { e.currentTarget.src = noImage; }}
              />
              {/* Close Button */}
              <button
                onClick={() => setShowImageZoom(false)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: '#1e293b',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Close (ESC)"
              >
                ×
              </button>
            </div>
          </div>
        )
      }

      {/* Price Calculator Modal */}
      {
        showPriceCalculatorModal && (() => {
          const currentVariant = selectedProductVariants[selectedVariantImageIdx] || selectedProductVariants[0];

          // Get AP (Actual Price) in RMB - priority: variant price > selected info > product sale info
          const ap = currentVariant?.price?.rmb ||
            currentVariant?.rmb ||
            currentVariant?.ap ||
            selectedProductDetails?._selectedInfo?.rmb ||
            selectedProductDetails?.productSaleInfo?.priceRange ||
            selectedProductDetails?.productSaleInfo?.priceRangeOriginal ||
            selectedProductDetails?.priceInfo?.price ||
            '';

          // Get weight in kg - from variant weight (already converted to kg)
          const weight = currentVariant?.weight || '';

          // Get dimensions in cm - from variant dimensions object (already converted to cm)
          const length = currentVariant?.dimensions?.length || currentVariant?.length || '';
          const width = currentVariant?.dimensions?.width || currentVariant?.width || '';
          const height = currentVariant?.dimensions?.height || currentVariant?.height || '';

          // Transform variant data to match VariantPriceModal expected format
          // Get CP from variant - use actual CP value, not AP
          const cp = currentVariant?.cp ||
            currentVariant?.consignPrice ||
            currentVariant?.price?.cp ||
            ap; // Fallback to AP if CP not available

          const variantForModal = {
            skuId: currentVariant?.skuId || currentVariant?.sku || selectedProductDetails?.sku || '',
            sku: currentVariant?.sku || currentVariant?.skuId || selectedProductDetails?.sku || '',
            // AP (Actual Price) in RMB - this is the key field for price calculation
            ap: ap,
            cp: cp, // Use actual CP value from variant
            consignPrice: cp, // Use actual CP value from variant
            // Weight in kg
            weight: weight,
            // Dimensions in cm
            length: length,
            width: width,
            height: height,
            // Shipping detail structure for compatibility
            shippingDetail: {
              weight: weight,
              length: length,
              width: width,
              height: height,
              ...(currentVariant?.shippingDetail || {})
            },
            fenxiaoPriceInfo: currentVariant?.fenxiaoPriceInfo || {}
          };

          return (
            <PriceCalc
              open={showPriceCalculatorModal}
              onClose={() => setShowPriceCalculatorModal(false)}
              variation={variantForModal}
              localShipping={selectedProductDetails?.local_shipping || selectedProductDetails?.localShipping || ""}
              defaultCategoryId={selectedProductDetails?.categoryId || ""}
              pricingMode="dropshipping"
              onApplyPrice={(finalPrice, wholesaleFinalPrice) => {
                // This is for the price calculator modal (not weight/dimensions edit)
                // Prices are handled separately in that modal
              }}
              onApplyDimensions={() => { }}
            />
          );
        })()
      }

      {/* Dimensions & Weight Modal */}
      {showEditWeightDimensionsModal && editingVariantForWeightDimensions && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10002,
          }}
          onClick={() => {
            if (!dimApplyingCurrent && !dimApplyingAll) {
              setShowEditWeightDimensionsModal(false);
              setEditingVariantForWeightDimensions(null);
              setDimToast(null);
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              width: '100%',
              maxWidth: '420px',
              margin: '16px',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaRuler style={{ color: '#3b82f6', fontSize: '16px' }} />
                <span style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                  Edit Dimensions &amp; Weight
                </span>
              </div>
              <button
                onClick={() => {
                  if (!dimApplyingCurrent && !dimApplyingAll) {
                    setShowEditWeightDimensionsModal(false);
                    setEditingVariantForWeightDimensions(null);
                    setDimToast(null);
                  }
                }}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: '20px', color: '#64748b', lineHeight: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '28px', height: '28px', borderRadius: '50%',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                title="Close"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px' }}>
              {/* Variant info */}
              {editingVariantForWeightDimensions?.attributeText && (
                <div style={{
                  fontSize: '11px', color: '#64748b', marginBottom: '16px',
                  background: '#f8fafc', padding: '6px 10px', borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                }}>
                  Variant: <strong style={{ color: '#1e293b' }}>{editingVariantForWeightDimensions.attributeText}</strong>
                </div>
              )}

              {/* Input grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {[
                  { key: 'length', label: 'Length', unit: 'cm', icon: '↔' },
                  { key: 'width', label: 'Width', unit: 'cm', icon: '↕' },
                  { key: 'height', label: 'Height', unit: 'cm', icon: '↑' },
                  { key: 'weight', label: 'Weight', unit: 'kg', icon: '⚖' },
                ].map(({ key, label, unit, icon }) => (
                  <div key={key}>
                    <label style={{
                      display: 'block', fontSize: '11px', fontWeight: 600,
                      color: '#374151', marginBottom: '4px',
                    }}>
                      {icon} {label} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({unit})</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={dimFormValues[key]}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow only numeric input
                          if (val === '' || /^[0-9]*\.?[0-9]*$/.test(val)) {
                            setDimFormValues(prev => ({ ...prev, [key]: val }));
                            if (dimFormErrors[key]) {
                              setDimFormErrors(prev => ({ ...prev, [key]: undefined }));
                            }
                          }
                        }}
                        placeholder={`e.g. ${key === 'weight' ? '0.5' : '10'}`}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: `1.5px solid ${dimFormErrors[key] ? '#ef4444' : '#d1d5db'}`,
                          borderRadius: '7px',
                          fontSize: '13px',
                          color: '#1e293b',
                          outline: 'none',
                          boxSizing: 'border-box',
                          transition: 'border-color 0.15s',
                          background: dimFormErrors[key] ? '#fef2f2' : '#fff',
                        }}
                        onFocus={(e) => {
                          if (!dimFormErrors[key]) e.target.style.borderColor = '#3b82f6';
                        }}
                        onBlur={(e) => {
                          if (!dimFormErrors[key]) e.target.style.borderColor = '#d1d5db';
                        }}
                      />
                    </div>
                    {dimFormErrors[key] && (
                      <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '3px' }}>
                        {dimFormErrors[key]}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Toast */}
              {dimToast && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  marginBottom: '14px',
                  fontSize: '12px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: dimToast.type === 'success' ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${dimToast.type === 'success' ? '#86efac' : '#fca5a5'}`,
                  color: dimToast.type === 'success' ? '#166534' : '#991b1b',
                }}>
                  <span style={{ fontSize: '14px' }}>{dimToast.type === 'success' ? '✓' : '✕'}</span>
                  {dimToast.message}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={handleApplyDimensionsToCurrent}
                  disabled={dimApplyingCurrent || dimApplyingAll}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: dimApplyingCurrent || dimApplyingAll ? '#93c5fd' : '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: dimApplyingCurrent || dimApplyingAll ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!dimApplyingCurrent && !dimApplyingAll) e.currentTarget.style.background = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    if (!dimApplyingCurrent && !dimApplyingAll) e.currentTarget.style.background = '#3b82f6';
                  }}
                >
                  {dimApplyingCurrent
                    ? <><FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '12px' }} /> Applying...</>
                    : '✓ Apply to Current Variant'
                  }
                </button>

                <button
                  onClick={handleApplyDimensionsToAll}
                  disabled={dimApplyingCurrent || dimApplyingAll}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: dimApplyingCurrent || dimApplyingAll ? '#d1fae5' : '#fff',
                    color: dimApplyingCurrent || dimApplyingAll ? '#6ee7b7' : '#059669',
                    border: `1.5px solid ${dimApplyingCurrent || dimApplyingAll ? '#6ee7b7' : '#10b981'}`,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: dimApplyingCurrent || dimApplyingAll ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!dimApplyingCurrent && !dimApplyingAll) {
                      e.currentTarget.style.background = '#f0fdf4';
                      e.currentTarget.style.borderColor = '#059669';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!dimApplyingCurrent && !dimApplyingAll) {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = '#10b981';
                    }
                  }}
                >
                  {dimApplyingAll
                    ? <><FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '12px' }} /> Applying to all...</>
                    : `⊞ Apply to All Variants (${selectedProductVariants.length})`
                  }
                </button>

                <button
                  onClick={() => {
                    if (!dimApplyingCurrent && !dimApplyingAll) {
                      setShowEditWeightDimensionsModal(false);
                      setEditingVariantForWeightDimensions(null);
                      setDimToast(null);
                    }
                  }}
                  disabled={dimApplyingCurrent || dimApplyingAll}
                  style={{
                    width: '100%',
                    padding: '8px 16px',
                    background: 'transparent',
                    color: '#64748b',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: dimApplyingCurrent || dimApplyingAll ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!dimApplyingCurrent && !dimApplyingAll) {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!dimApplyingCurrent && !dimApplyingAll) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apply to All Results Modal */}
      <UpdatePriceModal
        open={showApplyToAllResultsModal}
        onClose={() => {
          setShowApplyToAllResultsModal(false);
          setApplyToAllResults([]);
          setApplyToAllDimensions(null);
          setApplyToAllProgress(0);
          setIsProcessingApplyToAll(false);
        }}
        results={applyToAllResults}
        dimensions={applyToAllDimensions}
        isProcessing={isProcessingApplyToAll}
        progress={applyToAllProgress}
      />
    </div >
  );
}
